import { ArrowsClockwise, CheckCircle, ShieldCheck, X } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { confirmProductionWrite } from "../lib/diagnostics";
import BotbConfidoCampaignsCard from "./BotbConfidoCampaignsCard";
import RevSourceAbortRecovery from "./RevSourceAbortRecovery";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const DEFAULT_LIMIT_PER_OPERATOR = 200;
const DEFAULT_MAX_DETAILS = 50;
const CLICK_MAX_SCAN_DETAILS = 250;

function parseClickScanMaxDetails(value) {
  const text = String(value).trim();
  if (!/^[0-9]+$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= CLICK_MAX_SCAN_DETAILS
    ? parsed
    : null;
}
const SEVENDAYS_MAX_DETAILS = 250;
const JFD_DEFAULT_LIMIT_PER_OPERATOR = 100;
const FEELGOOD_DEFAULT_LIMIT_PER_OPERATOR = 100;
const ASPIRE_DEFAULT_LIMIT_PER_OPERATOR = 250;
const ASPIRE_DEFAULT_SCAN_MAX_DETAILS = 250;
const ASPIRE_SCAN_MAX_DETAILS = 250;
const ASPIRE_DEFAULT_MAX_NEW_RECORDS = 25;
const ASPIRE_MAX_NEW_RECORDS_PER_RUN = 25;
const ASPIRE_CONTROLLED_PHASE_MAX_DETAILS = 250;
const ASPIRE_MANUAL_IMPORT_MAX_DETAILS = 25;
const ASPIRE_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const DCG_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const ELITE_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const BOTB_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const CLICK_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const SEVENDAYS_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const MCKINNEY_DEFAULT_LIMIT_PER_OPERATOR = 250;
const MCKINNEY_DEFAULT_MAX_DETAILS = 250;
const MCKINNEY_MAX_DETAILS = 250;
const MCKINNEY_DEFAULT_MAX_NEW_RECORDS = 25;
const MCKINNEY_MAX_NEW_RECORDS_PER_RUN = 250;
const MCKINNEY_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const LLF_DEFAULT_LIMIT_PER_OPERATOR = 250;
const LLF_DEFAULT_MAX_DETAILS = 250;
const LLF_MAX_DETAILS = 250;
const LLF_DEFAULT_MAX_NEW_RECORDS = 30;
const LLF_MAX_NEW_RECORDS_PER_RUN = 250;
const LLF_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const KILTED_DEFAULT_LIMIT_PER_OPERATOR = 250;
const KILTED_DEFAULT_MAX_DETAILS = 250;
const KILTED_MAX_DETAILS = 250;
const KILTED_DEFAULT_MAX_NEW_RECORDS = 50;
const KILTED_MAX_NEW_RECORDS_PER_RUN = 250;
const KILTED_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const REV_SCAN_SNAPSHOT_HASH_PATTERN = /^[0-9a-f]{64}$/;
const DEFAULT_DETAIL_FETCH_LIMIT = 250;
const MAX_DETAIL_FETCH_LIMIT = 250;
const MAX_DETAIL_FETCH_OFFSET = 10000;
export const REV_BROKEN_TILE_CLEANUP_LIVE_SET_ID = "2d3ffe7d-3635-4f1b-b284-d644f65f683d";
export const REV_BROKEN_TILE_CLEANUP_RECORDS = [
  {
    id: "e119327f-a5b0-4a7a-a93d-915d7d5ce795",
    title: "100 x BRAND NEW POKEMON ASCENDED HEROS PACKS - Rev Comps",
  },
  {
    id: "bfcedf85-85ca-41d0-9719-c82998f0a7bd",
    title: "ACE 10 CLAWITZER ART RARE MEGA SYMPHONIA SHOWCASE POKEMON - Rev Comps",
  },
];
export const REV_BROKEN_TILE_CLEANUP_RECORD_IDS = REV_BROKEN_TILE_CLEANUP_RECORDS.map(
  (record) => record.id,
);

export function buildRevBrokenTileCleanupPayload(dryRun) {
  return {
    expected_live_set_id: REV_BROKEN_TILE_CLEANUP_LIVE_SET_ID,
    record_ids: [...REV_BROKEN_TILE_CLEANUP_RECORD_IDS],
    dry_run: dryRun === true,
    hide_out_of_scope_broken: true,
    replacement_image_candidates: {},
  };
}

export function isSafeRevBrokenTileCleanupDryRun(data) {
  return revBrokenTileCleanupGateFailures(data).length === 0;
}

export function normaliseAspireScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !ASPIRE_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function normaliseDcgScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !DCG_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function dcgSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.dcg_scan_snapshot_id || container.scan_snapshot_id;
      const hash = (
        container.dcg_scan_snapshot_hash || container.scan_snapshot_hash
      );
      if (id == null && hash == null) return null;
      const reference = normaliseDcgScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function normaliseEliteScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !ELITE_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function normaliseBotbScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !BOTB_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function botbSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.botb_scan_snapshot_id || container.scan_snapshot_id;
      const hash = (
        container.botb_scan_snapshot_hash || container.scan_snapshot_hash
      );
      if (id == null && hash == null) return null;
      const reference = normaliseBotbScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function eliteSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.elite_scan_snapshot_id || container.scan_snapshot_id;
      const hash = (
        container.elite_scan_snapshot_hash || container.scan_snapshot_hash
      );
      if (id == null && hash == null) return null;
      const reference = normaliseEliteScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function normaliseClickScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !CLICK_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function clickSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.click_scan_snapshot_id || container.scan_snapshot_id;
      const hash = (
        container.click_scan_snapshot_hash || container.scan_snapshot_hash
      );
      if (id == null && hash == null) return null;
      const reference = normaliseClickScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function normaliseSevenDaysScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !SEVENDAYS_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function sevenDaysSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.sevendays_scan_snapshot_id || container.scan_snapshot_id;
      const hash = (
        container.sevendays_scan_snapshot_hash || container.scan_snapshot_hash
      );
      if (id == null && hash == null) return null;
      const reference = normaliseSevenDaysScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function aspireSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.aspire_apply_scan_snapshot_id;
      const hash = container.aspire_apply_scan_snapshot_hash;
      if (id == null && hash == null) return null;
      const reference = normaliseAspireScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function normaliseMckinneyScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !MCKINNEY_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function mckinneySnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.mckinney_apply_scan_snapshot_id;
      const hash = container.mckinney_apply_scan_snapshot_hash;
      if (id == null && hash == null) return null;
      const reference = normaliseMckinneyScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function normaliseLlfScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !LLF_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

export function llfSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container.llf_apply_scan_snapshot_id;
      const hash = container.llf_apply_scan_snapshot_hash;
      if (id == null && hash == null) return null;
      const reference = normaliseLlfScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function normaliseKiltedScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !KILTED_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

function exactSetPrefixForOperator(operatorKey) {
  if (operatorKey === "ukcc") return "ukcc";
  if (operatorKey === "bounty") return "bounty";
  if (operatorKey === "storm") return "storm";
  if (operatorKey === "redline") return "redline";
  if (operatorKey === "prizeparadise") return "prizeparadise";
  if (operatorKey === "apex") return "apex";
  if (operatorKey === "jammy") return "jammy";
  if (operatorKey === "pawsome") return "pawsome";
  if (operatorKey === "lincolnshire") return "lincolnshire";
  if (operatorKey === "drift") return "drift";
  if (operatorKey === "palladium") return "palladium";
  if (operatorKey === "pristine") return "pristine";
  if (operatorKey === "caddy") return "caddy";
  if (operatorKey === "hotcomps") return "hotcomps";
  if (operatorKey === "thatprizeguy") return "thatprizeguy";
  if (operatorKey === "giveawayguys") return "giveawayguys";
  if (operatorKey === "bright") return "bright";
  if (operatorKey === "salexmo") return "salexmo";
  return "kilted";
}

export function kiltedSnapshotReferenceFromRunResult(data) {
  const prefix = data?.operator_key
    ? exactSetPrefixForOperator(data.operator_key)
    : data?.diagnostics?.bounty_apply_scan_snapshot_id
      || data?.summary?.bounty_apply_scan_snapshot_id
      ? "bounty"
      : data?.diagnostics?.storm_apply_scan_snapshot_id
        || data?.summary?.storm_apply_scan_snapshot_id
        ? "storm"
      : data?.diagnostics?.redline_apply_scan_snapshot_id
        || data?.summary?.redline_apply_scan_snapshot_id
        ? "redline"
      : data?.diagnostics?.prizeparadise_apply_scan_snapshot_id
        || data?.summary?.prizeparadise_apply_scan_snapshot_id
        ? "prizeparadise"
      : data?.diagnostics?.apex_apply_scan_snapshot_id
        || data?.summary?.apex_apply_scan_snapshot_id
        ? "apex"
      : data?.diagnostics?.jammy_apply_scan_snapshot_id
        || data?.summary?.jammy_apply_scan_snapshot_id
        ? "jammy"
      : data?.diagnostics?.pawsome_apply_scan_snapshot_id
        || data?.summary?.pawsome_apply_scan_snapshot_id
        ? "pawsome"
      : data?.diagnostics?.lincolnshire_apply_scan_snapshot_id
        || data?.summary?.lincolnshire_apply_scan_snapshot_id
        ? "lincolnshire"
      : data?.diagnostics?.drift_apply_scan_snapshot_id
        || data?.summary?.drift_apply_scan_snapshot_id
        ? "drift"
      : data?.diagnostics?.palladium_apply_scan_snapshot_id
        || data?.summary?.palladium_apply_scan_snapshot_id
        ? "palladium"
      : data?.diagnostics?.pristine_apply_scan_snapshot_id
        || data?.summary?.pristine_apply_scan_snapshot_id
        ? "pristine"
      : data?.diagnostics?.caddy_apply_scan_snapshot_id
        || data?.summary?.caddy_apply_scan_snapshot_id
        ? "caddy"
      : data?.diagnostics?.hotcomps_apply_scan_snapshot_id
        || data?.summary?.hotcomps_apply_scan_snapshot_id
        ? "hotcomps"
      : data?.diagnostics?.ukcc_apply_scan_snapshot_id
        || data?.summary?.ukcc_apply_scan_snapshot_id
        ? "ukcc"
        : "kilted";
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const container = data?.[source];
      if (!container) return null;
      const id = container[`${prefix}_apply_scan_snapshot_id`];
      const hash = container[`${prefix}_apply_scan_snapshot_hash`];
      if (id == null && hash == null) return null;
      const reference = normaliseKiltedScanSnapshotReference(id, hash);
      return reference ? { ...reference, source } : { invalid: true, source };
    })
    .filter(Boolean);
  if (!references.length || references.some((reference) => reference.invalid)) {
    return null;
  }
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return { id: first.id, hash: first.hash };
}

export function normaliseRevScanSnapshotReference(snapshotId, snapshotHash) {
  const id = typeof snapshotId === "string" ? snapshotId.trim() : "";
  const hash = typeof snapshotHash === "string" ? snapshotHash.trim() : "";
  if (!id || !REV_SCAN_SNAPSHOT_HASH_PATTERN.test(hash)) return null;
  return { id, hash };
}

function revSnapshotFromContainer(container, { settings = false } = {}) {
  if (!container) return null;
  const prefix = settings ? "latest_rev_scan_snapshot_" : "rev_scan_snapshot_";
  const reference = normaliseRevScanSnapshotReference(
    container[`${prefix}id`],
    container[`${prefix}hash`],
  );
  if (!reference) return null;
  return {
    ...reference,
    state: container[`${prefix}state`] || null,
    createdAt: container[`${prefix}created_at`] || null,
    expiresAt: container[`${prefix}expires_at`] || null,
    candidateCount: Number(container[`${prefix}candidate_count`] || 0),
    exactIdentityCount: Number(
      container[`${prefix}exact_identity_count`] || 0,
    ),
    exactIdentityDigest: (
      container[`${prefix}exact_identity_digest`] || null
    ),
    trustedImageCount: Number(
      container[
        settings
          ? "latest_rev_scan_snapshot_trusted_image_count"
          : "rev_scan_snapshot_trusted_image_count"
      ] || 0,
    ),
    trustedMaxTicketsCount: Number(
      container[
        settings
          ? "latest_rev_scan_snapshot_trusted_max_tickets_count"
          : "rev_scan_snapshot_trusted_max_tickets_count"
      ] || 0,
    ),
    scanConfig: container[`${prefix}scan_config`] || null,
  };
}

export function revSnapshotReferenceFromRunResult(data) {
  const references = ["diagnostics", "summary"]
    .map((source) => {
      const snapshot = revSnapshotFromContainer(data?.[source]);
      return snapshot ? { ...snapshot, source } : null;
    })
    .filter(Boolean);
  if (!references.length) return null;
  const [first] = references;
  if (references.some(
    (reference) => reference.id !== first.id || reference.hash !== first.hash,
  )) {
    return null;
  }
  return first;
}

function latestAutomationRunSettings(settings, resultSettings) {
  if (!resultSettings?.last_run_summary) return settings || null;
  if (!settings?.last_run_summary) return resultSettings;
  const settingsRunAt = Date.parse(settings.last_run_at || "");
  const resultRunAt = Date.parse(resultSettings.last_run_at || "");
  if (
    Number.isFinite(resultRunAt)
    && (!Number.isFinite(settingsRunAt) || resultRunAt >= settingsRunAt)
  ) {
    return resultSettings;
  }
  return settings;
}

function nonNegativeCount(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
}

const REV_RETRYABLE_PUBLISH_BLOCK_REASONS = new Set([
  "rev_publish_transaction_rolled_back",
  "rev_publish_transaction_unavailable",
]);

function exactNonEmptyMembership(values, expectedCount) {
  if (!Array.isArray(values) || values.length !== expectedCount) return false;
  const normalised = values.map((value) => String(value || "").trim());
  return (
    normalised.every(Boolean)
    && new Set(normalised).size === expectedCount
  );
}

function revFailedPublishRetryState({ settings, runSettings, runSummary }) {
  const publishFailure = (
    runSettings?.last_run_status === "safety_blocked"
    && runSummary.mode === "publish_safety_blocked"
    && runSummary.requested_phase === "publish"
    && runSummary.effective_phase === "publish"
    && runSummary.requested_dry_run === false
    && runSummary.effective_dry_run === false
    && runSummary.safety_blocked === true
    && runSummary.publish_intentionally_skipped === false
    && runSummary.verify_intentionally_skipped === true
  );
  if (!publishFailure) return { applicable: false, failures: [] };

  const failures = [];
  const blockedReasons = [
    runSummary.blocked_reason,
    ...(Array.isArray(runSummary.blocked_reasons)
      ? runSummary.blocked_reasons
      : []),
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const uniqueBlockedReasons = [...new Set(blockedReasons)];
  if (
    uniqueBlockedReasons.length !== 1
    || !REV_RETRYABLE_PUBLISH_BLOCK_REASONS.has(uniqueBlockedReasons[0])
  ) {
    failures.push("publish_retry_reason_not_allowed");
  }
  if (runSummary.rev_publish_public_baseline_advanced !== false) {
    failures.push("publish_retry_public_baseline_state_invalid");
  }
  if (nonNegativeCount(runSummary.rev_publish_transaction_attempt_count) <= 0) {
    failures.push("publish_retry_attempt_missing");
  }

  const liveSetId = String(
    settings?.latest_successful_rev_live_set_id || "",
  ).trim();
  const candidateCount = Number(
    settings?.latest_successful_rev_live_set_candidate_count,
  );
  const recordCount = Number(
    settings?.latest_successful_rev_live_set_record_count,
  );
  const membershipComplete = (
    settings?.latest_successful_rev_live_set_complete === true
    && settings?.latest_successful_rev_live_set_source
      === "rev_apply_current_catalogue"
    && Number.isInteger(candidateCount)
    && candidateCount > 0
    && recordCount === candidateCount
    && exactNonEmptyMembership(
      settings?.latest_successful_rev_live_set_record_ids,
      candidateCount,
    )
    && exactNonEmptyMembership(
      settings?.latest_successful_rev_live_set_identities,
      candidateCount,
    )
    && REV_SCAN_SNAPSHOT_HASH_PATTERN.test(String(
      settings?.latest_successful_rev_live_set_identity_digest || "",
    ).trim())
  );
  if (!liveSetId || !membershipComplete) {
    failures.push("publish_retry_live_set_metadata_invalid");
  }

  const sourceSnapshotId = String(
    settings?.latest_successful_rev_scan_snapshot_id || "",
  ).trim();
  const sourceSnapshotHash = String(
    settings?.latest_successful_rev_scan_snapshot_hash || "",
  ).trim();
  if (
    !sourceSnapshotId
    || !REV_SCAN_SNAPSHOT_HASH_PATTERN.test(sourceSnapshotHash)
  ) {
    failures.push("publish_retry_source_snapshot_invalid");
  }

  const applyCompletedAt = Date.parse(
    settings?.latest_successful_rev_live_set_completed_at || "",
  );
  const failedPublishAt = Date.parse(runSettings?.last_run_at || "");
  if (
    !Number.isFinite(applyCompletedAt)
    || !Number.isFinite(failedPublishAt)
    || failedPublishAt < applyCompletedAt
  ) {
    failures.push("publish_retry_phase_order_invalid");
  }

  return {
    applicable: true,
    failures,
    blockedReason: uniqueBlockedReasons[0] || null,
  };
}

export function mckinneyPublishGateState(settings) {
  const currentLiveSetId = String(
    settings?.latest_successful_mckinney_live_set_id || "",
  ).trim();
  const gateLiveSetId = String(
    settings?.mckinney_publish_gate_live_set_id || "",
  ).trim();
  const currentDigest = String(
    settings?.latest_successful_mckinney_live_set_identity_digest || "",
  ).trim();
  const gateDigest = String(
    settings?.mckinney_publish_gate_identity_digest || "",
  ).trim();
  const currentCount = Number(
    settings?.latest_successful_mckinney_live_set_identity_count,
  );
  const gateCount = Number(settings?.mckinney_publish_gate_identity_count);
  const failures = [];

  if (settings?.publish_enabled !== true) failures.push("publish_enabled_disabled");
  if (settings?.expire_enabled !== false) failures.push("expiry_must_remain_disabled");
  if (settings?.scheduler_enabled !== false) failures.push("scheduler_must_remain_disabled");
  if (settings?.mckinney_publish_gate_allowed !== true) {
    failures.push(...(
      Array.isArray(settings?.mckinney_publish_gate_reasons)
        ? settings.mckinney_publish_gate_reasons
        : ["authoritative_publish_gate_not_allowed"]
    ));
  }
  if (!currentLiveSetId || gateLiveSetId !== currentLiveSetId) {
    failures.push("publish_gate_live_set_mismatch");
  }
  if (!currentDigest || gateDigest !== currentDigest) {
    failures.push("publish_gate_identity_digest_mismatch");
  }
  if (!Number.isInteger(currentCount) || currentCount <= 0 || gateCount !== currentCount) {
    failures.push("publish_gate_identity_count_mismatch");
  }
  return {
    enabled: failures.length === 0,
    failures: [...new Set(failures)],
    currentLiveSetId,
  };
}

export function llfPublishGateState(settings) {
  const currentLiveSetId = String(
    settings?.latest_successful_llf_live_set_id || "",
  ).trim();
  const gateLiveSetId = String(
    settings?.llf_publish_gate_live_set_id || "",
  ).trim();
  const currentDigest = String(
    settings?.latest_successful_llf_live_set_identity_digest || "",
  ).trim();
  const gateDigest = String(
    settings?.llf_publish_gate_identity_digest || "",
  ).trim();
  const currentCount = Number(
    settings?.latest_successful_llf_live_set_identity_count,
  );
  const gateCount = Number(settings?.llf_publish_gate_identity_count);
  const failures = [];

  if (settings?.publish_enabled !== true) failures.push("publish_enabled_disabled");
  if (settings?.llf_publish_gate_allowed !== true) {
    failures.push(...(
      Array.isArray(settings?.llf_publish_gate_reasons)
        ? settings.llf_publish_gate_reasons
        : ["authoritative_publish_gate_not_allowed"]
    ));
  }
  if (!currentLiveSetId || gateLiveSetId !== currentLiveSetId) {
    failures.push("publish_gate_live_set_mismatch");
  }
  if (!currentDigest || gateDigest !== currentDigest) {
    failures.push("publish_gate_identity_digest_mismatch");
  }
  if (!Number.isInteger(currentCount) || currentCount <= 0 || gateCount !== currentCount) {
    failures.push("publish_gate_identity_count_mismatch");
  }
  return {
    enabled: failures.length === 0,
    failures: [...new Set(failures)],
    currentLiveSetId,
  };
}

export function kiltedPublishGateState(settings) {
  const prefix = exactSetPrefixForOperator(settings?.operator_key);
  const currentLiveSetId = String(
    settings?.[`latest_successful_${prefix}_live_set_id`] || "",
  ).trim();
  const gateLiveSetId = String(
    settings?.[`${prefix}_publish_gate_live_set_id`] || "",
  ).trim();
  const currentDigest = String(
    settings?.[`latest_successful_${prefix}_live_set_identity_digest`] || "",
  ).trim();
  const gateDigest = String(
    settings?.[`${prefix}_publish_gate_identity_digest`] || "",
  ).trim();
  const currentCount = Number(
    settings?.[`latest_successful_${prefix}_live_set_identity_count`],
  );
  const gateCount = Number(settings?.[`${prefix}_publish_gate_identity_count`]);
  const failures = [];

  if (settings?.publish_enabled !== true) failures.push("publish_enabled_disabled");
  if (settings?.[`${prefix}_publish_gate_allowed`] !== true) {
    failures.push(...(
      Array.isArray(settings?.[`${prefix}_publish_gate_reasons`])
        ? settings[`${prefix}_publish_gate_reasons`]
        : ["authoritative_publish_gate_not_allowed"]
    ));
  }
  if (!currentLiveSetId || gateLiveSetId !== currentLiveSetId) {
    failures.push("publish_gate_live_set_mismatch");
  }
  if (!currentDigest || gateDigest !== currentDigest) {
    failures.push("publish_gate_identity_digest_mismatch");
  }
  if (!Number.isInteger(currentCount) || currentCount <= 0 || gateCount !== currentCount) {
    failures.push("publish_gate_identity_count_mismatch");
  }
  return {
    enabled: failures.length === 0,
    failures: [...new Set(failures)],
    currentLiveSetId,
  };
}

export function revPublishGateState({
  settings,
  resultSettings,
  executionLeaseStatus,
  busy = false,
}) {
  const durableRunSettings = latestAutomationRunSettings(settings, resultSettings);
  const runSummary = durableRunSettings?.last_run_summary || {};
  const currentLiveSetId = String(
    settings?.latest_successful_rev_live_set_id
      || durableRunSettings?.latest_successful_rev_live_set_id
      || "",
  ).trim();
  const verifiedLiveSetId = String(
    runSummary.rev_verify_selector_live_set_id || "",
  ).trim();
  const requestedLiveSetId = String(
    runSummary.rev_verify_requested_live_set_id || "",
  ).trim();
  const newlyVerifiedCount = nonNegativeCount(runSummary.auto_verified);
  const preVerifyPublishSafeCount = nonNegativeCount(
    runSummary.rev_publish_safe_count,
  );
  const publishableSignalCount = (
    newlyVerifiedCount + preVerifyPublishSafeCount
  );
  const verifyAccountedCount = nonNegativeCount(
    runSummary.rev_verify_accounted_record_count,
  );
  const verifyRejectedCount = nonNegativeCount(
    runSummary.rev_verify_rejected_record_count,
  );
  const fullyVerifiedExactSet = (
    verifyAccountedCount > 0
    && verifyRejectedCount === 0
    && runSummary.rev_verify_exact_set_accounted === true
  );
  const verifyRun = (
    runSummary.mode === "verify_only"
    || (
      runSummary.effective_phase === "verify"
      && runSummary.effective_dry_run === false
    )
  );
  const failedPublishRetry = revFailedPublishRetryState({
    settings,
    runSettings: durableRunSettings,
    runSummary,
  });
  const failures = [];

  if (settings?.publish_enabled !== true) failures.push("publish_disabled");
  if (!currentLiveSetId) failures.push("latest_live_set_missing");
  if (failedPublishRetry.applicable) {
    failures.push(...failedPublishRetry.failures);
  } else {
    if (durableRunSettings?.last_run_status !== "ok") {
      failures.push("latest_verify_not_successful");
    }
    if (!verifyRun) failures.push("latest_run_not_verify_only");
    if (
      !verifiedLiveSetId
      || verifiedLiveSetId !== currentLiveSetId
      || !requestedLiveSetId
      || requestedLiveSetId !== currentLiveSetId
      || runSummary.rev_verify_live_set_id_match !== true
    ) {
      failures.push("verify_live_set_mismatch");
    }
    if (runSummary.rev_verify_exact_set_accounted !== true) {
      failures.push("verify_exact_set_not_accounted");
    }
    if (
      runSummary.safety_blocked === true
      || runSummary.run_mode_safety_blocked === true
    ) {
      failures.push("verify_safety_blocked");
    }
    if (publishableSignalCount <= 0 && !fullyVerifiedExactSet) {
      failures.push("no_verified_unpublished_records");
    }
  }
  if (
    executionLeaseStatus?.status !== "ok"
    || executionLeaseStatus?.active !== false
  ) {
    failures.push(
      executionLeaseStatus?.active === true
        ? "execution_lease_active"
        : "execution_lease_status_unavailable",
    );
  }
  if (busy) failures.push("lifecycle_request_active");

  return {
    enabled: failures.length === 0,
    failures,
    currentLiveSetId,
    verifiedLiveSetId,
    newlyVerifiedCount,
    preVerifyPublishSafeCount,
    publishableSignalCount,
    verifyAccountedCount,
    verifyRejectedCount,
    fullyVerifiedExactSet,
    retryingFailedPublish: (
      failedPublishRetry.applicable
      && failures.length === 0
    ),
    retryablePublishBlockedReason: failedPublishRetry.blockedReason || null,
  };
}

export function revBrokenTileCleanupGateFailures(data) {
  if (!data) return ["Dry-run has not been run in this admin session."];
  const failures = [];
  const publicCount = Number(data.rev_public_image_public_count);
  const currentCount = Number(data.rev_public_image_current_live_public_count);
  const outsideCount = Number(data.rev_public_image_out_of_scope_public_count);
  if (data.status !== "ok") failures.push('status must be "ok"');
  if (data.blocked_reason !== null) failures.push("blocked_reason must be null");
  if (data.dry_run !== true) failures.push("response must be a dry-run");
  if (data.expected_live_set_id !== REV_BROKEN_TILE_CLEANUP_LIVE_SET_ID) {
    failures.push("expected live-set ID does not match");
  }
  if (!Number.isInteger(publicCount) || publicCount <= 0) {
    failures.push("public Rev count is missing or invalid");
  }
  if (!Number.isInteger(currentCount) || currentCount <= 0) {
    failures.push("current-live public count is missing or invalid");
  }
  if (outsideCount !== 2) failures.push("out-of-scope public count must be 2");
  if (Number.isInteger(publicCount) && Number.isInteger(currentCount) && publicCount !== currentCount + 2) {
    failures.push("public count does not reconcile with current-live plus 2 out-of-scope rows");
  }
  if (Number(data.rev_public_image_checked_count) !== 2) failures.push("checked count must be 2");
  if (Number(data.rev_public_image_server_failure_count) !== 2) failures.push("server failure count must be 2");
  if (Number(data.rev_public_image_explicit_out_of_scope_hide_count) !== 2) {
    failures.push("explicit hide decision count must be 2");
  }
  if ((data.missing_record_ids || []).length > 0) failures.push("an authorised record is missing");
  if (Number(data.rev_public_image_current_live_repair_count || 0) !== 0) {
    failures.push("current-live repair decisions are not allowed");
  }
  if (Number(data.rev_public_image_legacy_duplicate_replacement_count || 0) !== 0) {
    failures.push("replacement decisions are not allowed");
  }
  if (Number(data.hidden_count || 0) !== 0) failures.push("dry-run unexpectedly hid records");
  if (Number(data.repaired_count || 0) !== 0) failures.push("dry-run unexpectedly repaired records");
  if (Number(data.expired_hidden || 0) !== 0) failures.push("dry-run unexpectedly ran expiry");
  if (Number(data.deleted_count || 0) !== 0) failures.push("dry-run unexpectedly deleted records");
  const decisions = data.sample_rev_public_image_cleanup_decisions || [];
  if (decisions.length !== 2) failures.push("exactly two cleanup decisions are required");
  const expectedIds = new Set(REV_BROKEN_TILE_CLEANUP_RECORD_IDS);
  decisions.forEach((row) => {
    if (!expectedIds.delete(row.id)) failures.push(`unauthorised or duplicate record ID: ${row.id || "missing"}`);
    if (row.action !== "hide_out_of_scope_broken") failures.push(`wrong action for ${row.id || "record"}`);
    if (row.decision_reason !== "rev_legacy_public_out_of_scope_broken_image") {
      failures.push(`wrong decision reason for ${row.id || "record"}`);
    }
    if (row.rev_live_set_id === REV_BROKEN_TILE_CLEANUP_LIVE_SET_ID || row.rev_live_set_member === true) {
      failures.push(`current-live membership detected for ${row.id || "record"}`);
    }
    if (row.exact_current_live_equivalent_id || row.trusted_repair_image_url || row.replacement_image_check) {
      failures.push(`replacement or repair decision detected for ${row.id || "record"}`);
    }
    const statusCode = Number(row.image_check?.status_code);
    if (row.image_check?.ok !== false || !Number.isInteger(statusCode) || (statusCode >= 200 && statusCode < 300)) {
      failures.push(`server image failure is not proven for ${row.id || "record"}`);
    }
  });
  if (expectedIds.size > 0) failures.push("one or more authorised record IDs are absent");
  return [...new Set(failures)];
}

export function isSuccessfulRevBrokenTileCleanupLiveResult(data) {
  if (!data || data.status !== "ok" || data.dry_run !== false) return false;
  const affected = new Set(data.affected_record_ids || []);
  const publicBefore = Number(data.rev_public_image_public_count_before);
  const currentBefore = Number(data.rev_public_image_current_live_public_count_before);
  const outsideBefore = Number(data.rev_public_image_out_of_scope_public_count_before);
  return (
    Number(data.hidden_count) === 2
    && REV_BROKEN_TILE_CLEANUP_RECORD_IDS.every((id) => affected.delete(id))
    && affected.size === 0
    && Number.isInteger(publicBefore)
    && Number.isInteger(currentBefore)
    && outsideBefore === 2
    && Number(data.rev_public_image_public_count_after) === publicBefore - 2
    && Number(data.rev_public_image_current_live_public_count_after) === currentBefore
    && Number(data.rev_public_image_out_of_scope_public_count_after) === outsideBefore - 2
    && (data.current_live_affected_record_ids || []).length === 0
    && Number(data.deleted_count || 0) === 0
    && Number(data.expired_hidden || 0) === 0
    && Number(data.errors || 0) === 0
    && data.scheduler_enabled === false
    && data.expire_enabled === false
  );
}

export function parseBatchNumber(value, { fallback, min, max }) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const whole = Math.trunc(parsed);
  return Math.max(min, Math.min(max, whole));
}

export function parseAspireMaxNewRecords(value) {
  const candidate = value ?? ASPIRE_DEFAULT_MAX_NEW_RECORDS;
  if (candidate === "" || typeof candidate === "boolean") {
    throw new Error("Aspire max new records must be a whole number.");
  }
  const parsed = Number(candidate);
  if (!Number.isInteger(parsed)) {
    throw new Error("Aspire max new records must be a whole number.");
  }
  if (parsed < 0 || parsed > ASPIRE_MAX_NEW_RECORDS_PER_RUN) {
    throw new Error(
      `Aspire max new records must be between 0 and ${ASPIRE_MAX_NEW_RECORDS_PER_RUN}.`,
    );
  }
  return parsed;
}

export function parseMckinneyMaxNewRecords(value) {
  const candidate = value ?? MCKINNEY_DEFAULT_MAX_NEW_RECORDS;
  if (candidate === "" || typeof candidate === "boolean") {
    throw new Error("McKinney max new records must be a whole number.");
  }
  const parsed = Number(candidate);
  if (!Number.isInteger(parsed)) {
    throw new Error("McKinney max new records must be a whole number.");
  }
  if (parsed < 0 || parsed > MCKINNEY_MAX_NEW_RECORDS_PER_RUN) {
    throw new Error(
      `McKinney max new records must be between 0 and ${MCKINNEY_MAX_NEW_RECORDS_PER_RUN}.`,
    );
  }
  return parsed;
}

export function parseLlfMaxNewRecords(value) {
  const candidate = value ?? LLF_DEFAULT_MAX_NEW_RECORDS;
  if (candidate === "" || typeof candidate === "boolean") {
    throw new Error("LLF max new records must be a whole number.");
  }
  const parsed = Number(candidate);
  if (!Number.isInteger(parsed)) {
    throw new Error("LLF max new records must be a whole number.");
  }
  if (parsed < 0 || parsed > LLF_MAX_NEW_RECORDS_PER_RUN) {
    throw new Error(
      `LLF max new records must be between 0 and ${LLF_MAX_NEW_RECORDS_PER_RUN}.`,
    );
  }
  return parsed;
}

export function revExpiryGateFailures(response, currentLiveSetId) {
  const expiry = response?.expiry || response?.summary?.expiry || response;
  if (!expiry) return ["Run the Rev expiry dry-run in this admin session first."];
  const failures = [];
  const candidateIds = Array.isArray(expiry.candidate_ids) ? expiry.candidate_ids : [];
  const candidates = Array.isArray(expiry.sample_candidates)
    ? expiry.sample_candidates
    : (expiry.sample_would_expire || []);
  const candidateCount = Number(expiry.candidate_count ?? expiry.would_expire ?? 0);
  const cap = Number(expiry.max_expire_records);
  if (response?.status !== "ok") failures.push('status must be "ok"');
  if (expiry.dry_run !== true) failures.push("response must be a dry-run");
  if (expiry.safety_blocked === true) failures.push("expiry dry-run is safety-blocked");
  if ((expiry.blocked_reasons || []).length) failures.push("expiry has blocked reasons");
  if (!currentLiveSetId || expiry.latest_successful_rev_live_set_id !== currentLiveSetId) {
    failures.push("latest Rev live-set ID does not match current settings");
  }
  if (!Number.isInteger(candidateCount) || candidateCount < 0) failures.push("candidate count is invalid");
  if (candidateIds.length !== candidateCount || new Set(candidateIds).size !== candidateIds.length) {
    failures.push("candidate IDs do not exactly reconcile with candidate count");
  }
  if (candidates.length !== candidateCount) failures.push("every exact candidate must be present in the dry-run sample");
  if (Number.isFinite(cap) && candidateCount > cap) failures.push("candidate count exceeds expiry cap");
  const publishedStatusCount = Number(expiry.rev_published_status_count);
  const effectivePublicCount = Number(expiry.rev_effective_public_count);
  const hiddenPublishedCount = Number(expiry.rev_hidden_published_count);
  const currentLiveEffectiveCount = Number(expiry.rev_current_live_effective_public_count);
  const staleEffectiveCandidateCount = Number(expiry.rev_prior_live_set_effective_public_candidate_count);
  const unmanagedEffectiveCount = Number(expiry.rev_unmanaged_effective_public_count);
  const expectedAfter = Number(expiry.rev_expected_effective_public_count_after_expiry);
  const publicBeforeAlias = Number(expiry.current_public_rev_count);
  const publicAfterAlias = Number(expiry.expected_public_count_after_expiry);
  const reconciliationValues = [
    publishedStatusCount, effectivePublicCount, hiddenPublishedCount,
    currentLiveEffectiveCount, staleEffectiveCandidateCount,
    unmanagedEffectiveCount, expectedAfter,
  ];
  if (reconciliationValues.some((value) => !Number.isInteger(value) || value < 0)) {
    failures.push("effective-public reconciliation counters are missing or invalid");
  } else {
    if (publishedStatusCount !== effectivePublicCount + hiddenPublishedCount) {
      failures.push("published status does not reconcile with effective public plus hidden published");
    }
    if (staleEffectiveCandidateCount !== candidateCount) {
      failures.push("effective stale candidates do not reconcile with expiry candidates");
    }
    if (unmanagedEffectiveCount !== 0) failures.push("unmanaged effectively public Rev records exist");
    if (effectivePublicCount !== currentLiveEffectiveCount + staleEffectiveCandidateCount + unmanagedEffectiveCount) {
      failures.push("effective public scope does not reconcile");
    }
    if (expectedAfter !== effectivePublicCount - candidateCount) {
      failures.push("effective public before/after counts do not reconcile");
    }
    if (publicBeforeAlias !== effectivePublicCount || publicAfterAlias !== expectedAfter) {
      failures.push("legacy public count aliases do not match effective-public counts");
    }
  }
  const remaining = new Set(candidateIds);
  candidates.forEach((row) => {
    if (!remaining.delete(row.id)) failures.push(`missing, duplicate or unauthorised candidate ID: ${row.id || "unknown"}`);
    if (row.reason !== "rev_previous_live_set_record_absent_from_latest_complete_catalogue") {
      failures.push(`wrong expiry reason for ${row.id || "candidate"}`);
    }
    if (row.rev_live_set_id === currentLiveSetId || row.rev_live_set_member === true && row.previous_live_set_id === currentLiveSetId) {
      failures.push(`current-live candidate detected: ${row.id || "unknown"}`);
    }
    if (row.matched_current_catalogue === true) failures.push(`current catalogue match detected: ${row.id || "unknown"}`);
  });
  if (remaining.size) failures.push("one or more exact candidate IDs are missing");
  return [...new Set(failures)];
}

export function revExpiryApprovalMessage(status) {
  if (!status) return "Loading authoritative Rev expiry approval status.";
  const reason = status.disabled_reason;
  if (status.approval_valid === true && reason === "expiry_capability_disabled") {
    return "Dry-run approved. Enable Expiry to perform the live action.";
  }
  const messages = {
    no_successful_dry_run_approval: "No successful Rev expiry dry-run approval.",
    approval_expired: "The Rev expiry approval has expired.",
    live_set_changed: "The Rev live set changed after the dry-run.",
    candidate_set_changed: "The Rev expiry candidate set changed after the dry-run.",
    expected_public_count_changed: "The expected public count changed after the dry-run.",
    expiry_capability_disabled: "Expiry capability is disabled.",
    active_execution_lease: "Another Rev automation run has an active execution lease.",
    candidate_ceiling_exceeded: "The Rev expiry candidate count exceeds the configured ceiling.",
    approving_actor_mismatch: "This approval belongs to a different authorised admin.",
    approval_consumed: "The Rev expiry approval has already been consumed.",
    approval_claimed: "The Rev expiry approval is already claimed by an active run.",
    approval_failed: "The Rev expiry approval failed and requires a fresh dry-run.",
    approval_invalidated: "The Rev expiry approval is invalid and requires a fresh dry-run.",
    expiry_approval_id_mismatch: "The Rev expiry approval ID does not match.",
    safety_gate_blocked: "The Rev expiry safety gate is blocked.",
  };
  if (!reason && status.live_action_ready === true) {
    return "Dry-run approved. Live Rev expiry is unlocked.";
  }
  return messages[reason] || `Rev expiry blocked: ${reason || "approval status unavailable"}.`;
}

export function botbExpiryGateFailures(response, currentSnapshotId, nowMs = Date.now()) {
  const expiry = response?.expiry || response?.summary?.expiry || response;
  if (!expiry) return ["Run the BOTB expiry dry-run in this admin session first."];
  const failures = [];
  const candidateIds = Array.isArray(expiry.candidate_ids) ? expiry.candidate_ids : [];
  const candidates = Array.isArray(expiry.sample_candidates)
    ? expiry.sample_candidates
    : (expiry.sample_would_expire || []);
  const candidateCount = Number(expiry.candidate_count ?? expiry.would_expire ?? 0);
  const cap = Number(expiry.max_expire_records);
  const publicBefore = Number(expiry.public_before);
  const expectedAfter = Number(expiry.expected_public_after);
  if (response?.status !== "ok") failures.push('status must be "ok"');
  if (expiry.dry_run !== true) failures.push("response must be a dry-run");
  if (expiry.safety_blocked === true) failures.push("expiry dry-run is safety-blocked");
  if ((expiry.blocked_reasons || []).length) failures.push("expiry has blocked reasons");
  if (expiry.snapshot_reconciled !== true) failures.push("BOTB snapshot does not reconcile");
  if (!currentSnapshotId || expiry.latest_successful_botb_snapshot_id !== currentSnapshotId) {
    failures.push("latest BOTB snapshot ID does not match current settings");
  }
  if (!Number.isInteger(candidateCount) || candidateCount < 0) failures.push("candidate count is invalid");
  if (candidateIds.length !== candidateCount || new Set(candidateIds).size !== candidateIds.length) {
    failures.push("candidate IDs do not exactly reconcile with candidate count");
  }
  if (candidates.length !== candidateCount) failures.push("every exact candidate must be present for review");
  if (!Number.isInteger(cap) || cap < 0 || candidateCount > cap) failures.push("candidate count exceeds expiry cap");
  if (!Number.isInteger(publicBefore) || publicBefore < 0) failures.push("public-before count is invalid");
  if (!Number.isInteger(expectedAfter) || expectedAfter !== publicBefore - candidateCount) {
    failures.push("public before/after counts do not reconcile");
  }
  if (!expiry.expiry_approval_id) failures.push("expiry approval ID is missing");
  const approvalExpiresAt = Date.parse(expiry.approval_expires_at || "");
  if (!Number.isFinite(approvalExpiresAt) || approvalExpiresAt <= nowMs) {
    failures.push("expiry approval is missing or expired");
  }
  if (Number(expiry.deleted_count || 0) !== 0) failures.push("dry-run unexpectedly deleted records");
  if ((expiry.current_live_affected_record_ids || []).length) failures.push("current-live records are affected");
  const remaining = new Set(candidateIds);
  candidates.forEach((row) => {
    if (!remaining.delete(row.id)) failures.push(`missing, duplicate or unauthorised candidate ID: ${row.id || "unknown"}`);
    if (row.reason !== "prior_snapshot_exact_identity_absent_from_latest_complete_snapshot") {
      failures.push(`wrong expiry reason for ${row.id || "candidate"}`);
    }
    if (row.matched_current_catalogue === true) failures.push(`current catalogue match detected: ${row.id || "unknown"}`);
    if (!row.exact_identity || !(row.prior_snapshot_ids || []).length) {
      failures.push(`exact identity or prior snapshot evidence is missing for ${row.id || "candidate"}`);
    }
  });
  if (remaining.size) failures.push("one or more exact candidate IDs are missing");
  return [...new Set(failures)];
}

export function aspireExpiryGateFailures(response, currentLiveSetId) {
  const expiry = response?.expiry || response?.summary?.expiry || response;
  if (!expiry) return ["Run the Aspire expiry dry-run in this admin session first."];
  const failures = [];
  const candidateIds = Array.isArray(expiry.candidate_ids) ? expiry.candidate_ids : [];
  const candidateCount = Number(expiry.candidate_count ?? expiry.would_expire ?? 0);
  const cap = Number(expiry.max_expire_records);
  if (response?.status !== "ok") failures.push('status must be "ok"');
  if (expiry.dry_run !== true) failures.push("response must be a dry-run");
  if (expiry.safety_blocked === true) failures.push("expiry dry-run is safety-blocked");
  if ((expiry.blocked_reasons || []).length) failures.push("expiry has blocked reasons");
  if (expiry.aspire_expiry_scan_snapshot_reconciled !== true) {
    failures.push("Aspire scan snapshot does not reconcile");
  }
  if (
    !currentLiveSetId
    || expiry.aspire_expiry_latest_live_set_id !== currentLiveSetId
  ) {
    failures.push("latest Aspire live-set ID does not match current settings");
  }
  if (!Number.isInteger(candidateCount) || candidateCount < 0) {
    failures.push("candidate count is invalid");
  }
  if (
    candidateIds.length !== candidateCount
    || new Set(candidateIds).size !== candidateIds.length
  ) {
    failures.push("candidate IDs do not exactly reconcile with candidate count");
  }
  if (!Number.isInteger(cap) || cap < 0 || candidateCount > cap) {
    failures.push("candidate count exceeds expiry cap");
  }
  if (!expiry.expiry_approval_id) failures.push("expiry approval ID is missing");
  if (expiry.aspire_expiry_exact_candidate_set_match !== true) {
    failures.push("Aspire expiry candidate set does not reconcile");
  }
  if (
    Number(expiry.aspire_expiry_current_live_set_count)
      !== Number(expiry.aspire_expiry_current_live_verified_count)
    || Number(expiry.aspire_expiry_current_live_set_count)
      !== Number(expiry.aspire_expiry_current_live_published_count)
  ) {
    failures.push("current Aspire live set is not fully verified and public");
  }
  return [...new Set(failures)];
}

export function mckinneyExpiryGateFailures(response, currentLiveSetId) {
  const expiry = response?.expiry || response?.summary?.expiry || response;
  if (!expiry) return ["Run the McKinney expiry dry-run in this admin session first."];
  const failures = [];
  const candidateIds = Array.isArray(expiry.candidate_ids) ? expiry.candidate_ids : [];
  const candidateCount = Number(expiry.candidate_count ?? expiry.would_expire ?? 0);
  if (response?.status !== "ok") failures.push('status must be "ok"');
  if (expiry.safety_blocked === true) failures.push("expiry dry-run is safety-blocked");
  if ((expiry.blocked_reasons || []).length) failures.push("expiry has blocked reasons");
  if (
    !currentLiveSetId
    || expiry.mckinney_expiry_latest_live_set_id !== currentLiveSetId
  ) {
    failures.push("latest McKinney live-set ID does not match current settings");
  }
  if (!Number.isInteger(candidateCount) || candidateCount < 0) {
    failures.push("candidate count is invalid");
  }
  if (
    candidateIds.length !== candidateCount
    || new Set(candidateIds).size !== candidateIds.length
  ) {
    failures.push("candidate IDs do not exactly reconcile with candidate count");
  }
  if (expiry.mckinney_expiry_exact_candidate_set !== true) {
    failures.push("McKinney expiry candidate set does not reconcile");
  }
  if (expiry.mckinney_expiry_exact_set_accounted !== true) {
    failures.push("McKinney expiry accounting is incomplete");
  }
  if (expiry.approval_state && expiry.approval_state !== "active") {
    failures.push(`expiry approval is ${expiry.approval_state}`);
  }
  if (
    expiry.approval_ttl_seconds != null
    && Number(expiry.approval_ttl_seconds) <= 0
  ) {
    failures.push("expiry approval has expired");
  }
  if (!expiry.expiry_approval_id) failures.push("expiry approval ID is missing");
  return [...new Set(failures)];
}

export function llfExpiryGateFailures({
  settings,
  evidence,
  expireEnabled,
  nowMs = Date.now(),
}) {
  const failures = [];
  const currentLiveSetId = String(
    settings?.latest_successful_llf_live_set_id || "",
  ).trim();
  const candidateIds = Array.isArray(evidence?.candidate_ids)
    ? evidence.candidate_ids
    : [];
  const candidateCount = Number(evidence?.candidate_count);
  const maxExpireRecords = Number(evidence?.max_expire_records);

  if (expireEnabled !== true) failures.push("expire_disabled");
  if (!evidence) return [...failures, "approval_unavailable"];
  if (evidence.approval_state !== "active") failures.push("approval_not_active");
  if (evidence.approval_valid === false) failures.push("approval_invalid");
  if (!currentLiveSetId) failures.push("current_live_set_missing");
  if (evidence.llf_expiry_live_set_id !== currentLiveSetId) {
    failures.push("live_set_id_mismatch");
  }
  if (
    !evidence.llf_expiry_live_set_identity_digest
    || evidence.llf_expiry_live_set_identity_digest
      !== settings?.latest_successful_llf_live_set_identity_digest
  ) {
    failures.push("live_set_digest_mismatch");
  }
  if (
    Number(evidence.llf_expiry_current_live_set_count)
    !== Number(settings?.latest_successful_llf_live_set_identity_count)
  ) {
    failures.push("live_set_count_mismatch");
  }
  if (
    Number(evidence.expiry_generation)
    !== Number(settings?.expiry_generation)
  ) {
    failures.push("expiry_generation_mismatch");
  }
  if (maxExpireRecords !== Number(settings?.max_expire_records)) {
    failures.push("expiry_cap_mismatch");
  }
  if (!evidence.expiry_approval_id) failures.push("approval_id_missing");
  if (!Array.isArray(evidence.candidate_ids)) {
    failures.push("candidate_ids_missing");
  } else if (candidateIds.length !== candidateCount) {
    failures.push("candidate_count_mismatch");
  }
  if (!Number.isFinite(maxExpireRecords) || candidateIds.length > maxExpireRecords) {
    failures.push("candidate_cap_exceeded");
  }
  if (
    evidence.approval_expires_at
    && !(
      Number.isFinite(Date.parse(evidence.approval_expires_at))
      && Date.parse(evidence.approval_expires_at) > nowMs
    )
  ) {
    failures.push("approval_expired_or_invalid");
  }
  return [...new Set(failures)];
}

export function llfExpiryEvidenceFromRunResult(runResult) {
  const expiry = runResult?.expiry || runResult?.summary?.expiry || null;
  if (!expiry) return null;
  const diagnostics = runResult?.diagnostics || {};
  const runSettings = runResult?.settings || {};
  const candidateIds = Array.isArray(expiry.candidate_ids)
    ? expiry.candidate_ids
    : (Array.isArray(diagnostics.candidate_ids) ? diagnostics.candidate_ids : []);

  return {
    ...expiry,
    llf_expiry_live_set_id: (
      expiry.llf_expiry_live_set_id
      ?? diagnostics.llf_expiry_live_set_id
    ),
    llf_expiry_live_set_identity_digest: (
      expiry.llf_expiry_live_set_identity_digest
      ?? diagnostics.llf_expiry_live_set_identity_digest
    ),
    llf_expiry_current_live_set_count: (
      expiry.llf_expiry_current_live_set_count
      ?? diagnostics.llf_expiry_current_live_set_count
    ),
    llf_expiry_expected_public_count_after: (
      expiry.llf_expiry_expected_public_count_after
      ?? diagnostics.llf_expiry_expected_public_count_after
    ),
    candidate_ids: candidateIds,
    candidate_count: (
      expiry.candidate_count
      ?? expiry.llf_expiry_candidate_count
      ?? diagnostics.llf_expiry_candidate_count
      ?? candidateIds.length
    ),
    max_expire_records: (
      expiry.max_expire_records
      ?? expiry.llf_expiry_max_records
      ?? diagnostics.llf_expiry_max_records
      ?? runSettings.max_expire_records
    ),
    expiry_generation: (
      expiry.expiry_generation
      ?? diagnostics.expiry_generation
      ?? runSettings.expiry_generation
    ),
  };
}

export function parseKiltedMaxNewRecords(value) {
  const candidate = value ?? KILTED_DEFAULT_MAX_NEW_RECORDS;
  if (candidate === "" || typeof candidate === "boolean") {
    throw new Error("Max new records must be a whole number.");
  }
  const parsed = Number(candidate);
  if (!Number.isInteger(parsed)) {
    throw new Error("Max new records must be a whole number.");
  }
  if (parsed < 0 || parsed > KILTED_MAX_NEW_RECORDS_PER_RUN) {
    throw new Error(
      `Max new records must be between 0 and ${KILTED_MAX_NEW_RECORDS_PER_RUN}.`,
    );
  }
  return parsed;
}

export function kiltedExpiryGateFailures({
  settings,
  evidence,
  expireEnabled,
  nowMs = Date.now(),
}) {
  const prefix = exactSetPrefixForOperator(settings?.operator_key);
  const failures = [];
  const currentLiveSetId = String(
    settings?.[`latest_successful_${prefix}_live_set_id`] || "",
  ).trim();
  const candidateIds = Array.isArray(evidence?.candidate_ids)
    ? evidence.candidate_ids
    : [];
  const candidateCount = Number(evidence?.candidate_count);
  const maxExpireRecords = Number(evidence?.max_expire_records);

  if (expireEnabled !== true) failures.push("expire_disabled");
  if (!evidence) return [...failures, "approval_unavailable"];
  if (evidence.approval_state !== "active") failures.push("approval_not_active");
  if (evidence.approval_valid === false) failures.push("approval_invalid");
  if (!currentLiveSetId) failures.push("current_live_set_missing");
  if (evidence.kilted_expiry_live_set_id !== currentLiveSetId) {
    failures.push("live_set_id_mismatch");
  }
  if (
    !evidence.kilted_expiry_live_set_identity_digest
    || evidence.kilted_expiry_live_set_identity_digest
      !== settings?.[`latest_successful_${prefix}_live_set_identity_digest`]
  ) {
    failures.push("live_set_digest_mismatch");
  }
  if (
    Number(evidence.kilted_expiry_current_live_set_count)
    !== Number(settings?.[`latest_successful_${prefix}_live_set_identity_count`])
  ) {
    failures.push("live_set_count_mismatch");
  }
  if (Number(evidence.expiry_generation) !== Number(settings?.expiry_generation)) {
    failures.push("expiry_generation_mismatch");
  }
  if (maxExpireRecords !== Number(settings?.max_expire_records)) {
    failures.push("expiry_cap_mismatch");
  }
  if (!evidence.expiry_approval_id) failures.push("approval_id_missing");
  if (prefix === "bright" && (
    typeof evidence.candidate_id_digest !== "string"
    || !/^[0-9a-f]{64}$/.test(evidence.candidate_id_digest)
  )) {
    failures.push("candidate_digest_missing_or_invalid");
  }
  if (!Array.isArray(evidence.candidate_ids)) {
    failures.push("candidate_ids_missing");
  } else if (candidateIds.length !== candidateCount) {
    failures.push("candidate_count_mismatch");
  }
  if (!Number.isFinite(maxExpireRecords) || candidateIds.length > maxExpireRecords) {
    failures.push("candidate_cap_exceeded");
  }
  if (
    evidence.approval_expires_at
    && !(
      Number.isFinite(Date.parse(evidence.approval_expires_at))
      && Date.parse(evidence.approval_expires_at) > nowMs
    )
  ) {
    failures.push("approval_expired_or_invalid");
  }
  return [...new Set(failures)];
}

export function kiltedExpiryEvidenceFromRunResult(runResult) {
  const expiry = runResult?.expiry || runResult?.summary?.expiry || null;
  if (!expiry) return null;
  const diagnostics = runResult?.diagnostics || {};
  const runSettings = runResult?.settings || {};
  const prefix = exactSetPrefixForOperator(runResult?.operator_key);
  const candidateIds = Array.isArray(expiry.candidate_ids)
    ? expiry.candidate_ids
    : (Array.isArray(diagnostics.candidate_ids) ? diagnostics.candidate_ids : []);

  return {
    ...expiry,
    kilted_expiry_live_set_id: (
      expiry[`${prefix}_expiry_live_set_id`]
      ?? diagnostics[`${prefix}_expiry_live_set_id`]
    ),
    kilted_expiry_live_set_identity_digest: (
      expiry[`${prefix}_expiry_live_set_identity_digest`]
      ?? diagnostics[`${prefix}_expiry_live_set_identity_digest`]
    ),
    kilted_expiry_current_live_set_count: (
      expiry[`${prefix}_expiry_current_live_set_count`]
      ?? diagnostics[`${prefix}_expiry_current_live_set_count`]
    ),
    kilted_expiry_expected_public_count_after: (
      expiry[`${prefix}_expiry_expected_public_count_after`]
      ?? diagnostics[`${prefix}_expiry_expected_public_count_after`]
    ),
    candidate_ids: candidateIds,
    candidate_count: (
      expiry.candidate_count
      ?? expiry[`${prefix}_expiry_candidate_count`]
      ?? diagnostics[`${prefix}_expiry_candidate_count`]
      ?? candidateIds.length
    ),
    max_expire_records: (
      expiry.max_expire_records
      ?? expiry[`${prefix}_expiry_max_records`]
      ?? diagnostics[`${prefix}_expiry_max_records`]
      ?? runSettings.max_expire_records
    ),
    expiry_generation: (
      expiry.expiry_generation
      ?? diagnostics.expiry_generation
      ?? runSettings.expiry_generation
    ),
  };
}

export function mckinneyExpiryApprovalFromSettings(settings) {
  if (settings?.mckinney_expiry_approval_present !== true) return null;
  return {
    status: "ok",
    expiry: {
      expiry_approval_id: settings.mckinney_expiry_approval_id,
      approval_state: settings.mckinney_expiry_approval_state,
      approval_ttl_seconds: settings.mckinney_expiry_approval_ttl_seconds,
      candidate_ids: settings.mckinney_expiry_approval_candidate_ids || [],
      candidate_count: settings.mckinney_expiry_approval_candidate_count ?? 0,
      mckinney_expiry_latest_live_set_id:
        settings.mckinney_expiry_approval_live_set_id,
      mckinney_expiry_exact_candidate_set:
        settings.mckinney_expiry_approval_exact_set_accounted === true,
      mckinney_expiry_exact_set_accounted:
        settings.mckinney_expiry_approval_exact_set_accounted === true,
      mckinney_expiry_retained_record_ids:
        settings.mckinney_expiry_approval_retained_ids || [],
      mckinney_expiry_protected_record_ids:
        settings.mckinney_expiry_approval_protected_ids || [],
      mckinney_expiry_expected_public_count_before:
        settings.mckinney_expiry_approval_expected_public_before,
      mckinney_expiry_expected_public_count_after:
        settings.mckinney_expiry_approval_expected_public_after,
      max_expire_records:
        settings.mckinney_expiry_approval_candidate_ceiling,
      blocked_reasons: [],
      safety_blocked: false,
    },
  };
}

export function selectMckinneyExpiryEvidence({
  dryRunResult,
  persistedApproval,
  approvalIdBeforeDryRun,
}) {
  if (!dryRunResult) return persistedApproval || null;
  if (dryRunResult?.status !== "ok") return null;

  const dryRunApprovalId = String(
    dryRunResult?.expiry?.expiry_approval_id
      || dryRunResult?.summary?.expiry?.expiry_approval_id
      || "",
  ).trim();
  const persistedApprovalId = String(
    persistedApproval?.expiry?.expiry_approval_id
      || persistedApproval?.summary?.expiry?.expiry_approval_id
      || "",
  ).trim();
  const previousApprovalId = String(approvalIdBeforeDryRun || "").trim();

  if (!dryRunApprovalId || !persistedApprovalId) return null;
  if (persistedApprovalId === dryRunApprovalId) return persistedApproval;
  if (previousApprovalId && persistedApprovalId === previousApprovalId) return null;
  return persistedApproval;
}

export function pristineCountAcceptanceOption(settings, result) {
  const runSettings = latestAutomationRunSettings(settings, result?.settings);
  if (runSettings?.operator_key !== "pristine") return null;
  const failedResult = result?.operator_key === "pristine" && result?.phase === "scan"
    && result?.status === "safety_blocked";
  const summary = failedResult ? result.summary || {} : runSettings?.last_run_summary || {};
  const lease = runSettings?.execution_lease || {};
  const retained = summary.pristine_count_acceptance_reference;
  if (summary.pristine_count_acceptance_contract || (!failedResult && (
    runSettings?.last_run_status !== "safety_blocked" || lease.active !== false
    || summary.phase !== "scan"
  ))) return null;
  const reference = retained || {
    reference_run_id: lease.run_id,
    declared_count: summary.pristine_source_declared_count,
    source_identity_count: summary.pristine_complete_source_identity_count,
    identity_digest: summary.pristine_complete_source_identity_digest,
    settings_generation: lease.settings_generation,
    schedule_generation: lease.schedule_generation,
  };
  if (!retained && (JSON.stringify(summary.pristine_scan_completeness_blockers) !== JSON.stringify([
    "pristine_source_declared_count_mismatch",
  ]) || lease.completion_status !== "safety_blocked")) return null;
  const count = reference.source_identity_count;
  const declared = reference.declared_count;
  if (!/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/.test(reference.reference_run_id || "")
    || !Number.isInteger(count) || count < 1 || !Number.isInteger(declared) || declared !== count + 1
    || !/^[0-9a-f]{64}$/.test(reference.identity_digest || "")
    || reference.settings_generation !== runSettings?.settings_generation
    || reference.schedule_generation !== runSettings?.schedule_generation) return null;
  return { runId: reference.reference_run_id, count, declared };
}

export function buildOperatorAutomationRunPayload({
  operatorKey,
  dryRun,
  phase = "auto",
  limitPerOperator,
  maxDetails,
  maxNewRecords = ASPIRE_DEFAULT_MAX_NEW_RECORDS,
  detailFetchLimit,
  detailFetchOffset,
  automatedNetworkDiscovery = false,
  scanSnapshotId,
  scanSnapshotHash,
  aspireInitialReplacementAuthorisationId,
  aspireInitialReplacementAuthorisationToken,
  expectedLiveSetId,
  candidateIds,
  expiryApprovalId,
  pristineCountAcceptanceRunId,
}) {
  const isBotbPayload = operatorKey === "botb";
  const isDcgPayload = operatorKey === "dcg";
  const isElitePayload = operatorKey === "elite";
  const isSevenDaysPayload = operatorKey === "7days";
  const isClickPayload = operatorKey === "click";
  const isJonnyFromDonnyPayload = operatorKey === "jonnyfromdonny";
  const isFeelGoodPayload = operatorKey === "feelgood";
  const isAspirePayload = operatorKey === "aspire";
  const isMckinneyPayload = operatorKey === "mckinney";
  const isLlfPayload = operatorKey === "llf";
  const isKiltedPayload = ["kilted", "ukcc", "bounty", "storm", "redline", "prizeparadise", "apex", "jammy", "pawsome", "lincolnshire", "drift", "palladium", "pristine", "caddy", "hotcomps", "thatprizeguy", "giveawayguys", "bright"].includes(operatorKey);
  const isJonnyFromDonnyScanPayload = (
    isJonnyFromDonnyPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isFeelGoodScanPayload = (
    isFeelGoodPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isAspireScanPayload = (
    isAspirePayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isMckinneyScanPayload = (
    isMckinneyPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isLlfScanPayload = (
    isLlfPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isKiltedScanPayload = (
    isKiltedPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isClickScanPayload = (
    isClickPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isSevenDaysScanPayload = (
    isSevenDaysPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isSevenDaysExpiryPayload = (
    isSevenDaysPayload
    && phase === "expire"
  );
  const isBotbScanPayload = (
    isBotbPayload
    && dryRun === true
    && (phase === "auto" || phase === "scan")
  );
  const isBotbApplyPayload = (
    isBotbPayload
    && dryRun === false
    && phase === "apply"
  );
  const isBotbVerifyPayload = (
    isBotbPayload
    && dryRun === false
    && phase === "verify"
  );
  const isBotbPublishPayload = (
    isBotbPayload
    && dryRun === false
    && (phase === "publish" || phase === "publish_public_catalogue")
  );
  const isBotbPublicCataloguePayload = (
    isBotbPayload
    && dryRun === false
    && phase === "publish_public_catalogue"
  );
  const isBotbExpiryDryRunPayload = (
    isBotbPayload
    && dryRun === true
    && phase === "expire"
  );
  const isBotbExpiryLivePayload = (
    isBotbPayload
    && dryRun === false
    && phase === "expire"
  );
  const isBotbSafeExplicitPayload = (
    isBotbScanPayload
    || isBotbApplyPayload
    || isBotbVerifyPayload
    || isBotbPublishPayload
    || isBotbPublicCataloguePayload
    || isBotbExpiryDryRunPayload
    || isBotbExpiryLivePayload
  );
  const resolvedDryRun = (isBotbPayload && !isBotbSafeExplicitPayload) || isSevenDaysScanPayload || isClickScanPayload || isJonnyFromDonnyScanPayload || isFeelGoodScanPayload || isAspireScanPayload || isMckinneyScanPayload || isLlfScanPayload || isKiltedScanPayload ? true : dryRun;
  const resolvedPhase = (isBotbPayload && !isBotbSafeExplicitPayload) || isBotbScanPayload || isSevenDaysScanPayload || isClickScanPayload || isJonnyFromDonnyScanPayload || isFeelGoodScanPayload || isAspireScanPayload || isMckinneyScanPayload || isLlfScanPayload || isKiltedScanPayload
    ? "scan"
    : isBotbPublicCataloguePayload
    ? "publish"
    : phase;
  const aspireApplyPayload = isAspirePayload && dryRun === false && phase === "apply";
  const aspireApplyPreflightPayload = isAspirePayload && dryRun === true && phase === "apply_preflight";
  const aspireVerifyPayload = isAspirePayload && dryRun === false && phase === "verify";
  const aspirePublishPayload = isAspirePayload && dryRun === false && phase === "publish";
  const mckinneyApplyPayload = isMckinneyPayload && dryRun === false && phase === "apply";
  const mckinneyApplyPreflightPayload = isMckinneyPayload && dryRun === true && phase === "apply_preflight";
  const mckinneyVerifyPayload = isMckinneyPayload && dryRun === false && phase === "verify";
  const mckinneyPublishPayload = isMckinneyPayload && dryRun === false && phase === "publish";
  const llfApplyPayload = isLlfPayload && dryRun === false && phase === "apply";
  const llfApplyPreflightPayload = isLlfPayload && dryRun === true && phase === "apply_preflight";
  const llfVerifyPayload = isLlfPayload && dryRun === false && phase === "verify";
  const llfPublishPayload = isLlfPayload && dryRun === false && phase === "publish";
  const kiltedApplyPayload = isKiltedPayload && dryRun === false && phase === "apply";
  const kiltedApplyPreflightPayload = isKiltedPayload && dryRun === true && phase === "apply_preflight";
  const kiltedVerifyPayload = isKiltedPayload && dryRun === false && phase === "verify";
  const kiltedPublishPayload = isKiltedPayload && dryRun === false && phase === "publish";
  const clickApplyPreflightPayload = (
    isClickPayload && dryRun === true && phase === "apply_preflight"
  );
  const revApplyPayload = (
    operatorKey === "revcomps"
    && dryRun === false
    && (phase === "auto" || phase === "apply")
  );
  const dcgApplyPayload = (
    isDcgPayload && dryRun === false && phase === "apply"
  );
  const dcgApplyPreflightPayload = (
    isDcgPayload && dryRun === true && phase === "apply_preflight"
  );
  const eliteApplyPayload = (
    isElitePayload && dryRun === false && phase === "apply"
  );
  const payload = {
    operator_key: operatorKey,
    dry_run: resolvedDryRun,
    phase: resolvedPhase,
    limit_per_operator: operatorKey === "revcomps"
      ? parseBatchNumber(limitPerOperator, {
        fallback: MAX_DETAIL_FETCH_LIMIT,
        min: 1,
        max: MAX_DETAIL_FETCH_LIMIT,
      })
      : operatorKey === "jonnyfromdonny"
      ? (limitPerOperator ?? JFD_DEFAULT_LIMIT_PER_OPERATOR)
      : operatorKey === "feelgood"
      ? (limitPerOperator ?? FEELGOOD_DEFAULT_LIMIT_PER_OPERATOR)
      : operatorKey === "aspire"
      ? ((aspireVerifyPayload || aspirePublishPayload)
        ? ASPIRE_CONTROLLED_PHASE_MAX_DETAILS
        : (limitPerOperator ?? ASPIRE_DEFAULT_LIMIT_PER_OPERATOR))
      : operatorKey === "mckinney"
      ? (limitPerOperator ?? MCKINNEY_DEFAULT_LIMIT_PER_OPERATOR)
      : operatorKey === "llf"
      ? (limitPerOperator ?? LLF_DEFAULT_LIMIT_PER_OPERATOR)
      : isKiltedPayload
      ? (limitPerOperator ?? KILTED_DEFAULT_LIMIT_PER_OPERATOR)
      : (limitPerOperator ?? DEFAULT_LIMIT_PER_OPERATOR),
    max_details: isClickPayload && ["verify", "publish", "expire"].includes(resolvedPhase)
      ? DEFAULT_LIMIT_PER_OPERATOR
      : operatorKey === "7days"
      ? SEVENDAYS_MAX_DETAILS
      : operatorKey === "aspire"
      ? ((aspireVerifyPayload || aspirePublishPayload)
        ? ASPIRE_CONTROLLED_PHASE_MAX_DETAILS
        : parseBatchNumber(maxDetails, {
          fallback: ASPIRE_DEFAULT_SCAN_MAX_DETAILS,
          min: 1,
          max: ASPIRE_SCAN_MAX_DETAILS,
        }))
      : operatorKey === "mckinney"
      ? ((mckinneyVerifyPayload || mckinneyPublishPayload)
        ? MCKINNEY_DEFAULT_MAX_DETAILS
        : parseBatchNumber(maxDetails, {
          fallback: MCKINNEY_DEFAULT_MAX_DETAILS,
          min: 1,
          max: MCKINNEY_MAX_DETAILS,
        }))
      : operatorKey === "llf"
      ? ((llfVerifyPayload || llfPublishPayload)
        ? LLF_DEFAULT_MAX_DETAILS
        : parseBatchNumber(maxDetails, {
          fallback: LLF_DEFAULT_MAX_DETAILS,
          min: 1,
          max: LLF_MAX_DETAILS,
        }))
      : isKiltedPayload
      ? ((kiltedVerifyPayload || kiltedPublishPayload)
        ? KILTED_DEFAULT_MAX_DETAILS
        : parseBatchNumber(maxDetails, {
          fallback: KILTED_DEFAULT_MAX_DETAILS,
          min: 1,
          max: KILTED_MAX_DETAILS,
        }))
      : (maxDetails ?? DEFAULT_MAX_DETAILS),
  };
  if (pristineCountAcceptanceRunId) {
    if (operatorKey !== "pristine" || resolvedPhase !== "scan" || resolvedDryRun !== true
      || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/.test(pristineCountAcceptanceRunId)) {
      throw new Error("Pristine count acceptance requires the referenced failed Scan only.");
    }
    payload.pristine_count_acceptance_run_id = pristineCountAcceptanceRunId;
  }
  if (isSevenDaysExpiryPayload) {
    payload.max_details = 80;
    payload.max_expire_records = 20;
  }
  if (operatorKey === "7days" && resolvedDryRun === false && resolvedPhase === "apply") {
    const snapshotReference = normaliseSevenDaysScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "7Days Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
    payload.max_new_records = 40;
  }
  if (dcgApplyPayload || dcgApplyPreflightPayload) {
    const snapshotReference = normaliseDcgScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "DCG Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
  }
  if (operatorKey === "dcg") {
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (eliteApplyPayload) {
    const snapshotReference = normaliseEliteScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "Elite Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
  }
  if (operatorKey === "elite") {
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (operatorKey === "click" && (
    (resolvedDryRun === false && resolvedPhase === "apply")
    || clickApplyPreflightPayload
  )) {
    const snapshotReference = normaliseClickScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "Click Apply Preflight and Apply require a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
  }
  if (operatorKey === "aspire" && (
    (resolvedDryRun === false && resolvedPhase === "apply")
    || aspireApplyPreflightPayload
  )) {
    payload.max_new_records = parseAspireMaxNewRecords(maxNewRecords);
    payload.automated_network_discovery = true;
    if (
      aspireInitialReplacementAuthorisationId
      && aspireInitialReplacementAuthorisationToken
    ) {
      payload.aspire_initial_replacement_authorisation_id = (
        aspireInitialReplacementAuthorisationId
      );
      payload.aspire_initial_replacement_authorisation_token = (
        aspireInitialReplacementAuthorisationToken
      );
    }
  }
  if (aspireApplyPayload) {
    const snapshotReference = normaliseAspireScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "Aspire Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
  }
  if (isMckinneyPayload && (
    mckinneyApplyPayload || mckinneyApplyPreflightPayload
  )) {
    payload.max_new_records = parseMckinneyMaxNewRecords(maxNewRecords);
  }
  if (mckinneyApplyPayload || mckinneyApplyPreflightPayload) {
    const snapshotReference = normaliseMckinneyScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "McKinney Apply preflight/Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
  }
  if (isLlfPayload && (llfApplyPayload || llfApplyPreflightPayload)) {
    payload.max_new_records = parseLlfMaxNewRecords(maxNewRecords);
    const snapshotReference = normaliseLlfScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "LLF Apply preflight/Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
  }
  if (isKiltedPayload && (kiltedApplyPayload || kiltedApplyPreflightPayload)) {
    payload.max_new_records = parseKiltedMaxNewRecords(maxNewRecords);
    const snapshotReference = normaliseKiltedScanSnapshotReference(
      scanSnapshotId,
      scanSnapshotHash,
    );
    if (!snapshotReference) {
      throw new Error(
        "Apply preflight/Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
      );
    }
    payload.scan_snapshot_id = snapshotReference.id;
    payload.scan_snapshot_hash = snapshotReference.hash;
  }
  if (operatorKey === "aspire" && resolvedDryRun === false && resolvedPhase === "verify") {
    payload.automated_network_discovery = true;
  }
  if (operatorKey === "aspire" && resolvedDryRun === false && resolvedPhase === "publish") {
    payload.automated_network_discovery = true;
  }
  if (operatorKey === "revcomps") {
    payload.max_details = parseBatchNumber(detailFetchLimit, {
      fallback: DEFAULT_DETAIL_FETCH_LIMIT,
      min: 1,
      max: MAX_DETAIL_FETCH_LIMIT,
    });
    payload.detail_fetch_offset = parseBatchNumber(detailFetchOffset, {
      fallback: 0,
      min: 0,
      max: MAX_DETAIL_FETCH_OFFSET,
    });
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
    if (revApplyPayload) {
      const snapshotReference = normaliseRevScanSnapshotReference(
        scanSnapshotId,
        scanSnapshotHash,
      );
      if (!snapshotReference) {
        throw new Error(
          "Rev Apply requires a ready immutable snapshot ID and lowercase SHA-256 hash.",
        );
      }
      payload.scan_snapshot_id = snapshotReference.id;
      payload.scan_snapshot_hash = snapshotReference.hash;
    }
  }
  if (operatorKey === "botb") {
    if (isBotbApplyPayload) {
      const snapshotReference = normaliseBotbScanSnapshotReference(
        scanSnapshotId,
        scanSnapshotHash,
      );
      if (!snapshotReference) {
        throw new Error(
          "BOTB Apply requires a complete immutable snapshot ID and lowercase SHA-256 hash.",
        );
      }
      payload.scan_snapshot_id = snapshotReference.id;
      payload.scan_snapshot_hash = snapshotReference.hash;
    }
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (operatorKey === "click") {
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (operatorKey === "aspire") {
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (operatorKey === "mckinney") {
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (operatorKey === "llf") {
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (isKiltedPayload) {
    if (expectedLiveSetId) payload.expected_live_set_id = expectedLiveSetId;
    if (Array.isArray(candidateIds)) payload.candidate_ids = [...candidateIds];
    if (expiryApprovalId) payload.expiry_approval_id = expiryApprovalId;
  }
  if (operatorKey === "aspire" && (automatedNetworkDiscovery || isAspireScanPayload)) {
    payload.automated_network_discovery = true;
  }
  return payload;
}

export function buildRevCheckpointPayload(phase, options) {
  const phases = ["checkpoint_scan", "checkpoint_preflight", "checkpoint_apply", "checkpoint_verify", "checkpoint_publish"];
  if (!phases.includes(phase) || !options.requestId) throw new Error("A versioned checkpoint phase and operation identity are required.");
  const payload = { operator_key: "revcomps", workflow_version: "rev_create_only_checkpoint_v1",
    phase, dry_run: phase === "checkpoint_scan" || phase === "checkpoint_preflight",
    operation_request_id: options.requestId };
  if (options.checkpointId) {
    if (!options.checkpointHash) throw new Error("Inspect the exact checkpoint before continuing.");
    payload.checkpoint_id = options.checkpointId;
    payload.checkpoint_hash = options.checkpointHash;
  } else {
    if (phase !== "checkpoint_scan" || !options.preview?.preview_hash || options.drainConfirmed !== true) {
      throw new Error("Inspect the activation preview and confirm matching external writer-drain evidence.");
    }
    payload.manifest_id = options.preview.manifest_id;
    payload.manifest_hash = options.preview.manifest_hash;
    payload.activation_preview_hash = options.preview.preview_hash;
    payload.old_writer_drain_confirmed = true;
  }
  if (options.reconcileIntentId) payload.reconcile_intent_id = options.reconcileIntentId;
  return payload;
}

export function buildRevSourceManifestPayload() {
  return { operator_key: "revcomps", dry_run: true,
    phase: "source_manifest_capture", workflow_version: "rev_source_manifest_v1" };
}


const REV_C0A_VERSION = "rev_c0a_source_successor_handoff_v1";
const REV_C0A_FENCE_VERSION = "rev_c0a_provider_retirement_write_fence_v1";
const REV_C0A_BASE = "/admin/operator-automation/revcomps/incident-c0a";
const REV_C0A_INTENT = "c0a7afc660db4fbbbaf807b187af1c6c403adcd247a456ab732397e00f35b173";
const revC0aHash = (value) => typeof value === "string" && /^[0-9a-f]{64}$/.test(value);

export function buildRevC0aSuccessorCheckpointPayload(phase, options) {
  if (!["checkpoint_scan", "checkpoint_preflight", "checkpoint_apply", "checkpoint_verify", "checkpoint_publish"].includes(phase)
      || typeof options.requestId !== "string" || !options.requestId
      || !revC0aHash(options.handoffHash) || !revC0aHash(options.terminalHash)
      || options.otherWritersDrained !== true || options.retirementConfirmed !== true) {
    throw new Error("Inspect the exact retired-capture handoff and confirm the separate writer evidence.");
  }
  const payload = { phase, dry_run: ["checkpoint_scan", "checkpoint_preflight"].includes(phase),
    operation_request_id: options.requestId, source_handoff_hash: options.handoffHash,
    source_terminal_hash: options.terminalHash, other_old_writer_drain_confirmed: true,
    c0a_retirement_handoff_confirmed: true };
  if (options.checkpoint) {
    if (!revC0aHash(options.checkpoint._id) || !revC0aHash(options.checkpoint.header_hash)) {
      throw new Error("Inspect the same checkpoint before continuing.");
    }
    payload.checkpoint_id = options.checkpoint._id;
    payload.checkpoint_hash = options.checkpoint.header_hash;
  } else {
    const preview = options.preview;
    if (phase !== "checkpoint_scan" || !revC0aHash(preview?.manifest_id)
        || !revC0aHash(preview?.manifest_hash) || !revC0aHash(preview?.preview_hash)
        || preview.source_retirement_handoff?.handoff_hash !== options.handoffHash
        || preview.source_retirement_handoff?.terminal_hash !== options.terminalHash) {
      throw new Error("Inspect the matching source and activation preview.");
    }
    payload.manifest_id = preview.manifest_id;
    payload.manifest_hash = preview.manifest_hash;
    payload.activation_preview_hash = preview.preview_hash;
  }
  return payload;
}

export function RevC0aSuccessorRecoveryCard({ settings, busy, setBusy, onRun }) {
  const [fence, setFence] = useState(null);
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checkpointInfo, setCheckpointInfo] = useState(null);
  const [fenceConfirmed, setFenceConfirmed] = useState(false);
  const [captureConfirmed, setCaptureConfirmed] = useState(false);
  const [otherDrained, setOtherDrained] = useState(false);
  const [retirementConfirmed, setRetirementConfirmed] = useState(false);
  const [held, setHeld] = useState(false);
  const [phaseAdmissionRead, setPhaseAdmissionRead] = useState(false);
  const [message, setMessage] = useState(null);
  const mounted = useRef(true);
  const requestBusy = useRef(false);
  const fenceAttempted = useRef(false);
  const captureAttempted = useRef(false);
  const operation = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      setBusy((current) => typeof current === "string" && current.startsWith("rev-c0a:") ? null : current);
    };
  }, [setBusy]);

  const fenced = fence?.workflow_version === REV_C0A_FENCE_VERSION
    && ["fenced_unknown", "already_fenced_unknown"].includes(fence.status)
    && fence.receipt_id === REV_C0A_INTENT + ":provider_retirement_write_fenced_unknown:v1"
    && revC0aHash(fence.receipt_hash) && fence.cleanup_confirmed === false
    && fence.original_phase_outcome === "UNKNOWN" && fence.ordinary_admission_granted === false;
  const typedSource = source?.capability_version === REV_C0A_VERSION
    && source.original_cleanup_confirmed === false && source.original_phase_outcome === "UNKNOWN";
  const closedSource = typedSource && source.successor_native_ack_confirmed === true
    && source.successor_pending_readback_required === false
    && revC0aHash(source.handoff_hash) && revC0aHash(source.successor_terminal_hash)
    && revC0aHash(source.manifest_id) && revC0aHash(source.manifest_hash);
  const phasePermitted = closedSource && phaseAdmissionRead && source.checkpoint_phase_bridge_permitted === true && source.current_owner_active === false;
  const matchedPreview = closedSource && preview?.manifest_id === source.manifest_id
    && preview.manifest_hash === source.manifest_hash && revC0aHash(preview.preview_hash)
    && preview.source_retirement_handoff?.handoff_hash === source.handoff_hash
    && preview.source_retirement_handoff?.terminal_hash === source.successor_terminal_hash
    && preview.source_retirement_handoff?.original_cleanup_confirmed === false;
  const checkpoint = checkpointInfo?.checkpoint;
  const checkpointPending = checkpointInfo != null && (checkpointInfo.pending_readback_required !== false || checkpointInfo.intent?.pending === true);
  const matchingCheckpoint = !checkpointPending && closedSource && revC0aHash(checkpoint?._id)
    && revC0aHash(checkpoint.header_hash) && checkpoint.manifest_hash === source.manifest_hash;

  const action = async (name, run) => {
    if (busy !== null || requestBusy.current) return;
    requestBusy.current = true;
    setBusy("rev-c0a:" + name);
    try { await run(); }
    finally {
      requestBusy.current = false;
      if (mounted.current) setBusy((current) => current === "rev-c0a:" + name ? null : current);
    }
  };
  const inspectFence = () => action("inspect-fence", async () => {
    try {
      const { data } = await api.get(REV_C0A_BASE + "/provider-retirement-write-fence");
      if (!mounted.current) return;
      setFence(data); setSource(null); setPreview(null); setPhaseAdmissionRead(false);
      setMessage("Retirement fence inspected. The original capture outcome remains unknown.");
    } catch (_error) {
      if (mounted.current) { setFence(null); setMessage("The retirement fence is unconfirmed. Inspect it again; no source action is enabled."); }
    }
  });
  const writeFence = () => action("fence", async () => {
    if (fence?.status !== "unfenced_held" || !fenceConfirmed || fenceAttempted.current) return;
    fenceAttempted.current = true; setFence(null); setSource(null); setPreview(null);
    try {
      await api.post(REV_C0A_BASE + "/provider-retirement-write-fence", { confirm: true });
      if (mounted.current) setMessage("Fence request returned. Inspect its fixed receipt before the next step.");
    } catch (_error) {
      if (mounted.current) setMessage("Fence outcome is unconfirmed. Inspect the same receipt; do not submit it again.");
    }
  });
  const inspectSource = () => action("inspect-source", async () => {
    try {
      const { data } = await api.get(REV_C0A_BASE + "/source-only-successor");
      if (!mounted.current) return;
      setSource(data);
      setPhaseAdmissionRead(data.capability_version === REV_C0A_VERSION && data.checkpoint_phase_bridge_permitted === true && data.current_owner_active === false);
      if (data.handoff_hash !== source?.handoff_hash || data.successor_terminal_hash !== source?.successor_terminal_hash) {
        setPreview(null); setCheckpointInfo(null); setOtherDrained(false); setRetirementConfirmed(false);
      }
      setMessage("Successor capture inspected. This does not start another capture or publish records.");
    } catch (_error) {
      if (mounted.current) { setSource(null); setPhaseAdmissionRead(false); setMessage("Successor status is unconfirmed. Inspect the same capture; no new submission is enabled."); }
    }
  });
  const capture = () => action("capture", async () => {
    if (!fenced || !typedSource || source.source_successor_capture_permitted !== true
        || source.successor_pending_readback_required !== false
        || source.expected_fence_hash !== fence.receipt_hash || !captureConfirmed
        || captureAttempted.current || !settings?.scan_enabled || !settings?.dry_run_enabled) return;
    const expectedHash = source.expected_fence_hash;
    captureAttempted.current = true; setSource(null); setPreview(null); setPhaseAdmissionRead(false);
    try {
      await api.post(REV_C0A_BASE + "/source-only-successor", { confirm: true, expected_fence_hash: expectedHash });
      if (mounted.current) setMessage("Capture request returned. Inspect its exact successor receipt before any checkpoint action.");
    } catch (_error) {
      if (mounted.current) setMessage("Capture outcome is unconfirmed. Inspect this successor; do not submit it again.");
    }
  });
  const inspectPreview = () => action("preview", async () => {
    if (!phasePermitted || held || source.checkpoint_preview_permitted !== true) return;
    try {
      const { data } = await api.get(REV_C0A_BASE + "/successor-checkpoint-preview");
      if (!mounted.current) return;
      setOtherDrained(false); setRetirementConfirmed(false);
      if (data.supported !== true || data.workflow_version !== "rev_create_only_checkpoint_v1"
          || data.source_successor_capability_version !== REV_C0A_VERSION) { setPreview(null); return; }
      setPreview(data.activation_preview || null);
      setMessage("Activation preview inspected. Confirm each separate evidence statement before an action.");
    } catch (_error) {
      if (mounted.current) { setPreview(null); setMessage("Activation preview is unconfirmed; no activation is enabled."); }
    }
  });
  const inspectCheckpoint = () => action("inspect-checkpoint", async () => {
    if (!closedSource) return;
    const params = { manifest_id: source.manifest_id, manifest_hash: source.manifest_hash };
    if (checkpoint?._id) params.checkpoint_id = checkpoint._id;
    if (operation.current?.intentId) params.intent_id = operation.current.intentId;
    try {
      const { data } = await api.get("/admin/operator-automation/revcomps/checkpoint", { params });
      if (!mounted.current || data.supported !== true || data.workflow_version !== "rev_create_only_checkpoint_v1") return;
      setCheckpointInfo(data);
      if (data.pending_readback_required !== false || data.intent?.pending === true) setHeld(true);
      else if (!operation.current) setHeld(false);
      if (operation.current && data.intent?.operation_request_id === operation.current.requestId) {
        operation.current = { ...operation.current, intentId: data.intent.intent_id };
        if (data.pending_readback_required === false && data.intent.pending === false) setHeld(false);
      }
      setMessage("Checkpoint inspected. Only its matching submitted operation can resolve an uncertain response.");
    } catch (_error) { if (mounted.current) setMessage("Checkpoint status is unconfirmed. Inspect the same operation before continuing."); }
  });
  const runPhase = (phase) => action("checkpoint", async () => {
    if (!phasePermitted || held || checkpointPending || !otherDrained || !retirementConfirmed || (!matchedPreview && !matchingCheckpoint)) return;
    const requestId = globalThis.crypto.randomUUID();
    const payload = buildRevC0aSuccessorCheckpointPayload("checkpoint_" + phase, {
      requestId, handoffHash: source.handoff_hash, terminalHash: source.successor_terminal_hash,
      otherWritersDrained: otherDrained, retirementConfirmed,
      checkpoint: matchingCheckpoint ? checkpoint : undefined, preview });
    operation.current = { requestId, intentId: null };
    setPhaseAdmissionRead(false); setHeld(true);
    try {
      const { data } = await api.post(REV_C0A_BASE + "/successor-checkpoint", payload);
      if (!mounted.current) return;
      if (data.operation_request_id !== requestId) throw new Error("Unmatched operation response");
      operation.current = { requestId, intentId: data.intent_id || null };
      setCheckpointInfo((current) => ({ ...current, supported: true, workflow_version: "rev_create_only_checkpoint_v1",
        checkpoint: data.checkpoint || current?.checkpoint, last_result: data }));
      // All writes remain readback-only until the same durable operation is inspected.
      setMessage("Checkpoint request returned. Inspect this operation before the next phase.");
      onRun?.(data);
    } catch (_error) {
      if (mounted.current) setMessage("Checkpoint outcome is unconfirmed. Inspect the same operation; do not submit it again.");
    }
  });
  const disabled = busy !== null;
  return (
    <section className="w-full rounded border border-amber-400/25 p-3 text-xs space-y-2" data-testid="rev-c0a-successor-panel">
      <p>Recover the held Rev source capture through a separate retired-owner handoff. The original capture remains unknown; its source data is never replayed.</p>
      <button type="button" onClick={inspectFence} disabled={disabled} data-testid="rev-c0a-fence-inspect">Inspect retirement fence</button>
      <label className="block"><input type="checkbox" checked={fenceConfirmed} onChange={(e) => setFenceConfirmed(e.target.checked)} data-testid="rev-c0a-fence-confirm" /> I have checked the exact retired deployment and original capture identity. This does not confirm its cleanup or result.</label>
      <button type="button" onClick={writeFence} disabled={disabled || fence?.status !== "unfenced_held" || !fenceConfirmed || fenceAttempted.current} data-testid="rev-c0a-fence-submit">Fence the retired capture's journal writes</button>
      <button type="button" onClick={inspectSource} disabled={disabled} data-testid="rev-c0a-source-inspect">Inspect successor capture</button>
      <label className="block"><input type="checkbox" checked={captureConfirmed} onChange={(e) => setCaptureConfirmed(e.target.checked)} data-testid="rev-c0a-source-confirm" /> Start one fresh source-only capture under its new fenced owner. This creates no competitions.</label>
      <button type="button" onClick={capture} disabled={disabled || !fenced || !typedSource || source.source_successor_capture_permitted !== true || source.successor_pending_readback_required !== false || source.expected_fence_hash !== fence?.receipt_hash || !captureConfirmed || captureAttempted.current || !settings?.scan_enabled || !settings?.dry_run_enabled} data-testid="rev-c0a-source-submit">Capture fresh successor source</button>
      {source?.intent_id && <p>Successor capture: {source.intent_id}. State: {source.successor_state || "Inspect receipt"}.</p>}
      <button type="button" onClick={inspectPreview} disabled={disabled || !phasePermitted || held || source.checkpoint_preview_permitted !== true} data-testid="rev-c0a-preview-inspect">Inspect successor activation preview</button>
      <button type="button" onClick={inspectCheckpoint} disabled={disabled || !closedSource} data-testid="rev-c0a-checkpoint-inspect">Inspect this checkpoint operation</button>
      {checkpoint && <p>Checkpoint: {checkpoint._id}. State: {checkpoint.state}.</p>}
      <label className="block"><input type="checkbox" checked={retirementConfirmed} onChange={(e) => setRetirementConfirmed(e.target.checked)} data-testid="rev-c0a-retirement-confirm" /> I confirm this exact c0a retirement handoff, while its original outcome remains unknown.</label>
      <label className="block"><input type="checkbox" checked={otherDrained} onChange={(e) => setOtherDrained(e.target.checked)} data-testid="rev-c0a-other-writers-confirm" /> I have independently reconciled and drained all other old writers. The handoff alone does not prove this.</label>
      {["scan", "preflight", "apply", "verify", "publish"].map((phase) => (
        <button key={phase} type="button" onClick={() => runPhase(phase)} data-testid={"rev-c0a-checkpoint-" + phase}
          disabled={disabled || held || checkpointPending || !phasePermitted || (!matchedPreview && !matchingCheckpoint) || !otherDrained || !retirementConfirmed
            || (!matchingCheckpoint && phase !== "scan")
            || (phase === "scan" && (!settings?.scan_enabled || !settings?.dry_run_enabled))
            || (phase === "apply" && (!settings?.apply_enabled || checkpoint?.state !== "batch_admitted"))
            || (phase === "verify" && (!settings?.verify_enabled || checkpoint?.state !== "drafted"))
            || (phase === "publish" && (!settings?.publish_enabled || checkpoint?.state !== "verified"))}>
          {phase === "scan" ? "Read next owned detail batch" : phase === "preflight" ? "Preflight exact successor batch" : phase === "apply" ? "Create eligible drafts" : phase === "verify" ? "Verify exact successor drafts" : "Publish verified successor additions"}
        </button>))}
      {message && <p role="status">{message}</p>}
      {held && <p>Held for this operation's readback. No phase is submitted automatically.</p>}
    </section>
  );
}

export default function OperatorAutomationRunCard({
  operator,
  settings,
  diag,
  onRun,
  executionLeaseStatus,
}) {
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [revManifestStatus, setRevManifestStatus] = useState(null);
  const [revManifestResult, setRevManifestResult] = useState(null);
  const [revManifestUncertain, setRevManifestUncertain] = useState(false);
  const [revManifestError, setRevManifestError] = useState(null);
  const revManifestSubmission = useRef(0);
  const revManifestActiveIntent = useRef(null);
  const [revCheckpointInfo, setRevCheckpointInfo] = useState(null);
  const [revCheckpointUncertain, setRevCheckpointUncertain] = useState(false);
  const [revCheckpointError, setRevCheckpointError] = useState(null);
  const [revDrainConfirmed, setRevDrainConfirmed] = useState(false);
  const revCheckpointOperation = useRef(null);
  const revCheckpointSubmission = useRef(0);

  useEffect(() => {
    let active = true;
    revManifestSubmission.current += 1;
    revManifestActiveIntent.current = null;
    revCheckpointOperation.current = null;
    revCheckpointSubmission.current += 1;
    setRevCheckpointInfo(null);
    setRevCheckpointUncertain(false);
    setRevCheckpointError(null);
    setRevDrainConfirmed(false);
    setRevManifestStatus(null);
    setRevManifestResult(null);
    setRevManifestUncertain(false);
    setRevManifestError(null);
    if (operator?.key === "revcomps") {
      api.get("/admin/operator-automation/revcomps/source-manifest")
        .then(({ data }) => { if (active) setRevManifestStatus(data); })
        .catch(() => { if (active) setRevManifestStatus(null); });
    }
    return () => { active = false; };
  }, [operator?.key]);

  const inspectRevManifest = async () => {
    const submission = revManifestSubmission.current;
    const intentId = revManifestActiveIntent.current;
    setBusy("rev-source-inspect");
    try {
      const { data } = await api.get("/admin/operator-automation/revcomps/source-manifest",
        intentId ? { params: { intent_id: intentId } } : undefined);
      if (submission !== revManifestSubmission.current || intentId !== revManifestActiveIntent.current) return;
      setRevManifestStatus(data);
      // An unknown POST without a returned intent ID cannot be matched merely
      // to the previous successful capture. Keep capture disabled in this view.
      if (intentId && data.inspected_intent_id === intentId
          && data.pending_readback_required === false
          && data.terminal?.cleanup_confirmed === true) setRevManifestUncertain(false);
      setRevManifestError(null);
    } catch (_error) {
      if (submission !== revManifestSubmission.current || intentId !== revManifestActiveIntent.current) return;
      setRevManifestError("The capture status could not be confirmed. Inspect this capture again before continuing.");
    } finally { if (submission === revManifestSubmission.current) setBusy(null); }
  };

  const captureRevManifest = async () => {
    if (revManifestStatus?.capture_supported !== true
        || revManifestStatus?.workflow_version !== "rev_source_manifest_v1"
        || revManifestStatus?.pending_readback_required || revManifestUncertain) return;
    const submission = ++revManifestSubmission.current;
    // Detach A before submitting B. Displayed historical source evidence stays
    // separate; only B's own returned identity may reconcile B's uncertainty.
    revManifestActiveIntent.current = null;
    setRevManifestResult(null);
    setBusy("rev-source-capture");
    setRevManifestError(null);
    try {
      const { data } = await api.post("/admin/operator-automation/run-once", buildRevSourceManifestPayload());
      if (submission !== revManifestSubmission.current) return;
      revManifestActiveIntent.current = typeof data.intent_id === "string" && /^[0-9a-f]{64}$/.test(data.intent_id)
        ? data.intent_id : null;
      setRevManifestResult(data);
      setRevManifestUncertain(data.pending_readback_required === true);
      setRevManifestStatus((current) => ({ ...current,
        pending_readback_required: data.pending_readback_required === true,
        source_manifest: data.source_manifest }));
      if (data.status !== "ok") setRevManifestError("Capture did not complete. Inspect its status before continuing.");
      onRun?.(data);
    } catch (_error) {
      if (submission !== revManifestSubmission.current) return;
      setRevManifestUncertain(true);
      setRevManifestError("The capture outcome is unknown. Inspect its status; do not start another capture.");
    } finally { if (submission === revManifestSubmission.current) setBusy(null); }
  };

  const inspectRevCheckpoint = async () => {
    const submission = revCheckpointSubmission.current;
    const operation = revCheckpointOperation.current;
    const source = revManifestStatus?.source_manifest;
    const params = {};
    if (revCheckpointInfo?.checkpoint?._id) params.checkpoint_id = revCheckpointInfo.checkpoint._id;
    if (operation?.intentId) params.intent_id = operation.intentId;
    if (source?.intent_id && source?.manifest_hash) {
      params.manifest_id = source.intent_id;
      params.manifest_hash = source.manifest_hash;
    }
    setBusy("rev-checkpoint-inspect");
    try {
      const { data } = await api.get("/admin/operator-automation/revcomps/checkpoint", { params });
      if (submission !== revCheckpointSubmission.current || operation !== revCheckpointOperation.current) return;
      if (data.workflow_version !== "rev_create_only_checkpoint_v1" || data.supported !== true) return;
      // A lost response may adopt ONLY the durable marker for this submitted nonce.
      if (operation && data.intent?.operation_request_id === operation.requestId) {
        revCheckpointOperation.current = { ...operation, intentId: data.intent.intent_id };
        if (data.pending_readback_required === false && data.intent.pending === false) setRevCheckpointUncertain(false);
      }
      setRevCheckpointInfo(data);
      setRevDrainConfirmed(false);
      setRevCheckpointError(null);
    } catch (_error) {
      setRevCheckpointError("Checkpoint status is unconfirmed. Inspect the same operation before continuing.");
    } finally { if (submission === revCheckpointSubmission.current) setBusy(null); }
  };

  const runRevCheckpoint = async (phase, reconcile = false) => {
    if (revManifestStatus?.checkpoint_capability_version !== "rev_create_only_checkpoint_v1"
        || revCheckpointInfo?.supported !== true || (revCheckpointUncertain && !reconcile)) return;
    const previous = revCheckpointOperation.current;
    const submission = ++revCheckpointSubmission.current;
    const requestId = reconcile ? previous?.requestId : globalThis.crypto.randomUUID();
    const checkpoint = revCheckpointInfo.checkpoint;
    const newEpoch = phase === "checkpoint_scan" && revCheckpointInfo.activation_preview
      && revCheckpointInfo.activation_preview.manifest_hash !== checkpoint?.manifest_hash;
    const options = { requestId, checkpointId: newEpoch ? undefined : checkpoint?._id,
      checkpointHash: newEpoch ? undefined : checkpoint?.header_hash,
      preview: revCheckpointInfo.activation_preview, drainConfirmed: revDrainConfirmed,
      reconcileIntentId: reconcile ? previous?.intentId : undefined };
    if (reconcile && (!previous?.intentId || revCheckpointInfo.intent?.operation_request_id !== requestId)) return;
    revCheckpointOperation.current = { requestId, intentId: reconcile ? previous.intentId : null };
    setBusy("rev-checkpoint-run");
    setRevCheckpointError(null);
    try {
      const payload = buildRevCheckpointPayload(phase, options);
      const { data } = await api.post("/admin/operator-automation/run-once", payload);
      if (submission !== revCheckpointSubmission.current) return;
      if (data.operation_request_id !== requestId) throw new Error("The response does not match this operation.");
      revCheckpointOperation.current = { requestId, intentId: data.intent_id || null };
      setRevCheckpointUncertain(data.pending_readback_required === true);
      setRevCheckpointInfo((current) => ({ ...current, last_result: data,
        checkpoint: data.checkpoint || current.checkpoint,
        active: data.checkpoint ? true : current.active,
        pending_readback_required: data.pending_readback_required === true }));
      if (data.status !== "ok") setRevCheckpointError(data.error || "The checkpoint remains blocked.");
      onRun?.(data);
    } catch (_error) {
      if (submission !== revCheckpointSubmission.current) return;
      setRevCheckpointUncertain(true);
      setRevCheckpointError("The checkpoint outcome is unknown. Inspect this operation; do not submit it again.");
    } finally { if (submission === revCheckpointSubmission.current) setBusy(null); }
  };

  const [productionConfirmation, setProductionConfirmation] = useState(null);
  const [pristineCountAcceptanceSelection, setPristineCountAcceptanceSelection] = useState("");
  const productionConfirmationResolver = useRef(null);
  const [revCleanupDryRunResult, setRevCleanupDryRunResult] = useState(null);
  const [revCleanupLiveResult, setRevCleanupLiveResult] = useState(null);
  const [revExpiryDryRunResult, setRevExpiryDryRunResult] = useState(null);
  const [dcgExpiryDryRunResult, setDcgExpiryDryRunResult] = useState(null);
  const [clickExpiryDryRunResult, setClickExpiryDryRunResult] = useState(null);
  const [eliteExpiryDryRunResult, setEliteExpiryDryRunResult] = useState(null);
  const [revExpiryApprovalStatus, setRevExpiryApprovalStatus] = useState(null);
  const [revExpiryApprovalError, setRevExpiryApprovalError] = useState(null);
  const [botbExpiryDryRunResult, setBotbExpiryDryRunResult] = useState(null);
  const [aspireExpiryDryRunResult, setAspireExpiryDryRunResult] = useState(null);
  const [mckinneyExpiryDryRunResult, setMckinneyExpiryDryRunResult] = useState(null);
  const [
    mckinneyExpiryApprovalIdBeforeDryRun,
    setMckinneyExpiryApprovalIdBeforeDryRun,
  ] = useState(null);
  const [llfExpiryDryRunResult, setLlfExpiryDryRunResult] = useState(null);
  const [kiltedExpiryDryRunResult, setKiltedExpiryDryRunResult] = useState(null);
  const [botbIdentityAuditResult, setBotbIdentityAuditResult] = useState(null);
  const [revBatch, setRevBatch] = useState({
    detailFetchLimit: DEFAULT_DETAIL_FETCH_LIMIT,
    detailFetchOffset: 0,
  });
  const [aspireScanMaxDetails, setAspireScanMaxDetails] = useState(
    ASPIRE_DEFAULT_SCAN_MAX_DETAILS,
  );
  const [aspireMaxNewRecords, setAspireMaxNewRecords] = useState(
    ASPIRE_DEFAULT_MAX_NEW_RECORDS,
  );
  const [aspireScanSnapshot, setAspireScanSnapshot] = useState(null);
  const [aspireInitialReplacementAuthorisation, setAspireInitialReplacementAuthorisation] = useState(null);
  const [dcgScanSnapshot, setDcgScanSnapshot] = useState(null);
  const [dcgApplyPreflightApproval, setDcgApplyPreflightApproval] = useState(null);
  const [eliteScanSnapshot, setEliteScanSnapshot] = useState(null);
  const [botbScanSnapshot, setBotbScanSnapshot] = useState(null);
  const [clickScanMaxDetails, setClickScanMaxDetails] = useState(
    String(DEFAULT_MAX_DETAILS),
  );
  const [clickScanSnapshot, setClickScanSnapshot] = useState(null);
  const [clickApplyPreflightApproval, setClickApplyPreflightApproval] = useState(null);
  const [sevenDaysScanSnapshot, setSevenDaysScanSnapshot] = useState(null);
  const [mckinneyScanMaxDetails, setMckinneyScanMaxDetails] = useState(
    MCKINNEY_DEFAULT_MAX_DETAILS,
  );
  const [mckinneyMaxNewRecords, setMckinneyMaxNewRecords] = useState(
    MCKINNEY_DEFAULT_MAX_NEW_RECORDS,
  );
  const [mckinneyScanSnapshot, setMckinneyScanSnapshot] = useState(null);
  const [mckinneyApplyPreflightApproval, setMckinneyApplyPreflightApproval] = useState(null);
  const [llfScanMaxDetails, setLlfScanMaxDetails] = useState(
    LLF_DEFAULT_MAX_DETAILS,
  );
  const [llfMaxNewRecords, setLlfMaxNewRecords] = useState(
    LLF_DEFAULT_MAX_NEW_RECORDS,
  );
  const [llfScanSnapshot, setLlfScanSnapshot] = useState(null);
  const [llfApplyPreflightApproval, setLlfApplyPreflightApproval] = useState(null);
  const [kiltedScanMaxDetails, setKiltedScanMaxDetails] = useState(
    KILTED_DEFAULT_MAX_DETAILS,
  );
  const [kiltedMaxNewRecords, setKiltedMaxNewRecords] = useState(
    KILTED_DEFAULT_MAX_NEW_RECORDS,
  );
  const [kiltedScanSnapshot, setKiltedScanSnapshot] = useState(null);
  const [kiltedApplyPreflightApproval, setKiltedApplyPreflightApproval] = useState(null);
  const [revScanSnapshot, setRevScanSnapshot] = useState(null);
  const [aspireManualImport, setAspireManualImport] = useState({
    inputType: "routes",
    text: "",
    dryRun: true,
    maxDetails: ASPIRE_MANUAL_IMPORT_MAX_DETAILS,
  });
  const settleProductionConfirmation = useCallback((accepted) => {
    const resolve = productionConfirmationResolver.current;
    productionConfirmationResolver.current = null;
    setProductionConfirmation(null);
    resolve?.(accepted);
  }, []);
  const confirmProductionWriteInApp = (message) => {
    if (!diag?.is_production) {
      return Promise.resolve(confirmProductionWrite(diag, message));
    }
    if (productionConfirmationResolver.current) {
      productionConfirmationResolver.current(false);
    }
    return new Promise((resolve) => {
      productionConfirmationResolver.current = resolve;
      setProductionConfirmation({
        message,
        databaseName: diag.db_name || "production",
      });
    });
  };

  useEffect(() => () => {
    if (productionConfirmationResolver.current) {
      productionConfirmationResolver.current(false);
      productionConfirmationResolver.current = null;
    }
  }, []);
  const revSettingsSnapshotId = settings?.latest_rev_scan_snapshot_id;
  const revSettingsSnapshotHash = settings?.latest_rev_scan_snapshot_hash;
  const revSettingsSnapshotState = settings?.latest_rev_scan_snapshot_state;
  const revSettingsSnapshotCreatedAt = (
    settings?.latest_rev_scan_snapshot_created_at
  );
  const revSettingsSnapshotExpiresAt = (
    settings?.latest_rev_scan_snapshot_expires_at
  );
  const revSettingsSnapshotCandidateCount = (
    settings?.latest_rev_scan_snapshot_candidate_count
  );
  const revSettingsSnapshotExactIdentityCount = (
    settings?.latest_rev_scan_snapshot_exact_identity_count
  );
  const revSettingsSnapshotExactIdentityDigest = (
    settings?.latest_rev_scan_snapshot_exact_identity_digest
  );
  const revSettingsSnapshotTrustedImageCount = (
    settings?.latest_rev_scan_snapshot_trusted_image_count
  );
  const revSettingsSnapshotTrustedMaxTicketsCount = (
    settings?.latest_rev_scan_snapshot_trusted_max_tickets_count
  );
  const revSettingsSnapshotLimitPerOperator = (
    settings?.latest_rev_scan_snapshot_scan_config?.limit_per_operator
  );
  const revSettingsSnapshotRequestedMaxDetails = (
    settings?.latest_rev_scan_snapshot_scan_config?.requested_max_details
  );
  const revSettingsSnapshotValidatedMaxDetails = (
    settings?.latest_rev_scan_snapshot_scan_config?.validated_max_details
  );
  const revSettingsSnapshotDetailFetchOffset = (
    settings?.latest_rev_scan_snapshot_scan_config?.detail_fetch_offset
  );
  const revSettingsSnapshotNetworkDiscovery = (
    settings
      ?.latest_rev_scan_snapshot_scan_config
      ?.automated_network_discovery
  );
  const revSettingsSnapshotSourcePhase = (
    settings?.latest_rev_scan_snapshot_scan_config?.source_phase
  );
  const revSettingsSnapshotSourceDryRun = (
    settings?.latest_rev_scan_snapshot_scan_config?.source_dry_run
  );
  const botbSettingsSnapshotId = settings?.latest_botb_scan_snapshot_id;
  const botbSettingsSnapshotHash = settings?.latest_botb_scan_snapshot_hash;
  const botbSettingsSnapshotState = settings?.latest_botb_scan_snapshot_state;
  const botbSettingsSnapshotVersion = settings?.latest_botb_scan_snapshot_version;
  const botbSettingsSnapshotExpiresAt = settings?.latest_botb_scan_snapshot_expires_at;
  const clickSettingsSnapshotId = settings?.latest_click_scan_snapshot_id;
  const clickSettingsSnapshotHash = settings?.latest_click_scan_snapshot_hash;
  const clickSettingsSnapshotState = settings?.latest_click_scan_snapshot_state;
  const clickSettingsSnapshotVersion = settings?.latest_click_scan_snapshot_version;
  const clickSettingsSnapshotExpiresAt = settings?.latest_click_scan_snapshot_expires_at;
  const clickSettingsPreflightState = settings?.latest_click_apply_preflight_state;
  const clickSettingsPreflightSnapshotId = settings?.latest_click_apply_preflight_snapshot_id;
  const clickSettingsPreflightSnapshotHash = settings?.latest_click_apply_preflight_snapshot_hash;
  const sevenDaysSettingsSnapshotId = settings?.latest_sevendays_scan_snapshot_id;
  const sevenDaysSettingsSnapshotHash = settings?.latest_sevendays_scan_snapshot_hash;
  const sevenDaysSettingsSnapshotState = settings?.latest_sevendays_scan_snapshot_state;
  const sevenDaysSettingsSnapshotVersion = settings?.latest_sevendays_scan_snapshot_version;
  const sevenDaysSettingsSnapshotExpiresAt = settings?.latest_sevendays_scan_snapshot_expires_at;
  const mckinneySettingsSnapshotId = (
    settings?.latest_mckinney_scan_snapshot_id
  );
  const mckinneySettingsSnapshotHash = (
    settings?.latest_mckinney_scan_snapshot_hash
  );
  const mckinneySettingsSnapshotState = (
    settings?.latest_mckinney_scan_snapshot_state
  );
  const mckinneySettingsSnapshotVersion = (
    settings?.latest_mckinney_scan_snapshot_version
  );
  const mckinneySettingsSnapshotExpiresAt = (
    settings?.latest_mckinney_scan_snapshot_expires_at
  );
  const llfSettingsSnapshotId = settings?.latest_llf_scan_snapshot_id;
  const llfSettingsSnapshotHash = settings?.latest_llf_scan_snapshot_hash;
  const llfSettingsSnapshotState = settings?.latest_llf_scan_snapshot_state;
  const llfSettingsSnapshotVersion = settings?.latest_llf_scan_snapshot_version;
  const llfSettingsSnapshotExpiresAt = settings?.latest_llf_scan_snapshot_expires_at;
  const exactSetSettingsPrefix = exactSetPrefixForOperator(operator?.key);
  const kiltedSettingsSnapshotId = settings?.[`latest_${exactSetSettingsPrefix}_scan_snapshot_id`];
  const kiltedSettingsSnapshotHash = settings?.[`latest_${exactSetSettingsPrefix}_scan_snapshot_hash`];
  const kiltedSettingsSnapshotState = settings?.[`latest_${exactSetSettingsPrefix}_scan_snapshot_state`];
  const kiltedSettingsSnapshotVersion = settings?.[`latest_${exactSetSettingsPrefix}_scan_snapshot_version`];
  const kiltedSettingsSnapshotExpiresAt = settings?.[`latest_${exactSetSettingsPrefix}_scan_snapshot_expires_at`];
  const revExpiryApprovalContextKey = [
    operator?.key || "",
    settings?.expire_enabled === true ? "expiry-on" : "expiry-off",
    settings?.scheduler_enabled === true ? "scheduler-on" : "scheduler-off",
    settings?.latest_successful_rev_live_set_id || "",
  ].join("|");
  const revExpiryApprovalContextRef = useRef(revExpiryApprovalContextKey);
  revExpiryApprovalContextRef.current = revExpiryApprovalContextKey;
  const refreshRevExpiryApprovalStatus = useCallback(async (
    expectedContextKey = revExpiryApprovalContextRef.current,
  ) => {
    if (operator?.key !== "revcomps") {
      setRevExpiryApprovalStatus(null);
      setRevExpiryApprovalError(null);
      return null;
    }
    try {
      const { data } = await api.get(
        "/admin/operator-automation/revcomps/expiry-approval",
      );
      if (revExpiryApprovalContextRef.current !== expectedContextKey) {
        return null;
      }
      setRevExpiryApprovalStatus(data);
      setRevExpiryApprovalError(null);
      return data;
    } catch (error) {
      if (revExpiryApprovalContextRef.current !== expectedContextKey) {
        return null;
      }
      setRevExpiryApprovalStatus(null);
      setRevExpiryApprovalError(
        error.response?.data?.detail?.error
          || error.response?.data?.detail
          || error.message
          || "approval_status_unavailable",
      );
      return null;
    }
  }, [operator?.key]);
  useEffect(() => {
    refreshRevExpiryApprovalStatus(revExpiryApprovalContextKey);
  }, [refreshRevExpiryApprovalStatus, revExpiryApprovalContextKey]);
  useEffect(() => {
    setRevCleanupDryRunResult(null);
    setRevCleanupLiveResult(null);
    setRevExpiryDryRunResult(null);
    setDcgExpiryDryRunResult(null);
    setClickExpiryDryRunResult(null);
    setBotbExpiryDryRunResult(null);
    setAspireExpiryDryRunResult(null);
    setMckinneyExpiryDryRunResult(null);
    setMckinneyExpiryApprovalIdBeforeDryRun(null);
    setLlfExpiryDryRunResult(null);
    setKiltedExpiryDryRunResult(null);
    setBotbIdentityAuditResult(null);
  }, [
    operator?.key,
    settings?.latest_successful_rev_live_set_id,
    settings?.latest_successful_botb_snapshot_id,
    settings?.latest_successful_click_live_set_id,
    settings?.latest_successful_aspire_live_set_id,
    settings?.latest_successful_mckinney_live_set_id,
    settings?.latest_successful_kilted_live_set_id,
  ]);
  useEffect(() => {
    setAspireScanSnapshot(null);
    setAspireInitialReplacementAuthorisation(null);
    setDcgScanSnapshot(null);
    setDcgApplyPreflightApproval(null);
    setEliteScanSnapshot(null);
    setBotbScanSnapshot(null);
    setClickScanSnapshot(null);
    setClickApplyPreflightApproval(null);
    setClickScanMaxDetails(String(DEFAULT_MAX_DETAILS));
    setSevenDaysScanSnapshot(null);
    setMckinneyScanSnapshot(null);
    setMckinneyApplyPreflightApproval(null);
    setLlfScanSnapshot(null);
    setLlfApplyPreflightApproval(null);
    setKiltedScanSnapshot(null);
    setKiltedApplyPreflightApproval(null);
    setRevScanSnapshot(null);
  }, [operator?.key]);
  useEffect(() => {
    if (operator?.key !== "botb") return;
    const reference = normaliseBotbScanSnapshotReference(
      botbSettingsSnapshotId,
      botbSettingsSnapshotHash,
    );
    if (reference && botbSettingsSnapshotState === "ready") {
      setBotbScanSnapshot({
        ...reference,
        state: "ready",
        version: botbSettingsSnapshotVersion,
        expiresAt: botbSettingsSnapshotExpiresAt,
      });
      return;
    }
    if (["claimed", "consumed", "failed", "expired", "superseded", "mismatched"].includes(
      String(botbSettingsSnapshotState || ""),
    )) {
      setBotbScanSnapshot(null);
      return;
    }
    setBotbScanSnapshot((current) => (
      current && reference && (
        current.id !== reference.id || current.hash !== reference.hash
      )
        ? null
        : current
    ));
  }, [
    operator?.key,
    botbSettingsSnapshotExpiresAt,
    botbSettingsSnapshotHash,
    botbSettingsSnapshotId,
    botbSettingsSnapshotState,
    botbSettingsSnapshotVersion,
  ]);
  useEffect(() => {
    if (operator?.key !== "click") return;
    const reference = normaliseClickScanSnapshotReference(
      clickSettingsSnapshotId,
      clickSettingsSnapshotHash,
    );
    const expiry = Date.parse(clickSettingsSnapshotExpiresAt || "");
    const unexpired = !clickSettingsSnapshotExpiresAt
      || (Number.isFinite(expiry) && expiry > Date.now());
    if (reference && clickSettingsSnapshotState === "ready" && unexpired) {
      setClickScanSnapshot({
        ...reference,
        state: "ready",
        version: clickSettingsSnapshotVersion,
        expiresAt: clickSettingsSnapshotExpiresAt,
      });
      return;
    }
    if (
      !unexpired
      || ["claimed", "consumed", "failed", "expired", "superseded", "mismatched"]
        .includes(String(clickSettingsSnapshotState || ""))
    ) {
      setClickScanSnapshot(null);
      return;
    }
    setClickScanSnapshot((current) => (
      current && reference && (
        current.id !== reference.id || current.hash !== reference.hash
      )
        ? null
        : current
    ));
  }, [
    operator?.key,
    clickSettingsSnapshotExpiresAt,
    clickSettingsSnapshotHash,
    clickSettingsSnapshotId,
    clickSettingsSnapshotState,
    clickSettingsSnapshotVersion,
  ]);
  useEffect(() => {
    if (operator?.key !== "click") return;
    const authoritative = normaliseClickScanSnapshotReference(
      clickSettingsSnapshotId,
      clickSettingsSnapshotHash,
    );
    const authoritativeState = String(clickSettingsSnapshotState || "");
    const authoritativePreflight = normaliseClickScanSnapshotReference(
      clickSettingsPreflightSnapshotId,
      clickSettingsPreflightSnapshotHash,
    );
    const authoritativePreflightState = String(clickSettingsPreflightState || "");
    if (
      authoritativeState === "ready"
      && authoritativePreflightState === "ready"
      && authoritative
      && authoritativePreflight
      && authoritative.id === authoritativePreflight.id
      && authoritative.hash === authoritativePreflight.hash
    ) {
      setClickApplyPreflightApproval((current) => (
        current?.id === authoritativePreflight.id
        && current?.hash === authoritativePreflight.hash
          ? current
          : authoritativePreflight
      ));
      return;
    }
    if ([
      "claimed",
      "consumed",
      "failed",
      "expired",
      "superseded",
      "mismatched",
    ].includes(authoritativeState) || [
      "claimed",
      "consumed",
      "failed",
      "expired",
      "superseded",
      "mismatched",
    ].includes(authoritativePreflightState)) {
      setClickApplyPreflightApproval(null);
      return;
    }
    if (clickApplyPreflightApproval && authoritative && (
      authoritative.id !== clickApplyPreflightApproval.id
      || authoritative.hash !== clickApplyPreflightApproval.hash
    )) {
      setClickApplyPreflightApproval(null);
      return;
    }
    if (clickApplyPreflightApproval && authoritativePreflight && (
      authoritativePreflight.id !== clickApplyPreflightApproval.id
      || authoritativePreflight.hash !== clickApplyPreflightApproval.hash
    )) {
      setClickApplyPreflightApproval(null);
    }
  }, [
    clickApplyPreflightApproval,
    clickSettingsPreflightSnapshotHash,
    clickSettingsPreflightSnapshotId,
    clickSettingsPreflightState,
    clickSettingsSnapshotHash,
    clickSettingsSnapshotId,
    clickSettingsSnapshotState,
    operator?.key,
  ]);
  useEffect(() => {
    if (operator?.key !== "7days") return;
    const reference = normaliseSevenDaysScanSnapshotReference(
      sevenDaysSettingsSnapshotId,
      sevenDaysSettingsSnapshotHash,
    );
    const expiry = Date.parse(sevenDaysSettingsSnapshotExpiresAt || "");
    const unexpired = !sevenDaysSettingsSnapshotExpiresAt
      || (Number.isFinite(expiry) && expiry > Date.now());
    if (reference && sevenDaysSettingsSnapshotState === "ready" && unexpired) {
      setSevenDaysScanSnapshot({
        ...reference,
        state: "ready",
        version: sevenDaysSettingsSnapshotVersion,
        expiresAt: sevenDaysSettingsSnapshotExpiresAt,
      });
      return;
    }
    if (
      !unexpired
      || ["claimed", "consumed", "failed", "expired", "superseded", "mismatched"]
        .includes(String(sevenDaysSettingsSnapshotState || ""))
    ) {
      setSevenDaysScanSnapshot(null);
      return;
    }
    setSevenDaysScanSnapshot((current) => (
      current && reference && (
        current.id !== reference.id || current.hash !== reference.hash
      )
        ? null
        : current
    ));
  }, [
    operator?.key,
    sevenDaysSettingsSnapshotExpiresAt,
    sevenDaysSettingsSnapshotHash,
    sevenDaysSettingsSnapshotId,
    sevenDaysSettingsSnapshotState,
    sevenDaysSettingsSnapshotVersion,
  ]);
  useEffect(() => {
    if (operator?.key !== "mckinney") return;
    const reference = normaliseMckinneyScanSnapshotReference(
      mckinneySettingsSnapshotId,
      mckinneySettingsSnapshotHash,
    );
    if (reference && mckinneySettingsSnapshotState === "ready") {
      setMckinneyScanSnapshot({
        ...reference,
        state: "ready",
        version: mckinneySettingsSnapshotVersion,
        expiresAt: mckinneySettingsSnapshotExpiresAt,
      });
      return;
    }
    if (["claimed", "consumed", "failed", "expired", "superseded"].includes(
      String(mckinneySettingsSnapshotState || ""),
    )) {
      setMckinneyScanSnapshot(null);
      setMckinneyApplyPreflightApproval(null);
      return;
    }
    setMckinneyScanSnapshot((current) => (
      current && reference && (
        current.id !== reference.id || current.hash !== reference.hash
      )
        ? null
        : current
    ));
  }, [
    operator?.key,
    mckinneySettingsSnapshotExpiresAt,
    mckinneySettingsSnapshotHash,
    mckinneySettingsSnapshotId,
    mckinneySettingsSnapshotState,
    mckinneySettingsSnapshotVersion,
  ]);
  useEffect(() => {
    if (operator?.key !== "llf") return;
    const reference = normaliseLlfScanSnapshotReference(
      llfSettingsSnapshotId,
      llfSettingsSnapshotHash,
    );
    if (reference && llfSettingsSnapshotState === "ready") {
      setLlfScanSnapshot({
        ...reference,
        state: "ready",
        version: llfSettingsSnapshotVersion,
        expiresAt: llfSettingsSnapshotExpiresAt,
      });
      return;
    }
    if (["claimed", "consumed", "failed", "expired", "superseded"].includes(
      String(llfSettingsSnapshotState || ""),
    )) {
      setLlfScanSnapshot(null);
      setLlfApplyPreflightApproval(null);
      return;
    }
    setLlfScanSnapshot((current) => (
      current && reference && (
        current.id !== reference.id || current.hash !== reference.hash
      )
        ? null
        : current
    ));
  }, [
    operator?.key,
    llfSettingsSnapshotExpiresAt,
    llfSettingsSnapshotHash,
    llfSettingsSnapshotId,
    llfSettingsSnapshotState,
    llfSettingsSnapshotVersion,
  ]);
  useEffect(() => {
    if (!["kilted", "ukcc", "bounty", "storm", "redline", "prizeparadise", "apex", "jammy", "pawsome", "lincolnshire", "drift", "palladium", "pristine", "caddy", "hotcomps", "thatprizeguy", "giveawayguys", "bright"].includes(operator?.key)) return;
    const reference = normaliseKiltedScanSnapshotReference(
      kiltedSettingsSnapshotId,
      kiltedSettingsSnapshotHash,
    );
    if (reference && kiltedSettingsSnapshotState === "ready") {
      setKiltedScanSnapshot({
        ...reference,
        state: "ready",
        version: kiltedSettingsSnapshotVersion,
        expiresAt: kiltedSettingsSnapshotExpiresAt,
      });
      return;
    }
    if (["claimed", "consumed", "failed", "expired", "superseded"].includes(
      String(kiltedSettingsSnapshotState || ""),
    )) {
      setKiltedScanSnapshot(null);
      setKiltedApplyPreflightApproval(null);
      return;
    }
    setKiltedScanSnapshot((current) => (
      current && reference && (
        current.id !== reference.id || current.hash !== reference.hash
      )
        ? null
        : current
    ));
  }, [
    operator?.key,
    kiltedSettingsSnapshotExpiresAt,
    kiltedSettingsSnapshotHash,
    kiltedSettingsSnapshotId,
    kiltedSettingsSnapshotState,
    kiltedSettingsSnapshotVersion,
  ]);
  useEffect(() => {
    if (operator?.key !== "revcomps") return;
    const settingsSnapshot = revSnapshotFromContainer(
      {
        latest_rev_scan_snapshot_id: revSettingsSnapshotId,
        latest_rev_scan_snapshot_hash: revSettingsSnapshotHash,
        latest_rev_scan_snapshot_state: revSettingsSnapshotState,
        latest_rev_scan_snapshot_created_at: revSettingsSnapshotCreatedAt,
        latest_rev_scan_snapshot_expires_at: revSettingsSnapshotExpiresAt,
        latest_rev_scan_snapshot_candidate_count: (
          revSettingsSnapshotCandidateCount
        ),
        latest_rev_scan_snapshot_exact_identity_count: (
          revSettingsSnapshotExactIdentityCount
        ),
        latest_rev_scan_snapshot_exact_identity_digest: (
          revSettingsSnapshotExactIdentityDigest
        ),
        latest_rev_scan_snapshot_trusted_image_count: (
          revSettingsSnapshotTrustedImageCount
        ),
        latest_rev_scan_snapshot_trusted_max_tickets_count: (
          revSettingsSnapshotTrustedMaxTicketsCount
        ),
        latest_rev_scan_snapshot_scan_config: {
          limit_per_operator: revSettingsSnapshotLimitPerOperator,
          requested_max_details: revSettingsSnapshotRequestedMaxDetails,
          validated_max_details: revSettingsSnapshotValidatedMaxDetails,
          detail_fetch_offset: revSettingsSnapshotDetailFetchOffset,
          automated_network_discovery: revSettingsSnapshotNetworkDiscovery,
          source_phase: revSettingsSnapshotSourcePhase,
          source_dry_run: revSettingsSnapshotSourceDryRun,
        },
      },
      { settings: true },
    );
    if (settingsSnapshot?.state === "ready") {
      setRevScanSnapshot(settingsSnapshot);
      return;
    }
    const terminalStates = [
      "claimed",
      "consumed",
      "failed",
      "expired",
      "superseded",
      "mismatched",
    ];
    if (terminalStates.includes(String(revSettingsSnapshotState || ""))) {
      setRevScanSnapshot(null);
      return;
    }
    const authoritativeId = String(revSettingsSnapshotId || "").trim();
    const authoritativeHash = String(revSettingsSnapshotHash || "").trim();
    if ((authoritativeId || authoritativeHash) && !settingsSnapshot) {
      setRevScanSnapshot(null);
      return;
    }
    setRevScanSnapshot((current) => (
      current
      && (
        (authoritativeId && current.id !== authoritativeId)
        || (authoritativeHash && current.hash !== authoritativeHash)
      )
        ? null
        : current
    ));
  }, [
    operator?.key,
    revSettingsSnapshotCandidateCount,
    revSettingsSnapshotCreatedAt,
    revSettingsSnapshotDetailFetchOffset,
    revSettingsSnapshotExactIdentityCount,
    revSettingsSnapshotExactIdentityDigest,
    revSettingsSnapshotExpiresAt,
    revSettingsSnapshotHash,
    revSettingsSnapshotId,
    revSettingsSnapshotLimitPerOperator,
    revSettingsSnapshotNetworkDiscovery,
    revSettingsSnapshotRequestedMaxDetails,
    revSettingsSnapshotSourceDryRun,
    revSettingsSnapshotSourcePhase,
    revSettingsSnapshotState,
    revSettingsSnapshotTrustedImageCount,
    revSettingsSnapshotTrustedMaxTicketsCount,
    revSettingsSnapshotValidatedMaxDetails,
  ]);
  useEffect(() => {
    if (operator?.key !== "aspire") return;
    const configured = Number(settings?.max_new_records);
    setAspireMaxNewRecords(
      Number.isInteger(configured)
        && configured >= 0
        && configured <= ASPIRE_MAX_NEW_RECORDS_PER_RUN
        ? configured
        : ASPIRE_DEFAULT_MAX_NEW_RECORDS,
    );
  }, [operator?.key, settings?.max_new_records]);
  useEffect(() => {
    if (operator?.key !== "mckinney") return;
    const configured = Number(settings?.max_new_records);
    setMckinneyMaxNewRecords(
      Number.isInteger(configured)
        && configured >= 0
        && configured <= MCKINNEY_MAX_NEW_RECORDS_PER_RUN
        ? configured
        : MCKINNEY_DEFAULT_MAX_NEW_RECORDS,
    );
  }, [operator?.key, settings?.max_new_records]);
  useEffect(() => {
    if (operator?.key !== "llf") return;
    const configured = Number(settings?.max_new_records);
    setLlfMaxNewRecords(
      Number.isInteger(configured)
        && configured >= 0
        && configured <= LLF_MAX_NEW_RECORDS_PER_RUN
        ? configured
        : LLF_DEFAULT_MAX_NEW_RECORDS,
    );
  }, [operator?.key, settings?.max_new_records]);
  useEffect(() => {
    if (operator?.key !== "kilted") return;
    const configured = Number(settings?.max_new_records);
    setKiltedMaxNewRecords(
      Number.isInteger(configured)
        && configured >= 0
        && configured <= KILTED_MAX_NEW_RECORDS_PER_RUN
        ? configured
        : KILTED_DEFAULT_MAX_NEW_RECORDS,
    );
  }, [operator?.key, settings?.max_new_records]);
  if (!operator || !["dcg", "elite", "revcomps", "botb", "7days", "click", "jonnyfromdonny", "feelgood", "aspire", "mckinney", "llf", "kilted", "ukcc", "bounty", "storm", "redline", "prizeparadise", "apex", "jammy", "pawsome", "lincolnshire", "drift", "palladium", "pristine", "caddy", "hotcomps", "thatprizeguy", "giveawayguys", "bright", "salexmo"].includes(operator.key)) return null;

  const operatorKey = operator.key;
  const operatorName = operator.name || (
    operatorKey === "dcg"
      ? "Dream Car Giveaways"
      : operatorKey === "revcomps" ? "Rev Comps"
      : operatorKey === "botb" ? "BOTB"
      : operatorKey === "7days" ? "7days Performance"
      : operatorKey === "click" ? "Click Competitions"
      : operatorKey === "jonnyfromdonny" ? "Jonny From Donny"
      : operatorKey === "feelgood" ? "Feel Good Competitions"
      : operatorKey === "aspire" ? "Aspire Competitions"
      : operatorKey === "mckinney" ? "McKinney Competitions"
      : operatorKey === "llf" ? "LLF Games"
      : operatorKey === "kilted" ? "Kilted Competitions"
      : operatorKey === "ukcc" ? "UKCC Competitions"
      : operatorKey === "bounty" ? "Bounty Competitions"
      : operatorKey === "storm" ? "Storm Competitions"
      : operatorKey === "redline" ? "Redline Competitions"
      : operatorKey === "prizeparadise" ? "Prize Paradise"
      : operatorKey === "apex" ? "Apex Competitions"
      : operatorKey === "jammy" ? "JAMMY"
      : operatorKey === "pawsome" ? "Pawsome Competitions"
      : operatorKey === "lincolnshire" ? "Lincolnshire Competitions"
      : operatorKey === "drift" ? "Drift Competitions"
      : operatorKey === "palladium" ? "Palladium Competitions"
      : operatorKey === "pristine" ? "Pristine Competitions"
      : operatorKey === "caddy" ? "Caddy Comps"
      : operatorKey === "hotcomps" ? "Hot Comps"
      : operatorKey === "thatprizeguy" ? "That Prize Guy"
      : operatorKey === "giveawayguys" ? "The Giveaway Guys"
      : operatorKey === "bright" ? "Bright Competitions"
      : operatorKey === "salexmo" ? "Salexmo"
      : "Elite Competitions"
  );
  const isDcg = operatorKey === "dcg";
  const isElite = operatorKey === "elite";
  const isRevComps = operatorKey === "revcomps";
  const isBotb = operatorKey === "botb";
  const isSevenDays = operatorKey === "7days";
  const isClick = operatorKey === "click";
  const clickScanMaxDetailsValue = parseClickScanMaxDetails(clickScanMaxDetails);
  const isJonnyFromDonny = operatorKey === "jonnyfromdonny";
  const isFeelGood = operatorKey === "feelgood";
  const isAspire = operatorKey === "aspire";
  const isMcKinney = operatorKey === "mckinney";
  const isLlf = operatorKey === "llf";
  const isUkcc = operatorKey === "ukcc";
  const isBounty = operatorKey === "bounty";
  const isStorm = operatorKey === "storm";
  const isRedline = operatorKey === "redline";
  const isPrizeParadise = operatorKey === "prizeparadise";
  const isApex = operatorKey === "apex";
  const isJammy = operatorKey === "jammy";
  const isPawsome = operatorKey === "pawsome";
  const isLincolnshire = operatorKey === "lincolnshire";
  const isDrift = operatorKey === "drift";
  const isPalladium = operatorKey === "palladium";
  const isPristine = operatorKey === "pristine";
  const pristineCountOption = isPristine ? pristineCountAcceptanceOption(settings, result) : null;
  const isCaddy = operatorKey === "caddy";
  const isHotComps = operatorKey === "hotcomps";
  const isThatPrizeGuy = operatorKey === "thatprizeguy";
  const isGiveawayGuys = operatorKey === "giveawayguys";
  const isBright = operatorKey === "bright";
  const isSalexmo = operatorKey === "salexmo";
  const isKilted = operatorKey === "kilted" || isUkcc || isBounty || isStorm || isRedline || isPrizeParadise || isApex || isJammy || isPawsome || isLincolnshire || isDrift || isPalladium || isPristine || isCaddy || isHotComps || isThatPrizeGuy || isGiveawayGuys || isBright || isSalexmo;
  const isSequencedTrustedOperator = isDcg || isElite || isRevComps;
  const canRunVerifyOnly = isSequencedTrustedOperator || isBotb || isSevenDays || isClick || isAspire || isMcKinney || isLlf || isKilted;
  const canRunPublishOnly = isSequencedTrustedOperator || isBotb || isSevenDays || isClick || isAspire || isMcKinney || isLlf || isKilted;
  const scanEnabled = settings?.scan_enabled === true;
  const dryRunEnabled = settings?.dry_run_enabled === true;
  const applyEnabled = settings?.apply_enabled === true;
  const verifyEnabled = settings?.verify_enabled === true;
  const publishEnabled = settings?.publish_enabled === true;
  const expireEnabled = settings?.expire_enabled === true;
  const schedulerEnabled = settings?.scheduler_enabled === true;
  const revCleanupLiveSetMatchesSettings = (
    String(settings?.latest_successful_rev_live_set_id || "").trim()
    === REV_BROKEN_TILE_CLEANUP_LIVE_SET_ID
  );
  const revCleanupDryRunSafe = isSafeRevBrokenTileCleanupDryRun(revCleanupDryRunResult);
  const revCleanupGateFailures = revBrokenTileCleanupGateFailures(revCleanupDryRunResult);
  const revCleanupLiveSucceeded = isSuccessfulRevBrokenTileCleanupLiveResult(revCleanupLiveResult);
  const revCurrentLiveSetId = String(
    result?.settings?.latest_successful_rev_live_set_id
    || settings?.latest_successful_rev_live_set_id
    || "",
  ).trim();
  const revExpiryFailures = Array.isArray(revExpiryApprovalStatus?.blocked_reasons)
    ? revExpiryApprovalStatus.blocked_reasons
    : [];
  const revExpiryApproved = (
    isRevComps && revExpiryApprovalStatus?.approval_valid === true
  );
  const revExpiryLiveActionReady = (
    isRevComps && revExpiryApprovalStatus?.live_action_ready === true
  );
  const revExpiryStatusMessage = revExpiryApprovalError
    ? `Rev expiry approval status unavailable: ${revExpiryApprovalError}.`
    : revExpiryApprovalMessage(revExpiryApprovalStatus);
  const botbCurrentSnapshotId = String(
    settings?.latest_successful_live_set_id
      || settings?.latest_successful_botb_snapshot_id
      || "",
  ).trim();
  const botbExpiryFailures = botbExpiryGateFailures(
    botbExpiryDryRunResult,
    botbCurrentSnapshotId,
  );
  const botbExpiryApproved = isBotb && botbExpiryFailures.length === 0;
  const aspireCurrentLiveSetId = String(
    settings?.latest_successful_aspire_live_set_id || "",
  ).trim();
  const aspireExpiryFailures = aspireExpiryGateFailures(
    aspireExpiryDryRunResult,
    aspireCurrentLiveSetId,
  );
  const aspireExpiryApproved = isAspire && aspireExpiryFailures.length === 0;
  const mckinneyCurrentLiveSetId = String(
    settings?.latest_successful_mckinney_live_set_id || "",
  ).trim();
  const mckinneyPublishGate = mckinneyPublishGateState(settings);
  const mckinneyPersistedExpiryApproval = (
    mckinneyExpiryApprovalFromSettings(settings)
  );
  const mckinneyExpiryEvidence = selectMckinneyExpiryEvidence({
    dryRunResult: mckinneyExpiryDryRunResult,
    persistedApproval: mckinneyPersistedExpiryApproval,
    approvalIdBeforeDryRun: mckinneyExpiryApprovalIdBeforeDryRun,
  });
  const mckinneyExpiryApprovalRefreshPending = Boolean(
    mckinneyExpiryDryRunResult && !mckinneyExpiryEvidence,
  );
  const mckinneyExpiryFailures = mckinneyExpiryGateFailures(
    mckinneyExpiryEvidence,
    mckinneyCurrentLiveSetId,
  );
  const mckinneyExpiryApproved = (
    isMcKinney && mckinneyExpiryFailures.length === 0
  );
  const llfCurrentLiveSetId = String(
    settings?.latest_successful_llf_live_set_id || "",
  ).trim();
  const llfPublishGate = llfPublishGateState(settings);
  const llfPersistedExpiryApproval = settings?.llf_expiry_approval || null;
  const llfRunExpiryEvidence = llfExpiryEvidenceFromRunResult(
    llfExpiryDryRunResult,
  );
  const llfPersistedExpiryEvidence = llfPersistedExpiryApproval ? {
    expiry_approval_id: llfPersistedExpiryApproval.approval_id,
    approval_state: llfPersistedExpiryApproval.state,
    approval_expires_at: llfPersistedExpiryApproval.expires_at,
    llf_expiry_live_set_id: llfPersistedExpiryApproval.live_set_id,
    llf_expiry_live_set_identity_digest:
      llfPersistedExpiryApproval.live_set_identity_digest,
    llf_expiry_current_live_set_count:
      llfPersistedExpiryApproval.current_live_set_record_count,
    candidate_ids: llfPersistedExpiryApproval.candidate_ids || [],
    candidate_count: llfPersistedExpiryApproval.candidate_count || 0,
    candidate_id_digest: llfPersistedExpiryApproval.candidate_id_digest,
    llf_expiry_expected_public_count_after:
      llfPersistedExpiryApproval.expected_effective_public_count_after_expiry,
    max_expire_records: llfPersistedExpiryApproval.max_expire_records,
    expiry_generation: llfPersistedExpiryApproval.expiry_generation,
    approval_valid: llfPersistedExpiryApproval.state === "active",
  } : null;
  const llfExpiryEvidence = llfRunExpiryEvidence || llfPersistedExpiryEvidence;
  const llfExpiryFailures = isLlf
    ? llfExpiryGateFailures({
      settings,
      evidence: llfExpiryEvidence,
      expireEnabled,
    })
    : [];
  const llfExpiryApproved = isLlf && llfExpiryFailures.length === 0;
  const kiltedCurrentLiveSetId = String(
    settings?.[`latest_successful_${exactSetSettingsPrefix}_live_set_id`] || "",
  ).trim();
  const kiltedPublishGate = kiltedPublishGateState(settings);
  const kiltedPersistedExpiryApproval = settings?.[
    `${exactSetSettingsPrefix}_expiry_approval`
  ] || null;
  const kiltedRunExpiryEvidence = kiltedExpiryEvidenceFromRunResult(
    kiltedExpiryDryRunResult,
  );
  const kiltedPersistedExpiryEvidence = kiltedPersistedExpiryApproval ? {
    expiry_approval_id: kiltedPersistedExpiryApproval.approval_id,
    approval_state: kiltedPersistedExpiryApproval.state,
    approval_expires_at: kiltedPersistedExpiryApproval.expires_at,
    kilted_expiry_live_set_id: kiltedPersistedExpiryApproval.live_set_id,
    kilted_expiry_live_set_identity_digest:
      kiltedPersistedExpiryApproval.live_set_identity_digest,
    kilted_expiry_current_live_set_count:
      kiltedPersistedExpiryApproval.current_live_set_record_count,
    candidate_ids: kiltedPersistedExpiryApproval.candidate_ids || [],
    candidate_count: kiltedPersistedExpiryApproval.candidate_count || 0,
    candidate_id_digest: kiltedPersistedExpiryApproval.candidate_id_digest,
    kilted_expiry_expected_public_count_after:
      kiltedPersistedExpiryApproval.expected_effective_public_count_after_expiry,
    max_expire_records: kiltedPersistedExpiryApproval.max_expire_records,
    expiry_generation: kiltedPersistedExpiryApproval.expiry_generation,
    approval_valid: kiltedPersistedExpiryApproval.state === "active",
  } : null;
  const kiltedExpiryEvidence = (
    kiltedRunExpiryEvidence || kiltedPersistedExpiryEvidence
  );
  const kiltedExpiryFailures = isKilted
    ? kiltedExpiryGateFailures({
      settings,
      evidence: kiltedExpiryEvidence,
      expireEnabled,
    })
    : [];
  const kiltedExpiryApproved = (
    isKilted && kiltedExpiryFailures.length === 0
  );
  const dcgCurrentLiveSetId = String(
    settings?.latest_successful_dcg_live_set_id || "",
  ).trim();
  const dcgPersistedExpiryApproval = settings?.dcg_expiry_approval || null;
  const dcgExpiryEvidence = (
    dcgExpiryDryRunResult?.expiry
    || dcgExpiryDryRunResult?.summary?.expiry
    || (dcgPersistedExpiryApproval ? {
      expiry_approval_id: dcgPersistedExpiryApproval.approval_id,
      approval_state: dcgPersistedExpiryApproval.state,
      approval_expires_at: dcgPersistedExpiryApproval.expires_at,
      dcg_expiry_latest_live_set_id: dcgPersistedExpiryApproval.live_set_id,
      dcg_expiry_live_set_identity_digest:
        dcgPersistedExpiryApproval.live_set_identity_digest,
      dcg_expiry_current_live_set_count:
        dcgPersistedExpiryApproval.current_live_set_record_count,
      candidate_ids: dcgPersistedExpiryApproval.candidate_ids || [],
      candidate_count: dcgPersistedExpiryApproval.candidate_count || 0,
      candidate_id_digest: dcgPersistedExpiryApproval.candidate_id_digest,
      dcg_expected_effective_public_count_after_expiry:
        dcgPersistedExpiryApproval.expected_effective_public_count_after_expiry,
      max_expire_records: dcgPersistedExpiryApproval.max_expire_records,
      expiry_generation: dcgPersistedExpiryApproval.expiry_generation,
      approval_valid: dcgPersistedExpiryApproval.state === "active",
    } : null)
  );
  const dcgExpiryApproved = Boolean(
    isDcg
    && dcgExpiryEvidence
    && dcgExpiryEvidence.approval_state === "active"
    && dcgExpiryEvidence.approval_valid !== false
    && dcgExpiryEvidence.dcg_expiry_latest_live_set_id === dcgCurrentLiveSetId
    && dcgExpiryEvidence.dcg_expiry_live_set_identity_digest
      === settings?.latest_successful_dcg_live_set_identity_digest
    && Number(dcgExpiryEvidence.dcg_expiry_current_live_set_count) === Number(
      settings?.latest_successful_dcg_live_set_identity_count,
    )
    && Number(dcgExpiryEvidence.expiry_generation) === Number(
      settings?.expiry_generation,
    )
    && Number(dcgExpiryEvidence.max_expire_records) === Number(
      settings?.max_expire_records,
    )
    && dcgExpiryEvidence.expiry_approval_id
    && Array.isArray(dcgExpiryEvidence.candidate_ids)
    && dcgExpiryEvidence.candidate_ids.length
      === Number(dcgExpiryEvidence.candidate_count)
    && dcgExpiryEvidence.candidate_ids.length
      <= Number(dcgExpiryEvidence.max_expire_records)
    && (
      !dcgExpiryEvidence.approval_expires_at
      || Date.parse(dcgExpiryEvidence.approval_expires_at) > Date.now()
    )
  );
  const clickCurrentLiveSetId = String(
    settings?.latest_successful_click_live_set_id || "",
  ).trim();
  const clickPersistedExpiryApproval = settings?.click_expiry_approval || null;
  const clickPersistedExpiryEvidence = clickPersistedExpiryApproval ? {
    expiry_approval_id: clickPersistedExpiryApproval.approval_id,
    approval_state: clickPersistedExpiryApproval.state,
    approval_expires_at: clickPersistedExpiryApproval.expires_at,
    click_expiry_latest_live_set_id: clickPersistedExpiryApproval.live_set_id,
    click_expiry_live_set_identity_digest:
      clickPersistedExpiryApproval.live_set_identity_digest,
    click_expiry_current_live_set_count:
      clickPersistedExpiryApproval.current_live_set_record_count,
    candidate_ids: clickPersistedExpiryApproval.candidate_ids || [],
    candidate_count: clickPersistedExpiryApproval.candidate_count || 0,
    candidate_id_digest: clickPersistedExpiryApproval.candidate_id_digest,
    click_expected_effective_public_count_after_expiry:
      clickPersistedExpiryApproval.expected_effective_public_count_after_expiry,
    max_expire_records: clickPersistedExpiryApproval.max_expire_records,
    expiry_generation: clickPersistedExpiryApproval.expiry_generation,
    approval_valid: clickPersistedExpiryApproval.state === "active",
  } : null;
  const clickRunExpiryEvidence = (
    clickExpiryDryRunResult?.expiry
    || clickExpiryDryRunResult?.summary?.expiry
    || null
  );
  const clickExpiryEvidenceMatchesPersisted = Boolean(
    clickRunExpiryEvidence?.expiry_approval_id
    && clickRunExpiryEvidence.expiry_approval_id
      === clickPersistedExpiryEvidence?.expiry_approval_id
  );
  const clickExpiryEvidence = clickRunExpiryEvidence
    ? (
      clickExpiryEvidenceMatchesPersisted
        ? { ...clickPersistedExpiryEvidence, ...clickRunExpiryEvidence }
        : clickRunExpiryEvidence
    )
    : clickPersistedExpiryEvidence;
  const clickExpiryApproved = Boolean(
    isClick
    && clickExpiryEvidence
    && clickExpiryEvidence.approval_state === "active"
    && clickExpiryEvidence.approval_valid !== false
    && clickExpiryEvidence.click_expiry_latest_live_set_id === clickCurrentLiveSetId
    && clickExpiryEvidence.click_expiry_live_set_identity_digest
      === settings?.latest_successful_click_live_set_identity_digest
    && Number(clickExpiryEvidence.click_expiry_current_live_set_count) === Number(
      settings?.latest_successful_click_live_set_identity_count,
    )
    && Number(clickExpiryEvidence.expiry_generation) === Number(
      settings?.expiry_generation,
    )
    && Number(clickExpiryEvidence.max_expire_records) === Number(
      settings?.max_expire_records,
    )
    && clickExpiryEvidence.expiry_approval_id
    && Array.isArray(clickExpiryEvidence.candidate_ids)
    && clickExpiryEvidence.candidate_ids.length
      === Number(clickExpiryEvidence.candidate_count)
    && clickExpiryEvidence.candidate_ids.length
      <= Number(clickExpiryEvidence.max_expire_records)
    && (
      !clickExpiryEvidence.approval_expires_at
      || Date.parse(clickExpiryEvidence.approval_expires_at) > Date.now()
    )
  );
  const eliteCurrentLiveSetId = String(
    settings?.latest_successful_elite_live_set_id
      || settings?.latest_successful_live_set_id
      || "",
  ).trim();
  const elitePersistedExpiryApproval = settings?.elite_expiry_approval || null;
  const eliteExpiryEvidence = (
    eliteExpiryDryRunResult?.expiry
    || eliteExpiryDryRunResult?.summary?.expiry
    || (elitePersistedExpiryApproval ? {
      expiry_approval_id: elitePersistedExpiryApproval.approval_id,
      approval_status: elitePersistedExpiryApproval.state,
      approval_expires_at: elitePersistedExpiryApproval.expires_at,
      latest_successful_live_set_id: elitePersistedExpiryApproval.live_set_id,
      latest_successful_live_set_identity_digest:
        elitePersistedExpiryApproval.live_set_identity_digest,
      candidate_ids: elitePersistedExpiryApproval.candidate_ids || [],
      candidate_count: elitePersistedExpiryApproval.candidate_count || 0,
      candidate_id_digest: elitePersistedExpiryApproval.candidate_id_digest,
      max_expire_records: elitePersistedExpiryApproval.max_expire_records,
      approval_settings_generation: elitePersistedExpiryApproval.settings_generation,
      approval_expiry_generation: elitePersistedExpiryApproval.expiry_generation,
      approval_valid: elitePersistedExpiryApproval.state === "active",
    } : null)
  );
  const eliteExpiryApproved = Boolean(
    isElite
    && eliteExpiryEvidence
    && eliteExpiryEvidence.approval_status === "active"
    && eliteExpiryEvidence.approval_valid !== false
    && eliteExpiryEvidence.expiry_approval_id
    && eliteExpiryEvidence.latest_successful_live_set_id === eliteCurrentLiveSetId
    && eliteExpiryEvidence.latest_successful_live_set_identity_digest
      === settings?.latest_successful_live_set_identity_digest
    && Array.isArray(eliteExpiryEvidence.candidate_ids)
    && eliteExpiryEvidence.candidate_ids.length
      === Number(eliteExpiryEvidence.candidate_count)
    && eliteExpiryEvidence.candidate_ids.length
      <= Number(eliteExpiryEvidence.max_expire_records)
    && Number(eliteExpiryEvidence.approval_settings_generation)
      === Number(settings?.settings_generation)
    && Number(eliteExpiryEvidence.approval_expiry_generation)
      === Number(settings?.expiry_generation)
    && (
      !eliteExpiryEvidence.approval_expires_at
      || Date.parse(eliteExpiryEvidence.approval_expires_at) > Date.now()
    )
  );
  const dcgApplySnapshotReady = Boolean(
    normaliseDcgScanSnapshotReference(
      dcgScanSnapshot?.id,
      dcgScanSnapshot?.hash,
    ),
  );
  const dcgApplyPreflightReady = Boolean(
    dcgApplySnapshotReady
    && dcgApplyPreflightApproval?.id === dcgScanSnapshot?.id
    && dcgApplyPreflightApproval?.hash === dcgScanSnapshot?.hash
  );
  const eliteApplySnapshotReady = Boolean(
    normaliseEliteScanSnapshotReference(
      eliteScanSnapshot?.id,
      eliteScanSnapshot?.hash,
    ),
  );
  const botbApplySnapshotReady = Boolean(
    normaliseBotbScanSnapshotReference(
      botbScanSnapshot?.id,
      botbScanSnapshot?.hash,
    ),
  );
  const clickApplySnapshotReady = Boolean(
    normaliseClickScanSnapshotReference(
      clickScanSnapshot?.id,
      clickScanSnapshot?.hash,
    ),
  );
  const clickApplyPreflightReady = Boolean(
    clickApplySnapshotReady
    && clickApplyPreflightApproval?.id === clickScanSnapshot?.id
    && clickApplyPreflightApproval?.hash === clickScanSnapshot?.hash
  );
  const sevenDaysApplySnapshotReady = Boolean(
    normaliseSevenDaysScanSnapshotReference(
      sevenDaysScanSnapshot?.id,
      sevenDaysScanSnapshot?.hash,
    ),
  );
  const aspireApplySnapshotReady = Boolean(
    normaliseAspireScanSnapshotReference(
      aspireScanSnapshot?.id,
      aspireScanSnapshot?.hash,
    ),
  );
  const mckinneyApplySnapshotReady = Boolean(
    normaliseMckinneyScanSnapshotReference(
      mckinneyScanSnapshot?.id,
      mckinneyScanSnapshot?.hash,
    ),
  );
  const mckinneyApplyPreflightReady = Boolean(
    mckinneyApplySnapshotReady
    && mckinneyApplyPreflightApproval?.id === mckinneyScanSnapshot?.id
    && mckinneyApplyPreflightApproval?.hash === mckinneyScanSnapshot?.hash
    && String(mckinneyApplyPreflightApproval?.maxNewRecords)
      === String(mckinneyMaxNewRecords)
  );
  const llfApplySnapshotReady = Boolean(
    normaliseLlfScanSnapshotReference(
      llfScanSnapshot?.id,
      llfScanSnapshot?.hash,
    ),
  );
  const llfApplyPreflightReady = Boolean(
    llfApplySnapshotReady
    && llfApplyPreflightApproval?.id === llfScanSnapshot?.id
    && llfApplyPreflightApproval?.hash === llfScanSnapshot?.hash
    && String(llfApplyPreflightApproval?.maxNewRecords)
      === String(llfMaxNewRecords)
  );
  const kiltedApplySnapshotReady = Boolean(
    normaliseKiltedScanSnapshotReference(
      kiltedScanSnapshot?.id,
      kiltedScanSnapshot?.hash,
    ),
  );
  const kiltedApplyPreflightReady = Boolean(
    kiltedApplySnapshotReady
    && kiltedApplyPreflightApproval?.id === kiltedScanSnapshot?.id
    && kiltedApplyPreflightApproval?.hash === kiltedScanSnapshot?.hash
    && String(kiltedApplyPreflightApproval?.maxNewRecords)
      === String(kiltedMaxNewRecords)
  );
  const revSnapshotControlConfig = {
    limitPerOperator: MAX_DETAIL_FETCH_LIMIT,
    maxDetails: parseBatchNumber(revBatch.detailFetchLimit, {
      fallback: DEFAULT_DETAIL_FETCH_LIMIT,
      min: 1,
      max: MAX_DETAIL_FETCH_LIMIT,
    }),
    detailFetchOffset: parseBatchNumber(revBatch.detailFetchOffset, {
      fallback: 0,
      min: 0,
      max: MAX_DETAIL_FETCH_OFFSET,
    }),
  };
  const revSnapshotExpiry = Date.parse(revScanSnapshot?.expiresAt || "");
  const revSnapshotConfig = revScanSnapshot?.scanConfig || {};
  const revApplySnapshotReady = Boolean(
    normaliseRevScanSnapshotReference(
      revScanSnapshot?.id,
      revScanSnapshot?.hash,
    )
    && revScanSnapshot?.state === "ready"
    && Number.isFinite(revSnapshotExpiry)
    && revSnapshotExpiry > Date.now()
    && Number(revSnapshotConfig.limit_per_operator)
      === revSnapshotControlConfig.limitPerOperator
    && Number(revSnapshotConfig.requested_max_details)
      === revSnapshotControlConfig.maxDetails
    && Number(revSnapshotConfig.validated_max_details)
      === revSnapshotControlConfig.maxDetails
    && Number(revSnapshotConfig.detail_fetch_offset)
      === revSnapshotControlConfig.detailFetchOffset
    && revSnapshotConfig.automated_network_discovery === false
  );
  const aspirePreflightFailureReason = (() => {
    if (!isAspire || result?.phase !== "apply_preflight" || aspireApplySnapshotReady) {
      return null;
    }
    const blockedReasons = result?.summary?.blocked_reasons || [];
    if (result?.status === "ok") {
      return "Preflight response did not include the required snapshot ID and hash.";
    }
    return (
      result?.error
      || blockedReasons[0]
      || result?.diagnostics?.aspire_apply_scan_snapshot_error
      || result?.status
      || "preflight_failed"
    );
  })();
  const mckinneyPreflightFailureReason = (() => {
    if (
      !isMcKinney
      || result?.phase !== "apply_preflight"
    ) {
      return null;
    }
    const blockedReasons = result?.summary?.blocked_reasons || [];
    if (result?.status === "ok" && !mckinneyApplyPreflightReady) {
      return "Preflight response did not approve the current snapshot and create limit.";
    }
    if (result?.status === "ok") return null;
    return (
      result?.error
      || blockedReasons[0]
      || result?.status
      || "preflight_failed"
    );
  })();
  const llfPreflightFailureReason = (() => {
    if (!isLlf || result?.phase !== "apply_preflight") return null;
    const blockedReasons = result?.summary?.blocked_reasons || [];
    if (result?.status === "ok" && !llfApplyPreflightReady) {
      return "Preflight response did not approve the current snapshot and create limit.";
    }
    if (result?.status === "ok") return null;
    return result?.error || blockedReasons[0] || result?.status || "preflight_failed";
  })();
  const kiltedPreflightFailureReason = (() => {
    if (!isKilted || result?.phase !== "apply_preflight") return null;
    const blockedReasons = result?.summary?.blocked_reasons || [];
    if (result?.status === "ok" && !kiltedApplyPreflightReady) {
      return "Preflight response did not approve the current snapshot and create limit.";
    }
    if (result?.status === "ok") return null;
    return result?.error || blockedReasons[0] || result?.status || "preflight_failed";
  })();
  const scanPreviewEnabled = (isBotb || isSevenDays || isClick || isJonnyFromDonny || isFeelGood || isAspire || isMcKinney || isKilted)
    ? true
    : isLlf
    ? scanEnabled && dryRunEnabled
    : scanEnabled && dryRunEnabled;
  const applyButtonLabel = isRevComps
    ? `Run full Rev catalogue: create/update pending review records`
    : isBotb
    ? `Apply BOTB exact snapshot as drafts: no verify/publish`
    : isSevenDays
    ? `Apply 7days draft records: no verify/publish`
    : isClick
    ? `Apply Click draft records: no verify/publish`
    : isAspire
    ? `Apply Aspire drafts: no publish`
    : isMcKinney
    ? `Apply McKinney drafts from snapshot: no verify/publish`
    : isLlf
    ? `Apply LLF exact safe drafts from snapshot: no verify/publish`
    : isKilted
    ? `Apply ${operatorName} exact safe drafts from snapshot: no verify/publish`
    : isDcg
    ? `Run DCG apply-only: create/update records, no verify/publish`
    : isElite
    ? `Apply Elite draft records from snapshot: no verify/publish`
    : publishEnabled
    ? `Run ${operatorName} apply + verify + publish${expireEnabled ? " + expire" : ""}`
    : verifyEnabled
    ? `Run ${operatorName} apply + verify: no publish${expireEnabled ? ", expire guarded" : ""}`
    : `Apply ${operatorName} only: create/update records${expireEnabled ? ", expire guarded" : ", no verify/publish"}`;
  const applyConfirmText = isRevComps
    ? `Apply immutable Rev scan ${revScanSnapshot?.id || "missing"} with ${revScanSnapshot?.exactIdentityCount || 0} exact identities. This uses the displayed stored candidate set and performs no fresh discovery. No verify, publish, expire or scheduler phase will run.`
    : isBotb
    ? `Apply immutable BOTB scan ${botbScanSnapshot?.id || "missing"} as draft records only. This uses the exact stored candidate set without rediscovery. Verification, publication, expiry and scheduling remain separate settings-gated phases.`
    : isSevenDays
    ? `Apply accepted 7days standard-draw candidates as draft review records only. Verification and publication are separate existing-record phases; expiry and scheduler will not run.`
    : isClick
    ? `Apply the preflight-approved Click public-safe set as draft review records only. Awareness-only records remain non-odds; verification, publication and guarded expiry are separate phases.`
    : isAspire
    ? `Apply the exact immutable Aspire preflight snapshot as internal draft review records. Verification, publication, Expiry and Scheduler remain separate settings-gated actions and do not run here.`
    : isMcKinney
    ? `Apply only the safe identities from the exact preflight-approved McKinney snapshot. Review-only identities stay hidden and accounted for. No rediscovery, verification, publication, expiry or scheduling occurs.`
    : isLlf
    ? `Apply only the safe identities from the exact preflight-approved LLF snapshot. Review and intentionally ineligible identities remain hidden and accounted for. No rediscovery, verification, publication, expiry or scheduling occurs.`
    : isKilted
    ? `Apply only the public-safe identities from the exact preflight-approved ${operatorName} snapshot. Awareness-only identities remain honest no-odds records. No rediscovery, verification, publication, expiry or scheduling occurs.`
    : isDcg
    ? `Apply immutable DCG scan ${dcgScanSnapshot?.id || "missing"} (${dcgScanSnapshot?.hash || "missing hash"}) through the DCG V2 apply-only path without rediscovery. Create or update eligible records only; verification, publication and expiry remain separate settings-gated phases.`
    : isElite
    ? `Apply the exact immutable Elite scan snapshot as draft review records. This performs no rediscovery, verification, publication, expiry or scheduling.`
    : publishEnabled
    ? `Run ${operatorName} apply + verify + publish automation: publish only verified eligible ${operatorName} records. ${expireEnabled ? "Expiry will run only if the operator safety guardrails pass." : "Expire will not run."}`
    : verifyEnabled
    ? `Run ${operatorName} apply + verify automation: create/update review records and verify eligible records. No publish will run. ${expireEnabled ? "Expiry will run only if the operator safety guardrails pass." : "Expire will not run."}`
    : `Run ${operatorName} apply-only automation: create/update review records only. No verify or publish will run. ${expireEnabled ? "Expiry will run only if the operator safety guardrails pass." : "Expire will not run unless enabled separately."}`;
  const verifyConfirmText = isAspire
    ? "Verify existing Aspire draft records only. This will not create, publish, expose public cards, enable Budget Odds, expire, schedule or call cart endpoints."
    : isBotb
    ? "Verify only records belonging to the exact completed BOTB Apply set. This will not rediscover, create, publish, expire, schedule or call purchase endpoints."
    : isMcKinney
    ? "Verify only the exact McKinney live-set drafts created by Apply. This will not rediscover, create, publish, expose public cards, expire or schedule."
    : isLlf
    ? "Verify only the exact safe LLF live-set drafts created by Apply. This will not rediscover, create, publish, expose public cards, expire or schedule."
    : isKilted
    ? `Verify only the exact public-safe ${operatorName} live-set drafts created by Apply. This will not rediscover, create, publish, expose public cards, expire or schedule.`
    : `Verify existing ${operatorName} pending review records only. This will not create, publish, expire, schedule or call cart endpoints.`;
  const publishConfirmText = isSevenDays
    ? "Publish verified 7days drafts and refresh public image fields for eligible already-published 7days rows. This will not apply, create proposal data, verify, expire or schedule."
    : isBotb
    ? "Publish only the exact verified BOTB live set. Display-safe records with incomplete odds evidence remain honest no-odds records. This will not scan, apply, verify, expire, schedule or change affiliate routing."
    : isClick
    ? "Publish the exact verified Click live set. Awareness-only records remain public without odds and excluded from Budget Odds. This will not scan, apply, verify, expire or schedule."
    : isAspire
    ? "Publish verified Aspire drafts only. Paid records enter Budget Odds only when trusted price, total-cap, category and live evidence pass; free-entry rows remain public no-odds. This will not scan, apply, verify, expire, schedule or change affiliate links."
    : isMcKinney
    ? "Publish only the exact verified McKinney live set when Publish has been explicitly enabled. Unsupported mechanics remain awareness/no-standard-odds. This will not scan, apply, verify, expire or schedule."
    : isLlf
    ? "Publish only the exact verified LLF live set when Publish and its authoritative gate are enabled. Unsupported mechanics remain awareness/no-standard-odds. This will not scan, apply, verify, expire or schedule."
    : isKilted
    ? `Publish only the exact verified ${operatorName} live set when Publish and its authoritative gate are enabled. Unsupported mechanics remain awareness/no-standard-odds. This will not scan, apply, verify, expire or schedule.`
    : `Publish verified ${operatorName} drafts only. This will not scan, apply, create, update proposal data, verify, expire or schedule.`;
  const expiryDryRunText = `Expiry dry-run for ${operatorName}: scans the current catalogue and reports records that would be hidden. No records are hidden.`;
  const expiryLiveText = isBotb
    ? "Live BOTB expiry hides stale public rows only. It never deletes raw records and only runs through backend safety gates."
    : isClick
    ? "Live Click expiry hides only published Click records with current-detail closed or past-draw evidence. It never deletes raw records and only runs through backend safety gates."
    : `Run ${operatorName} expiry: hide confirmed expired public records only when the current catalogue scan passes every safety gate. No records are deleted.`;

  const runAutomation = async ({
    dryRun,
    phase = "auto",
    detailFetchOffset,
    automatedNetworkDiscovery = false,
    scanSnapshotId,
    scanSnapshotHash,
    aspireInitialReplacementAuthorisationId,
    aspireInitialReplacementAuthorisationToken,
    expectedLiveSetId,
    candidateIds,
    expiryApprovalId,
  } = {}) => {
    const pristineCountAcceptanceRunId = isPristine && dryRun === true && ["auto", "scan"].includes(phase)
      && pristineCountOption?.runId === pristineCountAcceptanceSelection ? pristineCountAcceptanceSelection : undefined;
    const isSafeSevenDaysScanRequest = (
      isSevenDays
      && dryRun === true
      && (phase === "auto" || phase === "scan")
    );
    const isSafeClickScanRequest = (
      isClick
      && dryRun === true
      && (phase === "auto" || phase === "scan")
    );
    if (isSafeClickScanRequest && clickScanMaxDetailsValue == null) {
      toast.error("Enter a whole-number Click scan detail limit from 1 to 250.");
      return;
    }
    const isSafeClickApplyPreflightRequest = (
      isClick
      && dryRun === true
      && phase === "apply_preflight"
    );
    const isSafeJonnyFromDonnyScanRequest = (
      isJonnyFromDonny
      && dryRun === true
      && (phase === "auto" || phase === "scan")
    );
    const isSafeFeelGoodScanRequest = (
      isFeelGood
      && dryRun === true
      && (phase === "auto" || phase === "scan")
    );
    const isSafeAspireScanRequest = (
      isAspire
      && dryRun === true
      && (phase === "auto" || phase === "scan")
    );
    const isSafeAspireApplyPreflightRequest = (
      isAspire
      && dryRun === true
      && phase === "apply_preflight"
    );
    const isSafeMcKinneyScanRequest = (
      isMcKinney
      && dryRun === true
      && (phase === "auto" || phase === "scan")
    );
    const isSafeMcKinneyApplyPreflightRequest = (
      isMcKinney
      && dryRun === true
      && phase === "apply_preflight"
    );
    const isSafeBotbScanRequest = (
      isBotb
      && dryRun === true
      && (phase === "auto" || phase === "scan")
    );
    const isSafeBotbPublishRequest = (
      isBotb
      && dryRun === false
      && (phase === "publish" || phase === "publish_public_catalogue")
    );
    const isSafeBotbExpiryDryRunRequest = (
      isBotb
      && dryRun === true
      && phase === "expire"
    );
    const isSafeBotbExpiryLiveRequest = (
      isBotb
      && dryRun === false
      && phase === "expire"
    );
    const isSevenDaysExpiryDryRunRequest = (
      isSevenDays
      && dryRun === true
      && phase === "expire"
    );
    const isClickExpiryDryRunRequest = (
      isClick
      && dryRun === true
      && phase === "expire"
    );
    if (
      (dryRun || phase === "auto" || phase === "apply" || phase === "expire" || phase === "publish_public_catalogue")
      && !scanEnabled
      && !isSafeBotbScanRequest
      && !isSafeSevenDaysScanRequest
      && !isSafeClickScanRequest
      && !isSafeClickApplyPreflightRequest
      && !isSafeJonnyFromDonnyScanRequest
      && !isSafeFeelGoodScanRequest
      && !isSafeAspireScanRequest
      && !isSafeAspireApplyPreflightRequest
      && !isSafeMcKinneyScanRequest
      && !isSafeMcKinneyApplyPreflightRequest
      && !isSafeBotbPublishRequest
      && !isSafeBotbExpiryDryRunRequest
      && !isSafeBotbExpiryLiveRequest
      && !isSevenDaysExpiryDryRunRequest
      && !isClickExpiryDryRunRequest
    ) {
      toast.error(`${operatorName} scan is disabled in automation settings.`);
      return;
    }
    if (dryRun && !dryRunEnabled && !isSafeBotbScanRequest && !isSafeSevenDaysScanRequest && !isSafeClickScanRequest && !isSafeClickApplyPreflightRequest && !isSafeJonnyFromDonnyScanRequest && !isSafeFeelGoodScanRequest && !isSafeAspireScanRequest && !isSafeAspireApplyPreflightRequest && !isSafeMcKinneyScanRequest && !isSafeMcKinneyApplyPreflightRequest && !isSafeBotbExpiryDryRunRequest && !isSevenDaysExpiryDryRunRequest && !isClickExpiryDryRunRequest) {
      toast.error(`${operatorName} dry-run is disabled in automation settings.`);
      return;
    }
    if (!dryRun && phase === "verify" && !verifyEnabled) {
      toast.error(`${operatorName} verify is disabled in automation settings.`);
      return;
    }
    if (!dryRun && phase === "publish" && !publishEnabled) {
      toast.error(`${operatorName} publish is disabled in automation settings.`);
      return;
    }
    if (!dryRun && phase === "publish" && isMcKinney && !mckinneyPublishGate.enabled) {
      toast.error(`McKinney Publish is locked: ${mckinneyPublishGate.failures.join(", ")}.`);
      return;
    }
    if (!dryRun && phase === "publish" && isLlf && !llfPublishGate.enabled) {
      toast.error(`LLF Publish is locked: ${llfPublishGate.failures.join(", ")}.`);
      return;
    }
    if (!dryRun && phase === "publish" && isKilted && !kiltedPublishGate.enabled) {
      toast.error(`${operatorName} Publish is locked: ${kiltedPublishGate.failures.join(", ")}.`);
      return;
    }
    if (!dryRun && phase === "expire" && !expireEnabled) {
      toast.error(`${operatorName} expire is disabled in automation settings.`);
      return;
    }
    if (!dryRun && !["verify", "publish", "expire", "publish_public_catalogue"].includes(phase) && !applyEnabled) {
      toast.error(`${operatorName} apply is disabled in automation settings.`);
      return;
    }
    const confirmText = phase === "verify"
      ? verifyConfirmText
      : phase === "publish"
      ? publishConfirmText
      : phase === "publish_public_catalogue"
      ? "Publish BOTB public-safe catalogue records from a fresh scan. This creates or updates BOTB public-safe records only. No scheduler, expiry, affiliate links, cart or purchase endpoint will run."
      : phase === "expire"
      ? dryRun ? expiryDryRunText : expiryLiveText
      : applyConfirmText;
    if (
      ((!dryRun
      && !((isRevComps || isDcg || isElite || isBotb || isAspire || isMcKinney || isLlf || isKilted) && phase === "expire" && Array.isArray(candidateIds))
      && !await confirmProductionWriteInApp(confirmText))
      || (pristineCountAcceptanceRunId && !await confirmProductionWriteInApp(
        `Refresh and accept only the same ${pristineCountOption.count} confirmed Pristine identities from the referenced ${pristineCountOption.declared}-result Scan. Scan reference: ${pristineCountAcceptanceRunId}. The unidentified extra result remains unknown and will not be retired or expired. All other source checks must pass.`,
      )))
    ) {
      return;
    }
    const batchControls = {
      detailFetchLimit: parseBatchNumber(revBatch.detailFetchLimit, {
        fallback: DEFAULT_DETAIL_FETCH_LIMIT,
        min: 1,
        max: MAX_DETAIL_FETCH_LIMIT,
      }),
      detailFetchOffset: parseBatchNumber(
        detailFetchOffset ?? revBatch.detailFetchOffset,
        {
          fallback: 0,
          min: 0,
          max: MAX_DETAIL_FETCH_OFFSET,
        },
      ),
    };
    if (isRevComps) {
      setRevBatch(batchControls);
    }
    if (isAspire && phase === "scan") {
      setAspireScanSnapshot(null);
      setAspireInitialReplacementAuthorisation(null);
    }
    const isNextBatch = (
      isRevComps
      && dryRun
      && detailFetchOffset != null
      && batchControls.detailFetchOffset !== parseBatchNumber(
        revBatch.detailFetchOffset,
        {
          fallback: 0,
          min: 0,
          max: MAX_DETAIL_FETCH_OFFSET,
        },
      )
    );
    setBusy(
      dryRun && phase === "expire"
        ? "expire"
        : phase === "apply_preflight"
        ? (isDcg ? "dcg-preflight" : isClick ? "click-preflight" : isMcKinney ? "mckinney-preflight" : isLlf ? "llf-preflight" : isKilted ? "kilted-preflight" : "aspire-preflight")
        : dryRun
        ? (automatedNetworkDiscovery && isAspire ? "aspire-network" : isNextBatch ? "next-scan" : "scan")
        : (["verify", "publish", "expire", "publish_public_catalogue"].includes(phase) ? phase : "apply"),
    );
    try {
      const { data } = await api.post("/admin/operator-automation/run-once", buildOperatorAutomationRunPayload({
        operatorKey,
        dryRun,
        phase,
        limitPerOperator: isRevComps
          ? MAX_DETAIL_FETCH_LIMIT
          : isJonnyFromDonny
          ? JFD_DEFAULT_LIMIT_PER_OPERATOR
          : isFeelGood
          ? FEELGOOD_DEFAULT_LIMIT_PER_OPERATOR
          : isAspire
          ? ASPIRE_DEFAULT_LIMIT_PER_OPERATOR
          : isMcKinney
          ? MCKINNEY_DEFAULT_LIMIT_PER_OPERATOR
          : isLlf
          ? LLF_DEFAULT_LIMIT_PER_OPERATOR
          : isKilted
          ? KILTED_DEFAULT_LIMIT_PER_OPERATOR
          : DEFAULT_LIMIT_PER_OPERATOR,
        maxDetails: isSafeClickScanRequest
          ? clickScanMaxDetailsValue
          : isRevComps
          ? DEFAULT_DETAIL_FETCH_LIMIT
          : isSevenDays
          ? SEVENDAYS_MAX_DETAILS
          : isAspire
          ? aspireScanMaxDetails
          : isMcKinney
          ? mckinneyScanMaxDetails
          : isLlf
          ? llfScanMaxDetails
          : isKilted
          ? kiltedScanMaxDetails
          : DEFAULT_MAX_DETAILS,
        maxNewRecords: isAspire
          ? aspireMaxNewRecords
          : isMcKinney
          ? mckinneyMaxNewRecords
          : isLlf
          ? llfMaxNewRecords
          : isKilted
          ? kiltedMaxNewRecords
          : undefined,
        detailFetchLimit: batchControls.detailFetchLimit,
        detailFetchOffset: batchControls.detailFetchOffset,
        automatedNetworkDiscovery,
        scanSnapshotId: scanSnapshotId || (
            isDcg && ["apply_preflight", "apply"].includes(phase)
              ? dcgScanSnapshot?.id
            : isElite && phase === "apply"
              ? eliteScanSnapshot?.id
            : isBotb && phase === "apply"
            ? botbScanSnapshot?.id
            : isClick && ["apply_preflight", "apply"].includes(phase)
            ? clickScanSnapshot?.id
            : isSevenDays && phase === "apply"
            ? sevenDaysScanSnapshot?.id
            : isAspire && phase === "apply"
            ? aspireScanSnapshot?.id
            : isMcKinney && ["apply_preflight", "apply"].includes(phase)
            ? mckinneyScanSnapshot?.id
            : isLlf && ["apply_preflight", "apply"].includes(phase)
            ? llfScanSnapshot?.id
            : isKilted && ["apply_preflight", "apply"].includes(phase)
            ? kiltedScanSnapshot?.id
            : isRevComps && phase === "apply"
            ? revScanSnapshot?.id
            : undefined
        ),
        scanSnapshotHash: scanSnapshotHash || (
          isDcg && ["apply_preflight", "apply"].includes(phase)
            ? dcgScanSnapshot?.hash
            : isElite && phase === "apply"
              ? eliteScanSnapshot?.hash
            : isBotb && phase === "apply"
            ? botbScanSnapshot?.hash
            : isClick && ["apply_preflight", "apply"].includes(phase)
            ? clickScanSnapshot?.hash
            : isSevenDays && phase === "apply"
            ? sevenDaysScanSnapshot?.hash
            : isAspire && phase === "apply"
            ? aspireScanSnapshot?.hash
            : isMcKinney && ["apply_preflight", "apply"].includes(phase)
            ? mckinneyScanSnapshot?.hash
            : isLlf && ["apply_preflight", "apply"].includes(phase)
            ? llfScanSnapshot?.hash
            : isKilted && ["apply_preflight", "apply"].includes(phase)
            ? kiltedScanSnapshot?.hash
            : isRevComps && phase === "apply"
            ? revScanSnapshot?.hash
            : undefined
        ),
        aspireInitialReplacementAuthorisationId: (
          aspireInitialReplacementAuthorisationId
          || (["apply_preflight", "apply"].includes(phase)
            ? aspireInitialReplacementAuthorisation?.id
            : undefined)
        ),
        aspireInitialReplacementAuthorisationToken: (
          aspireInitialReplacementAuthorisationToken
          || (["apply_preflight", "apply"].includes(phase)
            ? aspireInitialReplacementAuthorisation?.token
            : undefined)
        ),
        expectedLiveSetId,
        candidateIds,
        expiryApprovalId,
        pristineCountAcceptanceRunId,
      }));
      if (isRevComps) {
        const summary = data.summary || {};
        setRevBatch((current) => ({
          detailFetchLimit: summary.detail_fetch_limit || current.detailFetchLimit,
          detailFetchOffset: summary.detail_fetch_offset ?? current.detailFetchOffset,
        }));
      }
      setResult(data);
      if (isDcg && phase === "scan" && dryRun === true) {
        const snapshotReference = dcgSnapshotReferenceFromRunResult(data);
        setDcgScanSnapshot(
          data.status === "ok" ? snapshotReference : null,
        );
        setDcgApplyPreflightApproval(null);
      }
      if (isDcg && phase === "apply_preflight" && dryRun === true) {
        const approved = (
          data.status === "ok"
          && data.summary?.dcg_apply_exact_identity_set_match === true
          && data.summary?.dcg_apply_unaccounted_identity_count === 0
          && data.diagnostics?.dcg_apply_preflight_ready === true
          && dcgScanSnapshot
        );
        setDcgApplyPreflightApproval(approved ? {
          id: dcgScanSnapshot.id,
          hash: dcgScanSnapshot.hash,
        } : null);
      }
      if (isDcg && phase === "apply" && dryRun === false) {
        setDcgScanSnapshot(null);
        setDcgApplyPreflightApproval(null);
      }
      if (isElite && phase === "scan" && dryRun === true) {
        const snapshotReference = eliteSnapshotReferenceFromRunResult(data);
        setEliteScanSnapshot(
          data.status === "ok" ? snapshotReference : null,
        );
      }
      if (isElite && phase === "apply" && dryRun === false) {
        setEliteScanSnapshot(null);
      }
      if (isBotb && phase === "scan" && dryRun === true) {
        const snapshotReference = botbSnapshotReferenceFromRunResult(data);
        setBotbScanSnapshot(
          data.status === "ok" ? snapshotReference : null,
        );
      }
      if (isBotb && phase === "apply" && dryRun === false) {
        setBotbScanSnapshot(null);
      }
      if (isClick && phase === "scan" && dryRun === true) {
        const snapshotReference = clickSnapshotReferenceFromRunResult(data);
        setClickScanSnapshot(
          data.status === "ok" ? snapshotReference : null,
        );
        setClickApplyPreflightApproval(null);
      }
      if (isClick && phase === "apply_preflight" && dryRun === true) {
        const approved = (
          data.status === "ok"
          && data.summary?.click_apply_exact_identity_set_match === true
          && data.summary?.click_apply_writes_performed === false
          && clickScanSnapshot
        );
        setClickApplyPreflightApproval(approved ? {
          id: clickScanSnapshot.id,
          hash: clickScanSnapshot.hash,
        } : null);
      }
      if (
        isClick
        && phase === "apply"
        && dryRun === false
        && data.status === "ok"
        && data.summary?.click_apply_complete_set_persisted === true
        && data.summary?.click_apply_exact_identity_set_match === true
      ) {
        setClickScanSnapshot(null);
        setClickApplyPreflightApproval(null);
      }
      if (isSevenDays && phase === "scan" && dryRun === true) {
        const snapshotReference = sevenDaysSnapshotReferenceFromRunResult(data);
        setSevenDaysScanSnapshot(
          data.status === "ok" ? snapshotReference : null,
        );
      }
      if (isSevenDays && phase === "apply" && dryRun === false) {
        setSevenDaysScanSnapshot(null);
      }
      if (isAspire && phase === "apply_preflight" && dryRun === true) {
        const snapshotReference = aspireSnapshotReferenceFromRunResult(data);
        setAspireScanSnapshot(
          data.status === "ok" ? snapshotReference : null,
        );
        if (data.status !== "ok") {
          setAspireInitialReplacementAuthorisation(null);
        }
      }
      if (isAspire && phase === "apply" && dryRun === false) {
        setAspireScanSnapshot(null);
        setAspireInitialReplacementAuthorisation(null);
      }
      if (isMcKinney && phase === "scan" && dryRun === true) {
        const snapshotReference = mckinneySnapshotReferenceFromRunResult(data);
        setMckinneyScanSnapshot(data.status === "ok" && snapshotReference ? {
          ...snapshotReference,
          state: "ready",
          version: data?.diagnostics?.mckinney_scan_snapshot_version,
        } : null);
        setMckinneyApplyPreflightApproval(null);
      }
      if (isMcKinney && phase === "apply_preflight" && dryRun === true) {
        const snapshotReference = mckinneySnapshotReferenceFromRunResult(data);
        const approved = (
          data.status === "ok"
          && snapshotReference?.id === mckinneyScanSnapshot?.id
          && snapshotReference?.hash === mckinneyScanSnapshot?.hash
        );
        setMckinneyApplyPreflightApproval(approved ? {
          ...snapshotReference,
          maxNewRecords: mckinneyMaxNewRecords,
        } : null);
      }
      if (isMcKinney && phase === "apply" && dryRun === false) {
        setMckinneyScanSnapshot(null);
        setMckinneyApplyPreflightApproval(null);
      }
      if (isLlf && phase === "scan" && dryRun === true) {
        const snapshotReference = llfSnapshotReferenceFromRunResult(data);
        setLlfScanSnapshot(data.status === "ok" && snapshotReference ? {
          ...snapshotReference,
          state: "ready",
          version: data?.diagnostics?.llf_scan_snapshot_version,
        } : null);
        setLlfApplyPreflightApproval(null);
      }
      if (isLlf && phase === "apply_preflight" && dryRun === true) {
        const snapshotReference = llfSnapshotReferenceFromRunResult(data);
        const approved = (
          data.status === "ok"
          && snapshotReference?.id === llfScanSnapshot?.id
          && snapshotReference?.hash === llfScanSnapshot?.hash
        );
        setLlfApplyPreflightApproval(approved ? {
          ...snapshotReference,
          maxNewRecords: llfMaxNewRecords,
        } : null);
      }
      if (isLlf && phase === "apply" && dryRun === false) {
        setLlfScanSnapshot(null);
        setLlfApplyPreflightApproval(null);
      }
      if (isKilted && phase === "scan" && dryRun === true) {
        const snapshotReference = kiltedSnapshotReferenceFromRunResult(data);
        setKiltedScanSnapshot(data.status === "ok" && snapshotReference ? {
          ...snapshotReference,
          state: "ready",
          version: data?.diagnostics?.[
            `${exactSetSettingsPrefix}_scan_snapshot_version`
          ],
        } : null);
        setKiltedApplyPreflightApproval(null);
      }
      if (isKilted && phase === "apply_preflight" && dryRun === true) {
        const snapshotReference = kiltedSnapshotReferenceFromRunResult(data);
        const approved = (
          data.status === "ok"
          && snapshotReference?.id === kiltedScanSnapshot?.id
          && snapshotReference?.hash === kiltedScanSnapshot?.hash
        );
        setKiltedApplyPreflightApproval(approved ? {
          ...snapshotReference,
          maxNewRecords: kiltedMaxNewRecords,
        } : null);
      }
      if (
        isKilted
        && phase === "apply"
        && dryRun === false
        && data.status === "ok"
        && data.summary?.[
          `${exactSetSettingsPrefix}_apply_complete_set_persisted`
        ] === true
        && data.summary?.[
          `${exactSetSettingsPrefix}_apply_exact_identity_set_match`
        ] === true
        && data.summary?.[
          `${exactSetSettingsPrefix}_apply_snapshot_consumed`
        ] === true
      ) {
        setKiltedScanSnapshot(null);
        setKiltedApplyPreflightApproval(null);
      }
      if (isRevComps && phase === "scan" && dryRun === true) {
        const snapshotReference = revSnapshotReferenceFromRunResult(data);
        if (data.status === "ok") {
          setRevScanSnapshot(snapshotReference);
        }
      }
      if (isRevComps && phase === "apply" && dryRun === false) {
        setRevScanSnapshot(null);
      }
      if (isRevComps && phase === "expire" && dryRun === true) {
        setRevExpiryDryRunResult(data);
      }
      if (isDcg && phase === "expire" && dryRun === true) {
        setDcgExpiryDryRunResult(data);
      }
      if (isClick && phase === "expire" && dryRun === true) {
        setClickExpiryDryRunResult(data);
      }
      if (isElite && phase === "expire" && dryRun === true) {
        setEliteExpiryDryRunResult(data);
      }
      if (isBotb && phase === "expire" && dryRun === true) {
        setBotbExpiryDryRunResult(data);
      }
      if (isAspire && phase === "expire" && dryRun === true) {
        setAspireExpiryDryRunResult(data);
      }
      if (isMcKinney && phase === "expire" && dryRun === true) {
        setMckinneyExpiryDryRunResult(data);
      }
      if (isLlf && phase === "expire" && dryRun === true) {
        setLlfExpiryDryRunResult(data);
      }
      if (isKilted && phase === "expire" && dryRun === true) {
        setKiltedExpiryDryRunResult(data);
      }
      const summary = data.summary || {};
      if (dryRun) {
        if (phase === "expire") {
          toast.success(
            `${operatorName} expiry dry-run: ${summary.would_expire || 0} would hide, ${summary.expired_hidden || 0} hidden.`,
          );
        } else {
          toast.success(
            `${operatorName} automation scan: ${summary.would_create || 0} would create, ${summary.would_update || 0} would update.`,
          );
        }
      } else if (phase === "verify") {
        toast.success(
          `${operatorName} automation verify: ${summary.auto_verified || 0} verified, ${summary.review_needed || 0} kept for review.`,
        );
      } else if (phase === "publish") {
        toast.success(
          `${operatorName} automation publish: ${summary.auto_published || 0} published, ${summary.review_needed || 0} kept out of public view.`,
        );
      } else if (phase === "publish_public_catalogue") {
        toast.success(
          `${operatorName} public catalogue: ${summary.created || 0} created, ${summary.updated || 0} updated, ${summary.auto_published || 0} newly published.`,
        );
      } else if (phase === "expire") {
        toast.success(
          `${operatorName} automation expiry: ${summary.expired_hidden || 0} hidden, ${summary.would_expire || 0} candidates.`,
        );
      } else {
        toast.success(
          `${operatorName} automation apply: ${summary.created || 0} created, ${summary.updated || 0} updated.`,
        );
      }
      onRun?.(data);
      return data;
    } catch (error) {
      if (isAspire && ["apply_preflight", "apply"].includes(phase)) {
        setAspireScanSnapshot(null);
        setAspireInitialReplacementAuthorisation(null);
      }
      if (isRevComps && phase === "expire" && dryRun) {
        setRevExpiryDryRunResult(null);
      }
      if (isDcg && phase === "expire") setDcgExpiryDryRunResult(null);
      if (isClick && phase === "expire") setClickExpiryDryRunResult(null);
      if (isElite && phase === "expire") setEliteExpiryDryRunResult(null);
      if (isBotb && phase === "expire") setBotbExpiryDryRunResult(null);
      if (isAspire && phase === "expire") setAspireExpiryDryRunResult(null);
      if (isMcKinney && phase === "expire") setMckinneyExpiryDryRunResult(null);
      if (isLlf && phase === "expire") setLlfExpiryDryRunResult(null);
      if (isKilted && phase === "expire") setKiltedExpiryDryRunResult(null);
      toast.error(
        `${operatorName} automation failed: `
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
      return null;
    } finally {
      setBusy(null);
    }
  };

  const aspireInitialReplacementIdentityCount = Number(
    result?.diagnostics?.aspire_exact_identity_count || 0,
  );
  const aspireInitialReplacementIdentityDigest = String(
    result?.diagnostics?.aspire_exact_identity_digest || "",
  ).trim().toLowerCase();
  const aspireInitialReplacementCanAuthorise = Boolean(
    isAspire
    && result?.status === "ok"
    && result?.phase === "scan"
    && result?.dry_run === true
    && result?.diagnostics?.aspire_scan_complete === true
    && result?.diagnostics?.aspire_identity_reconciliation_complete === true
    && aspireInitialReplacementIdentityCount > ASPIRE_DEFAULT_MAX_NEW_RECORDS
    && /^[0-9a-f]{64}$/.test(aspireInitialReplacementIdentityDigest)
  );

  const issueAspireInitialReplacementAuthorisation = async () => {
    if (!aspireInitialReplacementCanAuthorise) {
      toast.error("Aspire replacement authorisation requires the latest complete exact scan.");
      return null;
    }
    if (!await confirmProductionWriteInApp(
      `Authorise one manual Aspire replacement for exactly ${aspireInitialReplacementIdentityCount} current identities. The authorisation expires after 30 minutes, binds the exact scan digest during preflight, and cannot be used by the scheduler. This does not write competition records.`,
    )) {
      return null;
    }
    setBusy("aspire-initial-replacement-authorisation");
    try {
      const { data } = await api.post(
        "/admin/operator-automation/aspire/initial-replacement-authorisation",
        {
          operator_key: "aspire",
          exact_identity_count: aspireInitialReplacementIdentityCount,
          exact_identity_digest: aspireInitialReplacementIdentityDigest,
          max_authorised_create_count: aspireInitialReplacementIdentityCount,
          confirm_initial_replacement: true,
        },
      );
      const authorisationId = data?.aspire_initial_replacement_authorisation_id;
      const authorisationToken = data?.authorisation_token;
      if (
        data?.status !== "ok"
        || !authorisationId
        || !authorisationToken
      ) {
        throw new Error("Aspire replacement authorisation response was incomplete.");
      }
      setAspireInitialReplacementAuthorisation({
        id: authorisationId,
        token: authorisationToken,
        exactIdentityCount: aspireInitialReplacementIdentityCount,
        exactIdentityDigest: aspireInitialReplacementIdentityDigest,
        expiresAt: data?.aspire_initial_replacement_authorisation_expires_at || null,
      });
      toast.success(
        `Aspire one-time replacement authorised for ${aspireInitialReplacementIdentityCount} exact identities.`,
      );
      return data;
    } catch (error) {
      setAspireInitialReplacementAuthorisation(null);
      toast.error(
        "Aspire replacement authorisation failed: "
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
      return null;
    } finally {
      setBusy(null);
    }
  };

  const runClickExpiry = async ({ dryRun }) => {
    if (!isClick || !clickCurrentLiveSetId) {
      toast.error("Click expiry requires the latest successful exact live-set ID.");
      return;
    }
    if (!dryRun && !clickExpiryApproved) {
      toast.error("Run and pass the exact Click expiry dry-run first.");
      return;
    }
    const expiry = clickExpiryEvidence || {};
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest Click live set: ${clickCurrentLiveSetId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count || 0}\n`
        + `Candidate digest: ${expiry.candidate_id_digest || "missing"}\n`
        + `Expected effective public after: ${expiry.click_expected_effective_public_count_after_expiry ?? "unknown"}\n`
        + `Maximum expiry cap: ${expiry.max_expire_records ?? "unknown"}`
        + "\n\nOnly records in the exact approved Click live-set candidate set may be hidden."
        + "\nNo records will be deleted.",
    )) return;
    if (dryRun) setClickExpiryDryRunResult(null);
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: clickCurrentLiveSetId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) setClickExpiryDryRunResult(null);
  };

  const runDcgExpiry = async ({ dryRun }) => {
    if (!isDcg || !dcgCurrentLiveSetId) {
      toast.error("DCG expiry requires the latest successful exact live-set ID.");
      return;
    }
    if (!dryRun && !dcgExpiryApproved) {
      toast.error("Run and pass the exact DCG expiry dry-run first.");
      return;
    }
    const expiry = dcgExpiryEvidence || {};
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest DCG live set: ${dcgCurrentLiveSetId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count || 0}\n`
        + `Candidate digest: ${expiry.candidate_id_digest || "missing"}\n`
        + `Expected effective public after: ${expiry.dcg_expected_effective_public_count_after_expiry ?? "unknown"}\n`
        + `Maximum expiry cap: ${expiry.max_expire_records ?? "unknown"}`
        + "\n\nOnly prior exact DCG live-set records from the approved candidate set may be hidden."
        + "\nNo records will be deleted.",
    )) return;
    if (dryRun) setDcgExpiryDryRunResult(null);
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: dcgCurrentLiveSetId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) setDcgExpiryDryRunResult(null);
  };

  const runEliteExpiry = async ({ dryRun }) => {
    if (!isElite || !eliteCurrentLiveSetId) {
      toast.error("Elite expiry requires the latest successful exact live-set ID.");
      return;
    }
    if (!dryRun && !eliteExpiryApproved) {
      toast.error("Run and pass the exact Elite expiry dry-run first.");
      return;
    }
    const expiry = eliteExpiryEvidence || {};
    const candidates = expiry.sample_candidates || [];
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest Elite live set: ${eliteCurrentLiveSetId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count || 0}\n`
        + `Candidate digest: ${expiry.candidate_id_digest || "missing"}\n`
        + candidates.map((row) => `${row.id} - ${row.title || "Untitled"}`).join("\n")
        + `\nMaximum expiry cap: ${expiry.max_expire_records ?? "unknown"}`
        + "\n\nOnly prior exact Elite live-set records from the approved candidate set may be hidden."
        + "\nNo current-live record is affected and no record is deleted.",
    )) return;
    if (dryRun) setEliteExpiryDryRunResult(null);
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: eliteCurrentLiveSetId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) setEliteExpiryDryRunResult(null);
  };

  const runRevExpiry = async ({ dryRun }) => {
    if (!isRevComps || !revCurrentLiveSetId) {
      toast.error("Rev expiry requires the latest successful live-set ID.");
      return;
    }
    if (!dryRun && !revExpiryLiveActionReady) {
      toast.error(revExpiryStatusMessage);
      return;
    }
    const expiry = revExpiryDryRunResult?.expiry || revExpiryDryRunResult?.summary?.expiry || {};
    const approvedCandidateIds = revExpiryApprovalStatus?.candidate_ids || [];
    const candidates = expiry.sample_candidates || [];
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest live set: ${revCurrentLiveSetId}\n`
        + `Candidates: ${expiry.candidate_count}\n`
        + candidates.map((row) => `${row.id} - ${row.title || "Untitled"}`).join("\n")
        + `\nPublic before: ${expiry.current_public_rev_count}`
        + `\nExpected after: ${expiry.expected_public_count_after_expiry}`
        + `\nMaximum expiry cap: ${expiry.max_expire_records}`
        + "\n\nNo current-live Rev record will be affected"
        + "\nNo records will be deleted",
    )) return;
    if (dryRun) setRevExpiryDryRunResult(null);
    const completed = await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: revCurrentLiveSetId,
      candidateIds: dryRun ? undefined : approvedCandidateIds,
      expiryApprovalId: dryRun
        ? undefined
        : revExpiryApprovalStatus?.expiry_approval_id,
    });
    if (completed) {
      await refreshRevExpiryApprovalStatus();
    }
  };

  const runBotbExpiry = async ({ dryRun }) => {
    if (!isBotb || !botbCurrentSnapshotId) {
      toast.error("BOTB expiry requires the latest successful catalogue snapshot.");
      return;
    }
    if (!dryRun && !botbExpiryApproved) {
      toast.error("Run and pass the exact BOTB expiry dry-run in this session first.");
      return;
    }
    const expiry = botbExpiryDryRunResult?.expiry
      || botbExpiryDryRunResult?.summary?.expiry
      || {};
    const candidates = expiry.sample_candidates || [];
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest BOTB snapshot: ${botbCurrentSnapshotId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count}\n`
        + candidates.map((row) => `${row.id} - ${row.title || "Untitled"}`).join("\n")
        + `\nPublic before: ${expiry.public_before}`
        + `\nExpected after: ${expiry.expected_public_after}`
        + `\nMaximum expiry cap: ${expiry.max_expire_records}`
        + "\n\nNo current-live BOTB record will be affected"
        + "\nNo records will be deleted",
    )) return;
    if (dryRun) setBotbExpiryDryRunResult(null);
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: botbCurrentSnapshotId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) setBotbExpiryDryRunResult(null);
  };

  const runAspireExpiry = async ({ dryRun }) => {
    if (!isAspire || !aspireCurrentLiveSetId) {
      toast.error("Aspire expiry requires the latest successful exact live-set ID.");
      return;
    }
    if (!dryRun && !aspireExpiryApproved) {
      toast.error("Run and pass the exact Aspire expiry dry-run in this session first.");
      return;
    }
    const expiry = aspireExpiryDryRunResult?.expiry
      || aspireExpiryDryRunResult?.summary?.expiry
      || {};
    const candidates = expiry.sample_aspire_expiry_candidates
      || expiry.sample_candidates
      || [];
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest Aspire live set: ${aspireCurrentLiveSetId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count}\n`
        + candidates.map((row) => `${row.id} - ${row.title || "Untitled"}`).join("\n")
        + `\nMaximum expiry cap: ${expiry.max_expire_records}`
        + "\n\nOnly records from a prior complete Aspire live set may be hidden."
        + "\nNo current-live record is affected and no record is deleted.",
    )) return;
    if (dryRun) setAspireExpiryDryRunResult(null);
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: aspireCurrentLiveSetId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) setAspireExpiryDryRunResult(null);
  };

  const runMckinneyExpiry = async ({ dryRun }) => {
    if (!isMcKinney || !mckinneyCurrentLiveSetId) {
      toast.error("McKinney expiry requires the latest successful exact live-set ID.");
      return;
    }
    if (!dryRun && !mckinneyExpiryApproved) {
      toast.error("Run and pass an active exact McKinney expiry dry-run first.");
      return;
    }
    const expiry = mckinneyExpiryEvidence?.expiry
      || mckinneyExpiryEvidence?.summary?.expiry
      || {};
    const candidates = expiry.sample_mckinney_expiry_candidates
      || expiry.sample_candidates
      || [];
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest McKinney live set: ${mckinneyCurrentLiveSetId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count}\n`
        + candidates.map((row) => `${row.id} - ${row.title || "Untitled"}`).join("\n")
        + "\n\nOnly records from a prior complete McKinney managed set may be hidden."
        + "\nNo current-live record is affected and no record is deleted.",
    )) return;
    if (dryRun) {
      setMckinneyExpiryApprovalIdBeforeDryRun(
        mckinneyPersistedExpiryApproval?.expiry?.expiry_approval_id || null,
      );
      setMckinneyExpiryDryRunResult(null);
    }
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: mckinneyCurrentLiveSetId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) {
      setMckinneyExpiryDryRunResult(null);
      setMckinneyExpiryApprovalIdBeforeDryRun(null);
    }
  };

  const runLlfExpiry = async ({ dryRun }) => {
    if (!isLlf || !llfCurrentLiveSetId) {
      toast.error("LLF expiry requires the latest successful exact live-set ID.");
      return;
    }
    if (!dryRun && !llfExpiryApproved) {
      toast.error("Run and pass an active exact LLF expiry dry-run first.");
      return;
    }
    const expiry = llfExpiryEvidence || {};
    const candidates = expiry.sample_llf_expiry_candidates || [];
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest LLF live set: ${llfCurrentLiveSetId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count || 0}\n`
        + `Candidate digest: ${expiry.candidate_id_digest || "missing"}\n`
        + candidates.map((row) => `${row.record_id} - ${row.title || "Untitled"}`).join("\n")
        + `\nMaximum expiry cap: ${expiry.max_expire_records ?? "unknown"}`
        + "\n\nOnly historical LLF-managed records with same-product explicit closed evidence may be hidden."
        + "\nCatalogue absence or elapsed dates are not expiry evidence. No records will be deleted.",
    )) return;
    if (dryRun) setLlfExpiryDryRunResult(null);
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: llfCurrentLiveSetId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) setLlfExpiryDryRunResult(null);
  };

  const runKiltedExpiry = async ({ dryRun }) => {
    if (!isKilted || !kiltedCurrentLiveSetId) {
      toast.error(`${operatorName} expiry requires the latest successful exact live-set ID.`);
      return;
    }
    if (!dryRun && !kiltedExpiryApproved) {
      toast.error(`Run and pass an active exact ${operatorName} expiry dry-run first.`);
      return;
    }
    const expiry = kiltedExpiryEvidence || {};
    const candidates = expiry[
      `sample_${exactSetSettingsPrefix}_expiry_candidates`
    ] || [];
    if (!dryRun && !await confirmProductionWriteInApp(
      `Latest ${operatorName} live set: ${kiltedCurrentLiveSetId}\n`
        + `Approval: ${expiry.expiry_approval_id}\n`
        + `Candidates: ${expiry.candidate_count || 0}\n`
        + `Candidate digest: ${expiry.candidate_id_digest || "missing"}\n`
        + candidates.map((row) => `${row.record_id} - ${row.title || "Untitled"}`).join("\n")
        + `\nMaximum expiry cap: ${expiry.max_expire_records ?? "unknown"}`
        + `\n\nOnly historical ${operatorName}-managed records with same-product explicit closed evidence may be hidden.`
        + "\nCatalogue absence or elapsed dates are not expiry evidence. No records will be deleted.",
    )) return;
    if (dryRun) setKiltedExpiryDryRunResult(null);
    await runAutomation({
      dryRun,
      phase: "expire",
      expectedLiveSetId: kiltedCurrentLiveSetId,
      candidateIds: dryRun ? undefined : expiry.candidate_ids,
      expiryApprovalId: dryRun ? undefined : expiry.expiry_approval_id,
    });
    if (!dryRun) setKiltedExpiryDryRunResult(null);
  };

  const runBotbIdentityAudit = async () => {
    if (!isBotb) return;
    setBusy("botb-identity-audit");
    try {
      const { data } = await api.get("/admin/operator-automation/botb/identity-audit");
      setBotbIdentityAuditResult(data);
      toast.success(
        `BOTB identity audit: ${data.botb_snapshot_matched_public_record_count || 0} matched, `
          + `${data.botb_identity_conflict_count || 0} conflicts.`,
      );
    } catch (error) {
      setBotbIdentityAuditResult(null);
      toast.error(
        "BOTB identity audit failed: "
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const cancelBotbExpiryApproval = async () => {
    if (!isBotb) return;
    const expiry = botbExpiryDryRunResult?.expiry
      || botbExpiryDryRunResult?.summary?.expiry
      || {};
    if (!botbCurrentSnapshotId || !expiry.expiry_approval_id) {
      setBotbExpiryDryRunResult(null);
      return;
    }
    setBusy("botb-expiry-cancel");
    try {
      const { data } = await api.post(
        "/admin/operator-automation/botb/expiry-approval/cancel",
        {
          expected_snapshot_id: botbCurrentSnapshotId,
          expiry_approval_id: expiry.expiry_approval_id,
        },
      );
      if (data.status !== "ok" || data.cancelled !== true) {
        throw new Error((data.blocked_reasons || []).join(", ") || "approval cancellation blocked");
      }
      setBotbExpiryDryRunResult(null);
      toast.success("BOTB expiry approval cancelled. No records were changed.");
    } catch (error) {
      toast.error(
        "BOTB expiry approval could not be cancelled: "
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runAspireManualImport = async () => {
    if (!isAspire) return;
    const inputType = aspireManualImport.inputType === "har" ? "har" : "routes";
    const maxDetails = parseBatchNumber(aspireManualImport.maxDetails, {
      fallback: ASPIRE_MANUAL_IMPORT_MAX_DETAILS,
      min: 1,
      max: ASPIRE_MANUAL_IMPORT_MAX_DETAILS,
    });
    const text = String(aspireManualImport.text || "").trim();
    const payload = {
      input_type: inputType,
      dry_run: aspireManualImport.dryRun !== false,
      max_details: maxDetails,
    };
    try {
      if (inputType === "har") {
        payload.har = text ? JSON.parse(text) : {};
      } else if (text.startsWith("[")) {
        const parsed = JSON.parse(text);
        payload.routes = Array.isArray(parsed) ? parsed.map((item) => String(item || "").trim()).filter(Boolean) : [];
      } else {
        payload.routes = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
      }
    } catch (error) {
      toast.error(`Aspire manual import input could not be parsed: ${error.message}`);
      return;
    }
    if (inputType === "routes" && (!payload.routes || payload.routes.length === 0)) {
      toast.error("Add at least one Aspire product route before running the import.");
      return;
    }
    if (inputType === "har" && (!payload.har || Object.keys(payload.har).length === 0)) {
      toast.error("Paste a HAR JSON object before running the import.");
      return;
    }
    setBusy("aspire-import");
    try {
      const { data } = await api.post("/admin/operator-automation/aspire/import-routes", payload);
      setResult(data);
      const summary = data.summary || {};
      toast.success(
        `Aspire manual import: ${summary.aspire_manual_hints_would_create_count || summary.aspire_manual_hints_created_count || 0} new hints, ${summary.aspire_manual_hints_would_update_count || summary.aspire_manual_hints_updated_count || 0} updates.`,
      );
      onRun?.(data);
    } catch (error) {
      toast.error(
        "Aspire manual import failed: "
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runRevPublicImageAudit = async () => {
    if (!isRevComps) return;
    setRevCleanupDryRunResult(null);
    setRevCleanupLiveResult(null);
    const liveSetId = String(settings?.latest_successful_rev_live_set_id || "").trim();
    if (!liveSetId) {
      toast.error("Rev public image audit requires a latest successful live-set ID.");
      return;
    }
    setBusy("rev-image-audit");
    try {
      const { data } = await api.get(
        "/admin/operator-automation/revcomps/public-image-audit",
        { params: { expected_live_set_id: liveSetId } },
      );
      const auditResult = {
        ...data,
        phase: "public_image_audit",
        diagnostics: data,
        summary: data,
        settings,
      };
      setResult(auditResult);
      toast.success(
        `Rev public image audit: ${data.rev_public_image_server_failure_count || 0} server failures, ${data.rev_public_image_out_of_scope_public_count || 0} outside the live set.`,
      );
      onRun?.(auditResult);
    } catch (error) {
      toast.error(
        "Rev public image audit failed: "
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runRevBrokenTileCleanup = async ({ dryRun }) => {
    if (!isRevComps || !revCleanupLiveSetMatchesSettings) {
      toast.error("Rev broken-tile cleanup is locked because the expected live set is not current.");
      return;
    }
    if (!dryRun && !revCleanupDryRunSafe) {
      toast.error("Run and pass the exact two-record cleanup dry-run first.");
      return;
    }
    if (
      !dryRun
      && !await confirmProductionWriteInApp(
        `Live set: ${REV_BROKEN_TILE_CLEANUP_LIVE_SET_ID}\n\n`
          + REV_BROKEN_TILE_CLEANUP_RECORDS.map((record) => `${record.title}\n${record.id}`).join("\n\n")
          + "\n\nThis hides only these two out-of-scope Rev records."
          + "\nNo broad expiry will run."
          + "\nScheduler remains disabled.",
      )
    ) {
      return;
    }
    setBusy(dryRun ? "rev-cleanup-dry-run" : "rev-cleanup-live");
    setRevCleanupDryRunResult(null);
    setRevCleanupLiveResult(null);
    try {
      const { data } = await api.post(
        "/admin/operator-automation/revcomps/public-image-cleanup",
        buildRevBrokenTileCleanupPayload(dryRun),
      );
      const cleanupResult = {
        ...data,
        phase: dryRun ? "public_image_cleanup_dry_run" : "public_image_cleanup_live",
        diagnostics: data,
        summary: data,
        settings,
      };
      setResult(cleanupResult);
      if (dryRun) {
        setRevCleanupDryRunResult(data);
        if (isSafeRevBrokenTileCleanupDryRun(data)) {
          toast.success("Rev cleanup dry-run passed: exactly 2 safe hide decisions confirmed.");
        } else {
          toast.error("Rev cleanup dry-run did not prove exactly 2 safe hide decisions. Live cleanup remains locked.");
        }
      } else {
        setRevCleanupLiveResult(data);
        if (isSuccessfulRevBrokenTileCleanupLiveResult(data)) {
          toast.success("Rev cleanup complete: 2 exact records hidden. Hard-refresh the public Rev page to confirm the broken tiles are gone.");
        } else {
          toast.error("Rev cleanup response did not reconcile safely. Do not rerun without a new audit and dry-run.");
        }
      }
      onRun?.(cleanupResult);
    } catch (error) {
      setRevCleanupDryRunResult(null);
      if (!dryRun) setRevCleanupLiveResult(null);
      toast.error(
        `Rev cleanup ${dryRun ? "dry-run" : "live run"} failed: `
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const summary = result?.summary || settings?.last_run_summary || null;
  const displayedPhase = result?.phase || summary?.phase || settings?.last_run_phase;
  const displayedDryRun = result?.dry_run ?? summary?.dry_run ?? false;
  const revPublishSafeValue = result?.diagnostics?.rev_publish_safe_count
    ?? summary?.rev_publish_safe_count;
  const revPublishSafeCount = Number.isInteger(Number(revPublishSafeValue))
    ? Number(revPublishSafeValue)
    : null;
  const revPublishGate = revPublishGateState({
    settings,
    resultSettings: result?.settings,
    executionLeaseStatus,
    busy: busy != null,
  });
  const verification = result?.verification || summary?.verification || null;
  const publication = result?.publication || summary?.publication || null;
  const expiry = result?.expiry || summary?.expiry || null;
  const expiryWouldRows = expiry?.dry_run
    ? (expiry?.sample_would_expire || expiry?.sample_expired || [])
    : (expiry?.sample_hidden || expiry?.sample_expired || []);
  const expirySkippedRows = expiry?.sample_skipped || expiry?.sample_blocked || [];
  const expiryReasonBreakdown = expiry?.reason_breakdown || {};
  const expiryBlockedReasons = Array.isArray(expiry?.blocked_reasons)
    ? expiry.blocked_reasons
    : [];

  return (
    <div
      className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.035] p-4 space-y-4"
      data-testid="operator-automation-run-card"
    >
      <AlertDialog
        open={productionConfirmation != null}
        onOpenChange={(open) => {
          if (!open && productionConfirmationResolver.current) {
            settleProductionConfirmation(false);
          }
        }}
      >
        <AlertDialogContent data-testid="operator-automation-production-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm production run</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="whitespace-pre-wrap text-white/80">
                  {productionConfirmation?.message}
                </p>
                <p>
                  This will modify the connected production database (
                  {productionConfirmation?.databaseName || "production"}).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              type="button"
              onClick={() => settleProductionConfirmation(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={() => settleProductionConfirmation(true)}
              data-testid="operator-automation-confirm-production-run"
            >
              Confirm production run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-100">
            <ShieldCheck size={17} weight="duotone" />
            Run Operator Automation
          </div>
          <p className="text-[11px] text-white/60 mt-1 max-w-2xl leading-relaxed">
            {isRevComps
              ? `${operatorName} scan creates a six-hour immutable full-catalogue snapshot. Apply uses that exact displayed snapshot without fresh discovery; verify-only and publish-only operate on the resulting exact live set. Expiry dry-run reports would-hide candidates without changing records. Expiry and scheduling remain settings-gated.`
              : isDcg
              ? `${operatorName} automation now runs through DB-backed operator settings. Apply uses the existing DCG V2 refresh/apply code, while verify, publish and expire are isolated phases controlled by their own flags.`
              : isBotb
            ? "BOTB uses an immutable exact-catalogue Scan, snapshot-bound Apply, exact-live-set Verify and atomic Publish. Dream Car / Spot the Ball competitions remain honest no-standard-odds records; odds-compatible records still require strict BOTB evidence. Expiry and scheduling are disabled by default and separately gated."
            : isSevenDays
            ? "7days is wired for admin dry-run diagnostics, draft apply, verify-only, publish-only, guarded expiry and scheduled maintenance when enabled. Expiry hides only records confirmed closed, sold or past by current 7days API detail evidence."
            : isClick
            ? "Click Competitions is wired for admin dry-run diagnostics, apply-only draft ingestion, verify-only promotion, publish-only for verified odds-safe drafts, and guarded expiry when enabled. Expiry hides only records confirmed closed or past-draw by current Click detail evidence; scheduler stays disabled until manually enabled."
            : isJonnyFromDonny
            ? "Jonny From Donny feasibility scan is scan only. It fetches public homepage/sitemap/product evidence for diagnostics without creating records, enabling public exposure, or making anything Budget Odds eligible."
            : isFeelGood
            ? "Feel Good Competitions feasibility scan is scan only. It fetches public listing/detail evidence for diagnostics without creating records, enabling public exposure, or making anything Budget Odds eligible."
            : isAspire
            ? "Aspire uses bounded browser-network discovery, immutable preflight/apply snapshots, and separate exact-set verify/publish actions. Expiry and Scheduler capabilities are disabled by default; degraded or incomplete scans cannot advance the lifecycle."
            : `${operatorName} automation builds a fresh scan and applies through the settings-controlled path. Apply creates or updates review records first; verify, publish and expire only run when separately enabled.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider">
          <Flag label="Scan" enabled={scanEnabled} />
          <Flag label="Dry-run" enabled={dryRunEnabled} />
          <Flag label="Apply" enabled={applyEnabled} danger />
          <Flag label="Verify" enabled={verifyEnabled} />
          <Flag label="Publish" enabled={publishEnabled} danger />
          <Flag label="Expire" enabled={expireEnabled} danger />
          <Flag label="Scheduler" enabled={schedulerEnabled} danger />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {isRevComps && revManifestStatus?.incident_c0a_successor_capability_version === REV_C0A_VERSION && (
          <RevC0aSuccessorRecoveryCard settings={settings} busy={busy} setBusy={setBusy} onRun={onRun} />
        )}
        {isBotb && <BotbConfidoCampaignsCard />}
        {isRevComps && <RevSourceAbortRecovery settings={settings} busy={busy} setBusy={setBusy}
          scanEnabled={scanEnabled && dryRunEnabled}
          onDisposition={(data) => onRun?.(data)}
          onScanResult={(data) => { setResult(data); onRun?.(data); }} />}
        {isRevComps && revManifestStatus?.capture_supported === true
          && revManifestStatus?.workflow_version === "rev_source_manifest_v1" && (
          <section className="w-full rounded border border-sky-400/20 p-3 text-xs space-y-2"
            data-testid="rev-source-manifest-panel">
            <p>Capture the full Rev source catalogue for review. This does not create, verify or publish competitions.</p>
            <button type="button" onClick={captureRevManifest}
              disabled={busy != null || !scanEnabled || !dryRunEnabled || revManifestUncertain
                || revManifestStatus.pending_readback_required || executionLeaseStatus?.active === true}
              className="px-3 py-2 rounded bg-sky-500/20 disabled:opacity-45"
              data-testid="rev-source-manifest-capture">
              {busy === "rev-source-capture" ? "Capturing source catalogue..." : "Capture full source catalogue"}
            </button>
            <button type="button" onClick={inspectRevManifest} disabled={busy != null}
              className="ml-2 px-3 py-2 rounded bg-white/10 disabled:opacity-45"
              data-testid="rev-source-manifest-inspect">Inspect this capture</button>
            {revManifestError && <p role="status">{revManifestError}</p>}
            {(revManifestUncertain || revManifestStatus.pending_readback_required) && (
              <p>Capture is held until its existing operation and cleanup are confirmed.</p>
            )}
            {revManifestResult?.intent_id && <p>Capture: {revManifestResult.intent_id}</p>}
            {revManifestStatus.source_manifest && (
              <div data-testid="rev-source-manifest-result">
                {!revManifestResult?.intent_id && <p>Previously captured evidence. It does not identify an uncertain new capture.</p>}
                <p>Source URLs: {revManifestStatus.source_manifest.unique_url_count ?? "Unknown"}. Resolved product identities: {revManifestStatus.source_manifest.resolved_product_identity_count ?? "Unknown"}.</p>
                <p>Membership: {revManifestStatus.source_manifest.manifest_complete === true ? "Complete" : "Incomplete"}. Detail eligibility has not been checked. Created 0; verified 0; published 0.</p>
                <p>Evidence hash: {revManifestStatus.source_manifest.manifest_hash}. Expires: {revManifestStatus.source_manifest.source_expires_at}.</p>
              </div>
            )}
          </section>
        )}

        {isRevComps && revManifestStatus?.checkpoint_capability_version === "rev_create_only_checkpoint_v1" && (
          <section className="w-full rounded border border-sky-400/20 p-3 text-xs space-y-2" data-testid="rev-checkpoint-panel">
            <p>Rev create-only checkpoint. Full source detail coverage is required before any drafts are created. Existing competitions are carried unchanged.</p>
            <button type="button" onClick={inspectRevCheckpoint} disabled={busy !== null} data-testid="rev-checkpoint-inspect">Inspect checkpoint and activation preview</button>
            {revCheckpointInfo?.checkpoint && <div>
              <p>Checkpoint: {revCheckpointInfo.checkpoint._id}</p>
              <p>Source hash: {revCheckpointInfo.checkpoint.manifest_hash}</p>
              <p>State: {revCheckpointInfo.checkpoint.state}. Original source expiry: {revCheckpointInfo.checkpoint.source_expires_at}</p>
              <p>Full source identities: {revCheckpointInfo.checkpoint.source_identities?.length ?? "Unknown"}. Detail attempts are limited to 250 and creates to the lower of 205 or the current authorised cap for each real execution.</p>
              <p>Eligible: {revCheckpointInfo.checkpoint.partitions?.eligible?.length ?? "Not sealed"}. Review-held: {revCheckpointInfo.checkpoint.partitions?.review_held?.length ?? "Not sealed"}.</p>
            </div>}
            {revCheckpointInfo?.activation_preview && <label>
              <input type="checkbox" checked={revDrainConfirmed} onChange={(event) => setRevDrainConfirmed(event.target.checked)} data-testid="rev-checkpoint-drain-confirm" />
              I have matching serving-deployment/runtime evidence and have reconciled and drained the old writers. The preview alone does not prove this.
            </label>}
            {revCheckpointInfo?.activation_blocked_reason && <p>{revCheckpointInfo.activation_blocked_reason}</p>}
            {["scan", "preflight", "apply", "verify", "publish"].map((phase) => (
              <button key={phase} type="button" onClick={() => runRevCheckpoint(`checkpoint_${phase}`)}
                data-testid={`rev-checkpoint-${phase}`}
                disabled={busy !== null || !revCheckpointInfo?.supported || revCheckpointUncertain
                  || revCheckpointInfo.pending_readback_required
                  || (!revCheckpointInfo.checkpoint && (phase !== "scan" || !revDrainConfirmed || !revCheckpointInfo.activation_preview))
                  || (phase === "scan" && (!scanEnabled || !dryRunEnabled
                    || (revCheckpointInfo.activation_preview?.manifest_hash !== revCheckpointInfo.checkpoint?.manifest_hash
                      && revCheckpointInfo.activation_preview && !revDrainConfirmed)))
                  || (phase === "apply" && !settings?.apply_enabled)
                  || (phase === "apply" && revCheckpointInfo.checkpoint?.state !== "batch_admitted")
                  || (phase === "verify" && !settings?.verify_enabled)
                  || (phase === "verify" && revCheckpointInfo.checkpoint?.state !== "drafted")
                  || (phase === "publish" && (!settings?.publish_enabled || revCheckpointInfo.checkpoint?.state !== "verified"))}>
                {phase === "scan" ? "Read next owned detail batch" : phase === "preflight" ? "Preflight create-only batch" : phase === "apply" ? "Create eligible drafts" : phase === "verify" ? "Verify exact draft revisions" : "Publish verified additions"}
              </button>))}
            {revCheckpointUncertain && <p>Held for the same durable operation. An earlier success cannot reconcile this request.</p>}
            {revCheckpointInfo?.intent?.pending === true && revCheckpointOperation.current?.intentId
              && revCheckpointInfo.intent.operation_request_id === revCheckpointOperation.current.requestId
              && ["checkpoint_apply", "checkpoint_verify", "checkpoint_publish"].includes(revCheckpointInfo.intent.phase) && (
              <button type="button" disabled={busy !== null} onClick={() => runRevCheckpoint("checkpoint_preflight", true)} data-testid="rev-checkpoint-reconcile">Reconcile this exact committed operation</button>)}
            {revCheckpointError && <p role="status">{revCheckpointError}</p>}
            {revCheckpointInfo?.last_result && <p>Last operation: {revCheckpointInfo.last_result.intent_id}. Created {revCheckpointInfo.last_result.created ?? 0}; verified {revCheckpointInfo.last_result.verified ?? 0}; published {revCheckpointInfo.last_result.published ?? 0}.</p>}
          </section>
        )}

        {isRevComps && (
          <button
            type="button"
            onClick={runRevPublicImageAudit}
            disabled={busy != null || !settings?.latest_successful_rev_live_set_id}
            data-testid="operator-automation-revcomps-public-image-audit-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-45"
          >
            <ArrowsClockwise size={13} weight="bold" />
            {busy === "rev-image-audit" ? "Auditing Rev images..." : "Audit Rev public image failures"}
          </button>
        )}
        {isRevComps && (
          <div
            className="w-full rounded border border-white/10 bg-black/10 p-3"
            data-testid="rev-detail-batch-controls"
          >
            <div className="text-[10px] uppercase tracking-wide text-white/45 mb-2">
              Advanced diagnostics / manual recovery
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <label className="text-[10px] text-white/55">
                Detail fetch limit override
                <input
                  type="number"
                  min="1"
                  max={MAX_DETAIL_FETCH_LIMIT}
                  value={revBatch.detailFetchLimit}
                  onChange={(event) => setRevBatch((current) => ({
                    ...current,
                    detailFetchLimit: event.target.value,
                  }))}
                  className="mt-1 w-full rounded border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white"
                  data-testid="rev-detail-fetch-limit"
                />
              </label>
              <label className="text-[10px] text-white/55">
                Detail fetch offset
                <input
                  type="number"
                  min="0"
                  max={MAX_DETAIL_FETCH_OFFSET}
                  value={revBatch.detailFetchOffset}
                  onChange={(event) => setRevBatch((current) => ({
                    ...current,
                    detailFetchOffset: event.target.value,
                  }))}
                  className="mt-1 w-full rounded border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white"
                  data-testid="rev-detail-fetch-offset"
                />
              </label>
            </div>
          </div>
        )}
        {isRevComps && (
          <div
            className={`w-full rounded border p-3 space-y-1 text-[11px] ${
              revApplySnapshotReady
                ? "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100"
                : "border-amber-400/25 bg-amber-400/[0.06] text-amber-100"
            }`}
            data-testid="operator-automation-revcomps-snapshot-status"
          >
            <div className="font-semibold">
              {revApplySnapshotReady
                ? "Ready: Apply uses this exact immutable scan"
                : "Apply locked: run a complete Rev scan with these controls"}
            </div>
            <div className="break-all">
              Snapshot ID: {revScanSnapshot?.id || "missing"}
            </div>
            <div className="break-all">
              SHA-256: {revScanSnapshot?.hash || "missing"}
            </div>
            <div>
              Exact identities: {revScanSnapshot?.exactIdentityCount ?? 0};
              candidates: {revScanSnapshot?.candidateCount ?? 0};
              trusted images: {revScanSnapshot?.trustedImageCount ?? 0};
              trusted caps: {revScanSnapshot?.trustedMaxTicketsCount ?? 0}
            </div>
            <div>
              State: {revScanSnapshot?.state || "missing"};
              expires: {revScanSnapshot?.expiresAt || "not available"}
            </div>
            <div>
              Bound controls: max details {revSnapshotConfig.validated_max_details ?? "missing"},
              offset {revSnapshotConfig.detail_fetch_offset ?? "missing"}.
              Changing either control requires a new scan.
            </div>
          </div>
        )}
        {isRevComps && (
          <div
            className="w-full rounded border border-amber-400/25 bg-amber-400/[0.06] p-3 space-y-3"
            data-testid="rev-broken-tile-cleanup-controls"
          >
            <div>
              <div className="text-xs font-semibold text-amber-100">
                Controlled cleanup of 2 broken Rev tiles
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-white/60">
                Exact-record cleanup only. Review the dry-run decisions before the live action unlocks.
                This does not run Rev expiry, scheduling, publication or deletion.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => runRevBrokenTileCleanup({ dryRun: true })}
                disabled={busy != null || !revCleanupLiveSetMatchesSettings}
                data-testid="operator-automation-revcomps-broken-tile-cleanup-dry-run-btn"
                className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-100 border border-amber-400/25 disabled:opacity-45"
              >
                <ArrowsClockwise size={13} weight="bold" />
                {busy === "rev-cleanup-dry-run"
                  ? "Checking 2 broken Rev tiles..."
                  : "Dry-run cleanup of 2 broken Rev tiles"}
              </button>
              <button
                type="button"
                onClick={() => runRevBrokenTileCleanup({ dryRun: false })}
                disabled={busy != null || !revCleanupLiveSetMatchesSettings || !revCleanupDryRunSafe}
                data-testid="operator-automation-revcomps-broken-tile-cleanup-live-btn"
                className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-100 border border-red-400/30 disabled:opacity-45"
              >
                <ShieldCheck size={13} weight="bold" />
                {busy === "rev-cleanup-live"
                  ? "Hiding exactly 2 Rev tiles..."
                  : "Hide 2 confirmed broken legacy tiles"}
              </button>
            </div>
            <div
              className={`text-[11px] ${revCleanupDryRunSafe ? "text-emerald-200" : "text-white/50"}`}
              data-testid="rev-broken-tile-cleanup-gate-status"
            >
              {!revCleanupLiveSetMatchesSettings
                ? "Locked: the current Rev live-set ID does not match this exact cleanup."
                : revCleanupLiveSucceeded
                ? "Cleanup complete. Run a new audit and dry-run before any further action."
                : revCleanupDryRunSafe
                ? "SAFE TO CLEAN: exactly 2 safe out-of-scope hide decisions. Live cleanup is unlocked for this component session."
                : "Live cleanup locked until the exact two-record dry-run passes."}
            </div>
            {revCleanupDryRunResult && (
              <div className="space-y-2 text-[11px] text-white/65" data-testid="rev-broken-tile-cleanup-review">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <span>Public: {revCleanupDryRunResult.rev_public_image_public_count}</span>
                  <span>Current live: {revCleanupDryRunResult.rev_public_image_current_live_public_count}</span>
                  <span>Outside: {revCleanupDryRunResult.rev_public_image_out_of_scope_public_count}</span>
                  <span>Checked: {revCleanupDryRunResult.rev_public_image_checked_count}</span>
                  <span>Server failures: {revCleanupDryRunResult.rev_public_image_server_failure_count}</span>
                  <span>Hide decisions: {revCleanupDryRunResult.rev_public_image_explicit_out_of_scope_hide_count}</span>
                  <span>Missing: {(revCleanupDryRunResult.missing_record_ids || []).length}</span>
                  <span>Live set: {revCleanupDryRunResult.expected_live_set_id}</span>
                </div>
                <div className={revCleanupDryRunSafe ? "text-emerald-200" : "text-red-200"}>
                  {revCleanupDryRunSafe ? "SAFE TO CLEAN" : "BLOCKED"}
                </div>
                {!revCleanupDryRunSafe && (
                  <ul data-testid="rev-broken-tile-cleanup-gate-failures">
                    {revCleanupGateFailures.map((failure) => <li key={failure}>{failure}</li>)}
                  </ul>
                )}
                {(revCleanupDryRunResult.sample_rev_public_image_cleanup_decisions || []).map((row) => (
                  <div key={row.id} className="rounded border border-white/10 p-2 break-words">
                    <div className="text-white/85">{row.title || "Untitled Rev record"}</div>
                    <div>ID: {row.id}</div>
                    <div>Source URL: {row.source_competition_url || "missing"}</div>
                    <div>Image URL: {row.image_url || "missing"}</div>
                    <div>HTTP status: {row.image_check?.status_code ?? "unknown"}</div>
                    <div>Action: {row.action || "none"}</div>
                    <div>Reason: {row.decision_reason || "missing"}</div>
                  </div>
                ))}
              </div>
            )}
            {revCleanupLiveResult && (
              <div className={`rounded border p-2 text-[11px] ${revCleanupLiveSucceeded ? "border-emerald-400/30 text-emerald-100" : "border-red-400/30 text-red-100"}`} data-testid="rev-broken-tile-cleanup-live-result">
                <div>Status: {revCleanupLiveResult.status}</div>
                <div>Hidden: {revCleanupLiveResult.hidden_count || 0}</div>
                <div>Explicit hide decisions: {revCleanupLiveResult.rev_public_image_explicit_out_of_scope_hide_count || 0}</div>
                <div>Errors: {revCleanupLiveResult.errors || 0}</div>
                <div>Public before/after: {revCleanupLiveResult.rev_public_image_public_count_before} / {revCleanupLiveResult.rev_public_image_public_count_after}</div>
                <div>Current-live before/after: {revCleanupLiveResult.rev_public_image_current_live_public_count_before} / {revCleanupLiveResult.rev_public_image_current_live_public_count_after}</div>
                <div>Outside before/after: {revCleanupLiveResult.rev_public_image_out_of_scope_public_count_before} / {revCleanupLiveResult.rev_public_image_out_of_scope_public_count_after}</div>
                <div>Affected IDs: {(revCleanupLiveResult.affected_record_ids || []).join(", ") || "none"}</div>
                <div>Scheduler enabled: {String(revCleanupLiveResult.scheduler_enabled)}</div>
                <div>Expiry enabled: {String(revCleanupLiveResult.expire_enabled)}</div>
                {revCleanupLiveSucceeded && <div className="mt-1 font-semibold">Success. Hard-refresh the public Rev page.</div>}
              </div>
            )}
          </div>
        )}
        {isClick && (
          <div className="space-y-1 text-xs">
            <label htmlFor="click-next-scan-max-details" className="block text-white/80">
              Next Click scan detail limit
            </label>
            <input
              id="click-next-scan-max-details"
              type="text"
              inputMode="numeric"
              pattern="[0-9]+"
              value={clickScanMaxDetails}
              onChange={(event) => setClickScanMaxDetails(event.target.value)}
              disabled={busy != null}
              aria-invalid={clickScanMaxDetailsValue == null}
              aria-describedby="click-next-scan-max-details-note"
              data-testid="operator-automation-click-scan-max-details"
              className="w-28 rounded border border-white/20 bg-white/5 px-2 py-1 text-white"
            />
            <p id="click-next-scan-max-details-note" className="max-w-sm text-white/60">
              1–250, for the next scan only. Saved settings and the selected
              immutable snapshot stay unchanged.
            </p>
            {clickScanMaxDetailsValue == null && (
              <p role="alert" className="text-red-200">
                Enter a whole number from 1 to 250 before starting a Click scan.
              </p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => runAutomation({
            dryRun: true,
            phase: (isDcg || isElite || isRevComps || isBotb || isSevenDays || isClick || isJonnyFromDonny || isFeelGood || isAspire || isMcKinney || isLlf || isKilted) ? "scan" : "auto",
            automatedNetworkDiscovery: isAspire,
          })}
          disabled={busy != null || !scanPreviewEnabled || (isClick && clickScanMaxDetailsValue == null)}
          data-testid={`operator-automation-${operatorKey}-scan-btn`}
          className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-45"
        >
          <ArrowsClockwise size={13} weight="bold" />
          {busy === "scan"
            ? "Running scan..."
            : isBotb
            ? "Run BOTB scan-only diagnostics"
            : isSevenDays
            ? "Run 7days dry-run scan"
            : isClick
            ? "Run Click dry-run scan"
            : isJonnyFromDonny
            ? "Run Jonny From Donny feasibility scan"
            : isFeelGood
            ? "Run Feel Good feasibility scan"
            : isAspire
            ? "Run Aspire feasibility scan"
            : isMcKinney
            ? "Run McKinney GBP and mechanics scan"
            : isLlf
            ? "Run LLF exact-catalogue scan"
            : isKilted
            ? `Run ${operatorName} exact-catalogue scan`
            : `Run ${operatorName} scan-only automation`}
        </button>
        {isDcg && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: true,
              phase: "apply_preflight",
            })}
            disabled={busy != null || !dcgApplySnapshotReady}
            data-testid="operator-automation-dcg-apply-preflight-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "dcg-preflight"
              ? "Reconciling DCG write set..."
              : "Preflight DCG exact write set: no competition writes"}
          </button>
        )}
        {!isJonnyFromDonny && !isFeelGood && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: false,
              phase: (isSequencedTrustedOperator || isBotb || isSevenDays || isClick || isAspire || isMcKinney || isLlf || isKilted) ? "apply" : "auto",
              automatedNetworkDiscovery: isAspire,
            })}
            disabled={
              busy != null
              || !scanEnabled
              || !applyEnabled
              || (isDcg && !dcgApplyPreflightReady)
              || (isElite && !eliteApplySnapshotReady)
              || (isBotb && !botbApplySnapshotReady)
              || (isClick && !clickApplyPreflightReady)
              || (isSevenDays && !sevenDaysApplySnapshotReady)
              || (isAspire && !aspireApplySnapshotReady)
              || (isMcKinney && !mckinneyApplyPreflightReady)
              || (isLlf && !llfApplyPreflightReady)
              || (isKilted && !kiltedApplyPreflightReady)
              || (isRevComps && !revApplySnapshotReady)
            }
            data-testid={`operator-automation-${operatorKey}-apply-btn`}
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border border-emerald-500/30 disabled:opacity-45"
          >
            <CheckCircle size={13} weight="bold" />
            {busy === "apply"
              ? "Applying..."
              : applyButtonLabel}
          </button>
        )}
        {isBotb && (
          <button
            type="button"
            onClick={runBotbIdentityAudit}
            disabled={busy != null || !botbCurrentSnapshotId}
            data-testid="operator-automation-botb-identity-audit-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-45"
          >
            <ArrowsClockwise size={13} weight="bold" />
            {busy === "botb-identity-audit"
              ? "Auditing BOTB identities..."
              : "Audit exact BOTB identity reconciliation"}
          </button>
        )}
        {isSevenDays && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid="operator-automation-7days-dry-run-note"
          >
            7days supports dry-run scan, draft apply, verify-only, publish-only and guarded expiry. Verify
            promotes existing draft records only; publish promotes verified
            drafts only. Expiry uses current API detail evidence and Scheduler runs only when explicitly enabled.
          </span>
        )}
        {isClick && (
          <>
            <button
              type="button"
              onClick={() => runAutomation({
                dryRun: true,
                phase: "apply_preflight",
              })}
              disabled={busy != null || !clickApplySnapshotReady}
              data-testid="operator-automation-click-apply-preflight-btn"
              className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
            >
              <ShieldCheck size={13} weight="bold" />
              {busy === "click-preflight"
                ? "Checking Click write set..."
                : "Preflight Click exact public set: no writes"}
            </button>
            <span
              className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
              data-testid="operator-automation-click-dry-run-note"
            >
              Click Apply persists the exact public-safe set as drafts. Standard
              odds-ready records and awareness-only records share lifecycle
              membership; awareness-only records remain excluded from Budget Odds.
            </span>
          </>
        )}
        {isJonnyFromDonny && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid="operator-automation-jonnyfromdonny-dry-run-note"
          >
            Jonny From Donny feasibility scan is scan only - no records are
            created. It is not trusted automation yet, and Budget Odds remains
            disabled until extraction evidence is verified.
          </span>
        )}
        {isFeelGood && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid="operator-automation-feelgood-dry-run-note"
          >
            Feel Good Competitions feasibility scan is scan only - no records are
            created. It is not trusted automation yet, and Budget Odds remains
            disabled until extraction evidence is verified.
          </span>
        )}
        {isClick && (
          <div
            className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1"
            data-testid="operator-automation-click-expiry-contract"
          >
            <div>
              Click Scheduler: {schedulerEnabled ? "enabled" : "disabled"};
              {" "}Expiry: {expireEnabled ? "enabled" : "disabled"}.
            </div>
            <div>Latest exact live set: {clickCurrentLiveSetId || "missing"}</div>
            <div>
              Exact expiry approval: {clickExpiryApproved ? "PASS" : "BLOCKED"}.
              Live expiry requires the active server-owned live-set ID,
              candidate IDs and approval ID.
            </div>
            {clickExpiryEvidence && (
              <div>
                Candidates: {clickExpiryEvidence.candidate_count ?? 0};
                {" "}approval: {clickExpiryEvidence.expiry_approval_id || "missing"};
                {" "}expected public after: {clickExpiryEvidence.click_expected_effective_public_count_after_expiry ?? "unknown"}.
              </div>
            )}
          </div>
        )}
        {isAspire && (
          <>
            {aspireInitialReplacementCanAuthorise && (
              <button
                type="button"
                onClick={issueAspireInitialReplacementAuthorisation}
                disabled={busy != null || Boolean(aspireInitialReplacementAuthorisation)}
                data-testid="operator-automation-aspire-initial-replacement-authorise-btn"
                className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/30 disabled:opacity-45"
              >
                <ShieldCheck size={13} weight="bold" />
                {busy === "aspire-initial-replacement-authorisation"
                  ? "Authorising exact Aspire replacement..."
                  : `Authorise one-time Aspire replacement (${aspireInitialReplacementIdentityCount} exact identities)`}
              </button>
            )}
            <button
              type="button"
              onClick={() => runAutomation({
                dryRun: true,
                phase: "apply_preflight",
                automatedNetworkDiscovery: true,
              })}
              disabled={
                busy != null
                || !scanPreviewEnabled
                || (
                  aspireInitialReplacementCanAuthorise
                  && !aspireInitialReplacementAuthorisation
                )
              }
              data-testid="operator-automation-aspire-apply-preflight-btn"
              className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
            >
              <ShieldCheck size={13} weight="bold" />
              {busy === "aspire-preflight"
                ? "Reconciling Aspire records..."
                : "Preflight Aspire full-catalogue apply: no competition writes"}
            </button>
            {(aspireInitialReplacementCanAuthorise || aspireInitialReplacementAuthorisation) && (
              <span
                className="px-3 py-2 rounded border border-amber-400/25 bg-amber-400/[0.08] text-[11px] text-amber-100"
                data-testid="operator-automation-aspire-initial-replacement-status"
              >
                {aspireInitialReplacementAuthorisation
                  ? `One-time authorisation ready for ${aspireInitialReplacementAuthorisation.exactIdentityCount} exact identities. It remains in this admin session only and will be bound to the immutable preflight snapshot.`
                  : `The exact ${aspireInitialReplacementIdentityCount}-identity scan exceeds the ordinary 25-create limit. Issue the single-use manual authorisation before preflight.`}
              </span>
            )}
          </>
        )}
        {isAspire && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid="operator-automation-aspire-dry-run-note"
          >
            Aspire Competitions feasibility scan is scan only - no records are
            created unless you explicitly run the apply-only draft action.
            Apply uses the exact successful preflight snapshot. Verification
            and publication remain separate guarded actions. Expiry and
            Scheduler are disabled.
          </span>
        )}
        {isAspire && (
          <div
            className="w-full rounded border border-sky-400/20 bg-sky-400/[0.08] p-3 space-y-2 text-[11px] text-sky-100"
            data-testid="operator-automation-aspire-run-limits"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2">
                <span>Max details</span>
                <input
                  type="number"
                  min={1}
                  max={ASPIRE_SCAN_MAX_DETAILS}
                  value={aspireScanMaxDetails}
                  onChange={(event) => setAspireScanMaxDetails(event.target.value)}
                  disabled={busy != null}
                  title={`Aspire detail-fetch safety maximum: ${ASPIRE_SCAN_MAX_DETAILS}`}
                  data-testid="operator-automation-aspire-scan-max-details"
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
                <span className="text-sky-100/65">Maximum {ASPIRE_SCAN_MAX_DETAILS}</span>
              </label>
              <label className="flex items-center gap-2">
                <span>Max new records</span>
                <input
                  type="number"
                  min={0}
                  max={ASPIRE_MAX_NEW_RECORDS_PER_RUN}
                  step={1}
                  value={aspireMaxNewRecords}
                  onChange={(event) => setAspireMaxNewRecords(event.target.value)}
                  disabled={busy != null}
                  title={`Aspire genuine-create safety maximum: ${ASPIRE_MAX_NEW_RECORDS_PER_RUN}`}
                  data-testid="operator-automation-aspire-max-new-records"
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
                <span className="text-sky-100/65">Maximum {ASPIRE_MAX_NEW_RECORDS_PER_RUN}</span>
              </label>
            </div>
            <div
              className="text-sky-100/70"
              data-testid="operator-automation-aspire-run-preview"
            >
              Apply/preflight payload: max_details={aspireScanMaxDetails || "invalid"}, max_new_records={aspireMaxNewRecords === "" ? "invalid" : aspireMaxNewRecords}
            </div>
            <div
              className={aspireApplySnapshotReady ? "text-emerald-200" : "text-amber-200"}
              data-testid="operator-automation-aspire-snapshot-status"
            >
              {aspireApplySnapshotReady
                ? `Ready: immutable preflight snapshot ${aspireScanSnapshot.id}`
                : aspirePreflightFailureReason
                ? `Preflight did not create a snapshot: ${aspirePreflightFailureReason}`
                : "Run a successful preflight before Apply. Apply will use that exact snapshot and will not rescan."}
            </div>
          </div>
        )}
        {isMcKinney && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: true,
              phase: "apply_preflight",
            })}
            disabled={busy != null || !mckinneyApplySnapshotReady}
            data-testid="operator-automation-mckinney-apply-preflight-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "mckinney-preflight"
              ? "Reconciling McKinney records..."
              : "Preflight McKinney exact-set apply: no competition writes"}
          </button>
        )}
        {isMcKinney && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid="operator-automation-mckinney-private-note"
          >
            McKinney is a private, operator-specific lifecycle. Scan and
            preflight are read-only; Scan creates the immutable snapshot and
            preflight reconciles that exact stored candidate set without rediscovery.
            Publish, Expiry and Scheduler remain disabled until explicitly
            reviewed and enabled.
          </span>
        )}
        {isMcKinney && (
          <div
            className="w-full rounded border border-sky-400/20 bg-sky-400/[0.08] p-3 space-y-2 text-[11px] text-sky-100"
            data-testid="operator-automation-mckinney-run-limits"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2">
                <span>Max details</span>
                <input
                  type="number"
                  min={1}
                  max={MCKINNEY_MAX_DETAILS}
                  value={mckinneyScanMaxDetails}
                  onChange={(event) => setMckinneyScanMaxDetails(event.target.value)}
                  disabled={busy != null}
                  data-testid="operator-automation-mckinney-max-details"
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Max new records</span>
                <input
                  type="number"
                  min={0}
                  max={MCKINNEY_MAX_NEW_RECORDS_PER_RUN}
                  step={1}
                  value={mckinneyMaxNewRecords}
                  onChange={(event) => {
                    setMckinneyMaxNewRecords(event.target.value);
                    setMckinneyApplyPreflightApproval(null);
                  }}
                  disabled={busy != null}
                  data-testid="operator-automation-mckinney-max-new-records"
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
              </label>
            </div>
            <div data-testid="operator-automation-mckinney-run-preview">
              Apply/preflight payload: max_details={mckinneyScanMaxDetails || "invalid"}, max_new_records={mckinneyMaxNewRecords === "" ? "invalid" : mckinneyMaxNewRecords}, snapshot_id={mckinneyScanSnapshot?.id || "required"}
            </div>
            <div
              className={mckinneyApplyPreflightReady ? "text-emerald-200" : "text-amber-200"}
              data-testid="operator-automation-mckinney-snapshot-status"
            >
              {!mckinneyApplySnapshotReady
                ? "Run a complete Scan first. Scan persists the exact immutable snapshot used by preflight and Apply."
                : mckinneyPreflightFailureReason
                ? `Snapshot ${mckinneyScanSnapshot.id} remains ready; preflight blocked: ${mckinneyPreflightFailureReason}`
                : mckinneyApplyPreflightReady
                ? `Preflight approved: immutable snapshot ${mckinneyScanSnapshot.id}`
                : `Snapshot ready: ${mckinneyScanSnapshot.id}. Run Apply preflight before Apply.`}
            </div>
          </div>
        )}
        {isLlf && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: true,
              phase: "apply_preflight",
            })}
            disabled={busy != null || !llfApplySnapshotReady}
            data-testid="operator-automation-llf-apply-preflight-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "llf-preflight"
              ? "Reconciling LLF records..."
              : "Preflight LLF exact-set apply: no competition writes"}
          </button>
        )}
        {isLlf && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid="operator-automation-llf-lifecycle-note"
          >
            LLF uses a private exact-identity lifecycle. Scan creates the
            immutable snapshot; preflight reconciles that stored set without
            rediscovery. Publish, Expiry and Scheduler remain disabled by
            default and require their separate guarded controls.
          </span>
        )}
        {isLlf && (
          <div
            className="w-full rounded border border-sky-400/20 bg-sky-400/[0.08] p-3 space-y-2 text-[11px] text-sky-100"
            data-testid="operator-automation-llf-run-limits"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2">
                <span>Max details</span>
                <input
                  type="number"
                  min={1}
                  max={LLF_MAX_DETAILS}
                  value={llfScanMaxDetails}
                  onChange={(event) => setLlfScanMaxDetails(event.target.value)}
                  disabled={busy != null}
                  data-testid="operator-automation-llf-max-details"
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Max new records</span>
                <input
                  type="number"
                  min={0}
                  max={LLF_MAX_NEW_RECORDS_PER_RUN}
                  step={1}
                  value={llfMaxNewRecords}
                  onChange={(event) => {
                    setLlfMaxNewRecords(event.target.value);
                    setLlfApplyPreflightApproval(null);
                  }}
                  disabled={busy != null}
                  data-testid="operator-automation-llf-max-new-records"
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
              </label>
            </div>
            <div data-testid="operator-automation-llf-run-preview">
              Apply/preflight payload: max_details={llfScanMaxDetails || "invalid"}, max_new_records={llfMaxNewRecords === "" ? "invalid" : llfMaxNewRecords}, snapshot_id={llfScanSnapshot?.id || "required"}
            </div>
            <div
              className={llfApplyPreflightReady ? "text-emerald-200" : "text-amber-200"}
              data-testid="operator-automation-llf-snapshot-status"
            >
              {!llfApplySnapshotReady
                ? "Run a complete LLF Scan first. Scan persists the exact immutable snapshot used by preflight and Apply."
                : llfPreflightFailureReason
                ? `Snapshot ${llfScanSnapshot.id} remains ready; preflight blocked: ${llfPreflightFailureReason}`
                : llfApplyPreflightReady
                ? `Preflight approved: immutable snapshot ${llfScanSnapshot.id}`
                : `Snapshot ready: ${llfScanSnapshot.id}. Run Apply preflight before Apply.`}
            </div>
          </div>
        )}
        {isPristine && pristineCountOption && (
          <label className="w-full rounded border border-sky-400/20 p-3 text-xs text-sky-100">
            <input type="checkbox" data-testid="operator-automation-pristine-count-acceptance"
              checked={pristineCountAcceptanceSelection === pristineCountOption.runId}
              disabled={Boolean(busy)} onChange={(event) => setPristineCountAcceptanceSelection(
                event.target.checked ? pristineCountOption.runId : "",
              )} />{" "}
            Use accepted Pristine source result: {pristineCountOption.count} confirmed of {pristineCountOption.declared} declared.
            <span className="block mt-1">The next Scan must recover those same identities. The extra result remains unknown; no retirement or expiry is authorised.</span>
          </label>
        )}
        {isKilted && (
          <div
            className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1"
            data-testid={`operator-automation-${operatorKey}-disabled-phases`}
          >
            <div>
              Publish: {publishEnabled ? "enabled" : "disabled (publish_enabled=false)"};
              {" "}Expiry: {expireEnabled ? "enabled" : "disabled"};
              {" "}Scheduler: {schedulerEnabled ? "enabled" : "disabled"}.
            </div>
            <div>Verify is a separate exact-live-set phase; it never publishes records.</div>
            <div>Latest exact live set: {kiltedCurrentLiveSetId || "missing"}</div>
            <div>
              Publish gate: {kiltedPublishGate.enabled ? "PASS" : "BLOCKED"}.
              {kiltedPublishGate.failures.length > 0
                ? ` ${kiltedPublishGate.failures.join(", ")}`
                : " Exact verified membership reconciles."}
            </div>
            <div>
              Expiry approval: {kiltedExpiryApproved ? "PASS" : "BLOCKED"}.
              Live expiry requires the exact current live-set ID, candidate IDs
              and a single-use approval from the same-product detail dry-run.
            </div>
            {!kiltedExpiryApproved && kiltedExpiryFailures.length > 0 && (
              <div data-testid={`operator-automation-${operatorKey}-expiry-gate-reasons`}>
                Approval blockers: {kiltedExpiryFailures.join(", ")}.
              </div>
            )}
            {kiltedExpiryEvidence && (
              <div>
                Candidates: {kiltedExpiryEvidence.candidate_count ?? 0};
                {" "}approval: {kiltedExpiryEvidence.expiry_approval_id || "missing"};
                {" "}expected public after: {kiltedExpiryEvidence.kilted_expiry_expected_public_count_after ?? "unknown"}.
              </div>
            )}
          </div>
        )}
        {isKilted && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: true,
              phase: "apply_preflight",
            })}
            disabled={busy != null || !kiltedApplySnapshotReady}
            data-testid={`operator-automation-${operatorKey}-apply-preflight-btn`}
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "kilted-preflight"
              ? `Reconciling ${operatorName} records...`
              : `Preflight ${operatorName} exact-set apply: no competition writes`}
          </button>
        )}
        {isKilted && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid={`operator-automation-${operatorKey}-lifecycle-note`}
          >
            {operatorName} uses a private exact-identity lifecycle. Scan creates the
            immutable snapshot; preflight reconciles that stored set without
            rediscovery. Records with complete total-cap evidence remain
            odds-eligible while unsupported mechanics remain awareness-only.
          </span>
        )}
        {isKilted && (
          <div
            className="w-full rounded border border-sky-400/20 bg-sky-400/[0.08] p-3 space-y-2 text-[11px] text-sky-100"
            data-testid={`operator-automation-${operatorKey}-run-limits`}
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2">
                <span>Max details</span>
                <input
                  type="number"
                  min={1}
                  max={KILTED_MAX_DETAILS}
                  value={kiltedScanMaxDetails}
                  onChange={(event) => setKiltedScanMaxDetails(event.target.value)}
                  disabled={busy != null}
                  data-testid={`operator-automation-${operatorKey}-max-details`}
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Max new records</span>
                <input
                  type="number"
                  min={0}
                  max={KILTED_MAX_NEW_RECORDS_PER_RUN}
                  step={1}
                  value={kiltedMaxNewRecords}
                  onChange={(event) => {
                    setKiltedMaxNewRecords(event.target.value);
                    setKiltedApplyPreflightApproval(null);
                  }}
                  disabled={busy != null}
                  data-testid={`operator-automation-${operatorKey}-max-new-records`}
                  className="w-20 rounded border border-white/10 bg-black/20 px-2 py-1 text-white"
                />
              </label>
            </div>
            <div data-testid={`operator-automation-${operatorKey}-run-preview`}>
              Apply/preflight payload: max_details={kiltedScanMaxDetails || "invalid"}, max_new_records={kiltedMaxNewRecords === "" ? "invalid" : kiltedMaxNewRecords}, snapshot_id={kiltedScanSnapshot?.id || "required"}
            </div>
            <div
              className={kiltedApplyPreflightReady ? "text-emerald-200" : "text-amber-200"}
              data-testid={`operator-automation-${operatorKey}-snapshot-status`}
            >
              {!kiltedApplySnapshotReady
                ? `Run a complete ${operatorName} Scan first. Scan persists the exact immutable snapshot used by preflight and Apply.`
                : kiltedPreflightFailureReason
                ? `Snapshot ${kiltedScanSnapshot.id} remains ready; preflight blocked: ${kiltedPreflightFailureReason}`
                : kiltedApplyPreflightReady
                ? `Preflight approved: immutable snapshot ${kiltedScanSnapshot.id}`
                : `Snapshot ready: ${kiltedScanSnapshot.id}. Run Apply preflight before Apply.`}
            </div>
          </div>
        )}
        {isAspire && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: true,
              phase: "scan",
              automatedNetworkDiscovery: true,
            })}
            disabled={busy != null}
            data-testid="operator-automation-aspire-network-discovery-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
          >
            <ArrowsClockwise size={13} weight="bold" />
            {busy === "aspire-network"
              ? "Discovering routes..."
              : "Run automated Aspire network discovery"}
          </button>
        )}
        {isAspire && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/[0.08] text-[11px] text-sky-100/80"
            data-testid="operator-automation-aspire-network-note"
          >
            Uses a controlled browser-network pass to learn public Aspire product routes. Scan-only.
            Does not publish competitions.
          </span>
        )}
        {isAspire && (
          <div
            className="w-full rounded border border-sky-400/20 bg-sky-400/[0.08] p-3 space-y-3"
            data-testid="operator-automation-aspire-manual-import"
          >
            <div>
              <div className="text-[11px] font-semibold text-sky-100">
                Aspire manual route/HAR import
              </div>
              <p className="mt-1 text-[11px] text-sky-100/75">
                Manual Aspire route import is scan-only discovery evidence. It does not publish competitions.
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-2">
              <label className="text-[10px] text-white/55">
                Input type
                <select
                  value={aspireManualImport.inputType}
                  onChange={(event) => setAspireManualImport((current) => ({
                    ...current,
                    inputType: event.target.value,
                  }))}
                  className="mt-1 w-full rounded border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white"
                  data-testid="aspire-manual-import-input-type"
                >
                  <option value="routes">Routes</option>
                  <option value="har">HAR JSON</option>
                </select>
              </label>
              <label className="text-[10px] text-white/55">
                Max details
                <input
                  type="number"
                  min="1"
                  max={ASPIRE_MANUAL_IMPORT_MAX_DETAILS}
                  value={aspireManualImport.maxDetails}
                  onChange={(event) => setAspireManualImport((current) => ({
                    ...current,
                    maxDetails: event.target.value,
                  }))}
                  className="mt-1 w-full rounded border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white"
                  data-testid="aspire-manual-import-max-details"
                />
              </label>
              <label className="flex items-center gap-2 text-[11px] text-white/65 md:mt-5">
                <input
                  type="checkbox"
                  checked={aspireManualImport.dryRun !== false}
                  onChange={(event) => setAspireManualImport((current) => ({
                    ...current,
                    dryRun: event.target.checked,
                  }))}
                  data-testid="aspire-manual-import-dry-run"
                />
                Dry-run
              </label>
              <div className="md:mt-5">
                <button
                  type="button"
                  onClick={runAspireManualImport}
                  disabled={busy != null}
                  data-testid="operator-automation-aspire-manual-import-btn"
                  className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
                >
                  <ArrowsClockwise size={13} weight="bold" />
                  {busy === "aspire-import" ? "Importing..." : "Import Aspire routes"}
                </button>
              </div>
            </div>
            <textarea
              value={aspireManualImport.text}
              onChange={(event) => setAspireManualImport((current) => ({
                ...current,
                text: event.target.value,
              }))}
              placeholder={aspireManualImport.inputType === "har"
                ? "Paste HAR JSON captured from the Aspire public app"
                : "/shop/products/example-product\nhttps://www.aspirecomps.co.uk/shop/products/example-product"}
              rows={5}
              className="w-full rounded border border-white/10 bg-black/20 px-2 py-2 text-xs text-white placeholder:text-white/35"
              data-testid="aspire-manual-import-text"
            />
          </div>
        )}
        {canRunVerifyOnly && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: false,
              phase: "verify",
              automatedNetworkDiscovery: isAspire,
              expectedLiveSetId: isRevComps
                ? revCurrentLiveSetId
                : isBotb
                ? botbCurrentSnapshotId
                : isKilted
                ? kiltedCurrentLiveSetId
                : undefined,
            })}
            disabled={
              busy != null
              || !verifyEnabled
              || (isRevComps && !revCurrentLiveSetId)
              || (isBotb && !botbCurrentSnapshotId)
              || (isKilted && !kiltedCurrentLiveSetId)
            }
            data-testid={`operator-automation-${operatorKey}-verify-btn`}
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-500/30 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "verify"
              ? "Verifying..."
              : isSevenDays
              ? "Verify 7days draft records: no publish"
              : isBotb
              ? "Verify BOTB exact-live-set drafts: no publish"
              : isClick
              ? "Verify Click draft records: no publish"
              : isElite
              ? "Verify Elite exact-live-set drafts: no publish"
              : isAspire
              ? "Verify Aspire drafts: no publish"
              : isMcKinney
              ? "Verify McKinney exact-set drafts: no publish"
              : isLlf
              ? "Verify LLF exact-set drafts: no publish"
              : isKilted
              ? `Verify ${operatorName} exact-set drafts: no publish`
              : `Verify ${operatorName} pending records: no publish`}
          </button>
        )}
        {canRunPublishOnly && (
          <button
            type="button"
            onClick={() => runAutomation({
              dryRun: false,
              phase: "publish",
              automatedNetworkDiscovery: isAspire,
              expectedLiveSetId: isMcKinney
                ? mckinneyPublishGate.currentLiveSetId
                : isBotb
                ? botbCurrentSnapshotId
                : isKilted
                ? kiltedPublishGate.currentLiveSetId
                : undefined,
            })}
            disabled={
              busy != null
              || !publishEnabled
              || (isRevComps && !revPublishGate.enabled)
              || (isBotb && !botbCurrentSnapshotId)
              || (isMcKinney && !mckinneyPublishGate.enabled)
              || (isLlf && !llfPublishGate.enabled)
              || (isKilted && !kiltedPublishGate.enabled)
            }
            title={
              isRevComps
                ? revPublishGate.enabled
                  ? "Successful exact-set Verify matches the current Rev live set."
                  : `Publish locked: ${revPublishGate.failures.join(", ")}`
                : isMcKinney
                ? mckinneyPublishGate.enabled
                  ? "Authoritative McKinney Publish gate matches the exact verified live set."
                  : `Publish locked: ${mckinneyPublishGate.failures.join(", ")}`
                : isLlf
                ? llfPublishGate.enabled
                  ? "Authoritative LLF Publish gate matches the exact verified live set."
                  : `Publish locked: ${llfPublishGate.failures.join(", ")}`
                : isKilted
                ? kiltedPublishGate.enabled
                  ? `Authoritative ${operatorName} Publish gate matches the exact verified live set.`
                  : `Publish locked: ${kiltedPublishGate.failures.join(", ")}`
                : undefined
            }
            data-readiness-reasons={
              isRevComps
                ? revPublishGate.failures.join(",")
                : isMcKinney
                ? mckinneyPublishGate.failures.join(",")
                : isLlf
                ? llfPublishGate.failures.join(",")
                : isKilted
                ? kiltedPublishGate.failures.join(",")
                : undefined
            }
            data-testid={`operator-automation-${operatorKey}-publish-btn`}
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/30 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "publish"
              ? "Publishing..."
              : isRevComps
              ? `Publish Rev verified current-live records${revPublishSafeCount > 0 ? ` (${revPublishSafeCount})` : ""}`
              : isSevenDays
              ? "Publish verified 7days drafts"
              : isBotb
              ? "Publish BOTB verified exact live set"
              : isClick
              ? "Publish Click odds-safe drafts"
              : isElite
              ? "Publish Elite verified exact live set"
              : isAspire
              ? "Publish Aspire verified drafts"
              : isMcKinney
              ? "Publish McKinney verified exact set"
              : isLlf
              ? "Publish LLF verified exact set"
              : isKilted
              ? `Publish ${operatorName} verified exact set`
              : `Publish verified ${operatorName} drafts`}
          </button>
        )}
        {isRevComps && (
          <>
            <button
              type="button"
              onClick={() => runRevExpiry({ dryRun: true })}
              disabled={busy != null || !scanEnabled || !dryRunEnabled || !revCurrentLiveSetId}
              data-testid="operator-automation-revcomps-expiry-dry-run-btn"
              className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-rose-500/15 hover:bg-rose-500/25 text-rose-100 border border-rose-500/25 disabled:opacity-45"
            >
              <ArrowsClockwise size={13} weight="bold" />
              {busy === "expire" ? "Checking expiry..." : "Dry-run Rev expiry"}
            </button>
            <button
              type="button"
              onClick={() => runRevExpiry({ dryRun: false })}
              disabled={busy != null || !revExpiryLiveActionReady}
              data-testid="operator-automation-revcomps-expire-btn"
              className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
            >
              <ShieldCheck size={13} weight="bold" />
              Expire confirmed stale Rev records
            </button>
          </>
        )}
        {(isDcg || isElite || isBotb || isSevenDays || isClick || isAspire || isMcKinney || isLlf || isKilted) && (
          <button
            type="button"
            onClick={() => (
              isBotb
                ? runBotbExpiry({ dryRun: true })
                : isClick
                ? runClickExpiry({ dryRun: true })
                : isDcg
                ? runDcgExpiry({ dryRun: true })
                : isElite
                ? runEliteExpiry({ dryRun: true })
                : isAspire
                ? runAspireExpiry({ dryRun: true })
                : isMcKinney
                ? runMckinneyExpiry({ dryRun: true })
                : isLlf
                ? runLlfExpiry({ dryRun: true })
                : isKilted
                ? runKiltedExpiry({ dryRun: true })
                : runAutomation({ dryRun: true, phase: "expire" })
            )}
            disabled={
              busy != null
              || (isBotb && !botbCurrentSnapshotId)
              || (isClick && !clickCurrentLiveSetId)
              || (isElite && !eliteCurrentLiveSetId)
              || (isAspire && !aspireCurrentLiveSetId)
              || (isMcKinney && !mckinneyCurrentLiveSetId)
              || (isLlf && !llfCurrentLiveSetId)
              || (isKilted && !kiltedCurrentLiveSetId)
              || (!isBotb && !isSevenDays && !isClick && (!scanEnabled || !dryRunEnabled))
            }
            data-testid={`operator-automation-${operatorKey}-expiry-dry-run-btn`}
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-rose-500/15 hover:bg-rose-500/25 text-rose-100 border border-rose-500/25 disabled:opacity-45"
          >
            <ArrowsClockwise size={13} weight="bold" />
            {busy === "expire"
              ? "Checking expiry..."
              : isBotb ? "Dry-run BOTB expiry"
              : isElite ? "Dry-run Elite exact-set expiry"
              : isClick ? "Dry-run Click expiry"
              : isAspire ? "Dry-run Aspire managed-set expiry"
              : isMcKinney ? "Dry-run McKinney managed-set expiry"
              : isLlf ? "Dry-run LLF same-product expiry"
              : isKilted ? `Dry-run ${operatorName} same-product expiry`
              : "Expiry dry-run: no records hidden"}
          </button>
        )}
        {isDcg && expireEnabled && (
          <button
            type="button"
            onClick={() => runDcgExpiry({ dryRun: false })}
            disabled={busy != null || !dcgExpiryApproved}
            data-testid="operator-automation-dcg-expire-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "expire"
              ? "Running DCG exact expiry..."
              : "Expire approved stale DCG records"}
          </button>
        )}
        {(isSevenDays || isClick || isElite) && expireEnabled && (
          <button
            type="button"
            onClick={() => (
              isElite
                ? runEliteExpiry({ dryRun: false })
                : isClick
                ? runClickExpiry({ dryRun: false })
                : runAutomation({ dryRun: false, phase: "expire" })
            )}
            disabled={
              busy != null
              || !scanEnabled
              || !expireEnabled
              || (isClick && !clickExpiryApproved)
              || (isElite && !eliteExpiryApproved)
            }
            data-testid={`operator-automation-${operatorKey}-expire-btn`}
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "expire"
              ? "Running expiry..."
              : isElite
              ? "Expire approved stale Elite records"
              : isClick
              ? "Expire approved stale Click records"
              : `Run ${operatorName} expiry: hides expired public records`}
          </button>
        )}
        {isBotb && (
          <button
            type="button"
            onClick={() => runBotbExpiry({ dryRun: false })}
            disabled={busy != null || !expireEnabled || !botbExpiryApproved}
            data-testid="operator-automation-botb-expire-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "expire" ? "Running live expiry..." : "Run BOTB live expiry"}
          </button>
        )}
        {isAspire && expireEnabled && (
          <button
            type="button"
            onClick={() => runAspireExpiry({ dryRun: false })}
            disabled={busy != null || !aspireExpiryApproved}
            data-testid="operator-automation-aspire-expire-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "expire"
              ? "Running Aspire expiry..."
              : "Expire confirmed prior-set Aspire records"}
          </button>
        )}
        {isMcKinney && expireEnabled && (
          <button
            type="button"
            onClick={() => runMckinneyExpiry({ dryRun: false })}
            disabled={busy != null || !mckinneyExpiryApproved}
            data-testid="operator-automation-mckinney-expire-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "expire"
              ? "Running McKinney expiry..."
              : "Expire confirmed prior-set McKinney records"}
          </button>
        )}
        {isLlf && expireEnabled && (
          <button
            type="button"
            onClick={() => runLlfExpiry({ dryRun: false })}
            disabled={busy != null || !llfExpiryApproved}
            data-testid="operator-automation-llf-expire-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "expire"
              ? "Running LLF expiry..."
              : "Expire confirmed historical LLF records"}
          </button>
        )}
        {isKilted && expireEnabled && (
          <button
            type="button"
            onClick={() => runKiltedExpiry({ dryRun: false })}
            disabled={busy != null || !kiltedExpiryApproved}
            data-testid={`operator-automation-${operatorKey}-expire-btn`}
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/35 disabled:opacity-45"
          >
            <ShieldCheck size={13} weight="bold" />
            {busy === "expire"
              ? `Running ${operatorName} expiry...`
              : `Expire confirmed historical ${operatorName} records`}
          </button>
        )}
        {isBotb && botbExpiryDryRunResult && (
          <button
            type="button"
            onClick={cancelBotbExpiryApproval}
            disabled={busy != null}
            data-testid="operator-automation-botb-expiry-cancel-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-45"
          >
            <X size={13} weight="bold" />
            {busy === "botb-expiry-cancel"
              ? "Cancelling approval..."
              : "Cancel BOTB expiry approval"}
          </button>
        )}
        {isRevComps && (
          <div className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1" data-testid="operator-automation-rev-expiry-approval">
            <div>Expiry approval: {revExpiryApproved ? "PASS" : "BLOCKED"}</div>
            <div>Latest live set: {revCurrentLiveSetId || "missing"}</div>
            <div data-testid="operator-automation-rev-expiry-approval-status">
              {revExpiryStatusMessage}
            </div>
            {revExpiryApprovalStatus?.approval_expires_at && (
              <div>
                Approval expires: {String(revExpiryApprovalStatus.approval_expires_at)}
              </div>
            )}
            {revExpiryApprovalStatus?.approval_exists && (
              <div>
                Authoritative candidates: {revExpiryApprovalStatus.candidate_count ?? 0};
                approval ID: {revExpiryApprovalStatus.expiry_approval_id || "missing"}.
              </div>
            )}
            {revExpiryDryRunResult && (
              <div>
                Last dry-run candidates: {revExpiryDryRunResult?.expiry?.candidate_count ?? revExpiryDryRunResult?.summary?.expiry?.candidate_count ?? 0}.
              </div>
            )}
            {revExpiryFailures.length > 0 && (
              <div>Blocked: {revExpiryFailures.join(", ")}</div>
            )}
            <span>
            Rev scan reviews up to 250 current catalogue records. Apply creates
            or updates pending review records. Verify only
            promotes existing eligible pending records. Publish only publishes
            verified Rev draft records. Expiry dry-run hides nothing; live
            expiry is available only when Expire is enabled and safety gates pass.
            No scan, apply, verify, expire or scheduler runs during publish-only.
            Scheduled trusted automation runs the isolated apply, verify, publish
            and expiry phases in order when Scheduler is enabled.
            Rev Apply is single-use and bound to the displayed immutable scan;
            it never performs a second discovery.
            </span>
          </div>
        )}
        {isBotb && (
          <div
            className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1"
            data-testid="operator-automation-botb-expiry-approval"
          >
            <div>Exact expiry approval: {botbExpiryApproved ? "PASS" : "BLOCKED"}</div>
            <div>Latest snapshot: {botbCurrentSnapshotId || "missing"}</div>
            <div>Snapshot completed: {settings?.latest_successful_botb_snapshot_completed_at || "not available"}</div>
            <div>Current exact identities: {settings?.latest_successful_botb_snapshot_exact_identity_count ?? 0}</div>
            <div>Scheduler: {schedulerEnabled ? "enabled" : "disabled"}; expiry: {expireEnabled ? "enabled" : "disabled"}</div>
            {botbExpiryDryRunResult && (
              <>
                <div>
                  Candidates: {botbExpiryDryRunResult?.expiry?.candidate_count ?? botbExpiryDryRunResult?.summary?.expiry?.candidate_count ?? 0}; approval is valid only in this component session.
                </div>
                <div>
                  Public before/expected after: {botbExpiryDryRunResult?.expiry?.public_before ?? 0} / {botbExpiryDryRunResult?.expiry?.expected_public_after ?? 0}
                </div>
                <div>
                  Current-live protected: {botbExpiryDryRunResult?.expiry?.current_live_protected_count ?? 0}
                </div>
              </>
            )}
            {botbExpiryFailures.length > 0 && <div>Blocked: {botbExpiryFailures.join(", ")}</div>}
            {!expireEnabled && (
              <div>Live expiry is disabled. Dry-run and identity audit remain read-only.</div>
            )}
          </div>
        )}
        {isAspire && (
          <div
            className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1"
            data-testid="operator-automation-aspire-disabled-phases"
          >
            <div>
              Aspire Scheduler: {schedulerEnabled ? "enabled" : "disabled"};
              Expiry: {expireEnabled ? "enabled" : "disabled"}.
            </div>
            <div>Latest exact live set: {aspireCurrentLiveSetId || "missing"}</div>
            <div>
              Expiry approval: {aspireExpiryApproved ? "PASS" : "BLOCKED"}.
              Dry-run is always read-only; live expiry requires the exact
              live-set ID, candidate IDs and approval ID from this session.
            </div>
            {aspireExpiryDryRunResult && (
              <div>
                Candidates: {aspireExpiryDryRunResult?.expiry?.candidate_count
                  ?? aspireExpiryDryRunResult?.summary?.expiry?.candidate_count
                  ?? 0}
              </div>
            )}
            {aspireExpiryFailures.length > 0 && (
              <div>Blocked: {aspireExpiryFailures.join(", ")}</div>
            )}
          </div>
        )}
        {isMcKinney && (
          <div
            className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1"
            data-testid="operator-automation-mckinney-disabled-phases"
          >
            <div>
              Publish: {publishEnabled ? "enabled" : "disabled (publish_enabled=false)"};
              {" "}Expiry: {expireEnabled ? "enabled" : "disabled (expire_enabled=false)"};
              {" "}Scheduler: {schedulerEnabled ? "enabled" : "disabled (scheduler_enabled=false)"}.
            </div>
            <div>Verify is a separate exact-live-set phase; it never publishes records.</div>
            <div>Latest exact live set: {mckinneyCurrentLiveSetId || "missing"}</div>
            <div>
              Expiry approval: {mckinneyExpiryApproved ? "PASS" : "BLOCKED"}.
              Live expiry requires the active server-owned exact live-set ID,
              candidate IDs and approval ID.
            </div>
            {mckinneyExpiryApprovalRefreshPending && (
              <div data-testid="operator-automation-mckinney-expiry-refresh-pending">
                Waiting for server settings to confirm the latest dry-run approval.
              </div>
            )}
            {mckinneyExpiryEvidence && (
              <>
                <div>
                  Approval: {mckinneyExpiryEvidence?.expiry?.approval_state || "active"};
                  {" "}candidates: {mckinneyExpiryEvidence?.expiry?.candidate_count
                    ?? mckinneyExpiryEvidence?.summary?.expiry?.candidate_count
                    ?? 0}; TTL: {mckinneyExpiryEvidence?.expiry?.approval_ttl_seconds ?? "unknown"}s.
                </div>
                <div>
                  Expected public before / after: {mckinneyExpiryEvidence?.expiry?.mckinney_expiry_expected_public_count_before ?? "?"} / {mckinneyExpiryEvidence?.expiry?.mckinney_expiry_expected_public_count_after ?? "?"}.
                </div>
              </>
            )}
            {mckinneyExpiryFailures.length > 0 && (
              <div>Blocked: {mckinneyExpiryFailures.join(", ")}</div>
            )}
          </div>
        )}
        {isLlf && (
          <div
            className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1"
            data-testid="operator-automation-llf-disabled-phases"
          >
            <div>
              Publish: {publishEnabled ? "enabled" : "disabled (publish_enabled=false)"};
              {" "}Expiry: {expireEnabled ? "enabled" : "disabled"};
              {" "}Scheduler: {schedulerEnabled ? "enabled" : "disabled"}.
            </div>
            <div>Verify is a separate exact-live-set phase; it never publishes records.</div>
            <div>Latest exact live set: {llfCurrentLiveSetId || "missing"}</div>
            <div>
              Publish gate: {llfPublishGate.enabled ? "PASS" : "BLOCKED"}.
              {llfPublishGate.failures.length > 0
                ? ` ${llfPublishGate.failures.join(", ")}`
                : " Exact verified membership reconciles."}
            </div>
            <div>
              Expiry approval: {llfExpiryApproved ? "PASS" : "BLOCKED"}.
              Live expiry requires the exact current live-set ID, candidate IDs
              and a single-use approval from the same-product detail dry-run.
            </div>
            {!llfExpiryApproved && llfExpiryFailures.length > 0 && (
              <div data-testid="operator-automation-llf-expiry-gate-reasons">
                Approval blockers: {llfExpiryFailures.join(", ")}.
              </div>
            )}
            {llfExpiryEvidence && (
              <div>
                Candidates: {llfExpiryEvidence.candidate_count ?? 0};
                {" "}approval: {llfExpiryEvidence.expiry_approval_id || "missing"};
                {" "}expected public after: {llfExpiryEvidence.llf_expiry_expected_public_count_after ?? "unknown"}.
              </div>
            )}
          </div>
        )}
        {isBotb && botbIdentityAuditResult && (
          <div
            className="px-3 py-2 rounded border border-white/10 bg-white/[0.03] text-[11px] text-white/60 space-y-1"
            data-testid="operator-automation-botb-identity-audit"
          >
            <div>Identity audit: {botbIdentityAuditResult.status}</div>
            <div>Matched current records: {botbIdentityAuditResult.botb_snapshot_matched_public_record_count ?? 0}</div>
            <div>Conflicts: {botbIdentityAuditResult.botb_identity_conflict_count ?? 0}</div>
            <div>Unresolved: {botbIdentityAuditResult.botb_identity_unresolved_count ?? 0}</div>
            {(botbIdentityAuditResult.blocked_reasons || []).length > 0 && (
              <div>Blocked: {botbIdentityAuditResult.blocked_reasons.join(", ")}</div>
            )}
            <div className="grid md:grid-cols-2 gap-3 pt-2">
              <SampleList
                title="Identity conflicts"
                rows={botbIdentityAuditResult.sample_botb_identity_conflicts}
                empty="No identity conflicts"
                showReason
              />
              <SampleList
                title="Unresolved identities"
                rows={botbIdentityAuditResult.sample_botb_identity_unresolved}
                empty="No unresolved identities"
                showReason
              />
            </div>
          </div>
        )}
        {isDcg && (
          <div
            className={`px-3 py-2 rounded border text-[11px] ${
              dcgApplyPreflightReady
                ? "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100"
                : "border-amber-400/25 bg-amber-400/[0.06] text-amber-100"
            }`}
            data-testid="operator-automation-dcg-snapshot-status"
          >
            <div className="font-semibold">
              {dcgApplyPreflightReady
                ? "Ready: DCG Apply uses this preflight-approved immutable scan"
                : dcgApplySnapshotReady
                ? "Apply locked: run the read-only DCG write-set preflight"
                : "Apply locked: run a complete DCG scan"}
            </div>
            <div className="break-all">
              Snapshot ID: {dcgScanSnapshot?.id || "missing"}
            </div>
            <div className="break-all">
              SHA-256: {dcgScanSnapshot?.hash || "missing"}
            </div>
            <div className="mt-1 text-white/55">
              Preflight validates creates, updates, limits and exact identity
              accounting without claiming the snapshot. Verify and Publish
              remain separate exact-live-set phases.
            </div>
            <div
              className="mt-2 pt-2 border-t border-white/10 space-y-1"
              data-testid="operator-automation-dcg-expiry-approval"
            >
              <div>
                Exact expiry approval: {dcgExpiryApproved ? "PASS" : "BLOCKED"}
              </div>
              <div>Latest live set: {dcgCurrentLiveSetId || "missing"}</div>
              <div>
                Candidates: {dcgExpiryEvidence?.candidate_count ?? 0};
                approval: {dcgExpiryEvidence?.expiry_approval_id || "missing"}.
              </div>
              <div>
                Published-status / effective public / hidden published: {dcgExpiryEvidence?.dcg_expiry_published_status_count ?? "-"} / {dcgExpiryEvidence?.dcg_expiry_effective_public_count ?? "-"} / {dcgExpiryEvidence?.dcg_expiry_hidden_published_count ?? "-"}.
              </div>
              <div>
                Current source URLs / live identities: {dcgExpiryEvidence?.dcg_expiry_current_source_url_count ?? "-"} / {dcgExpiryEvidence?.dcg_expiry_current_identity_count ?? "-"}.
              </div>
              <div>
                Scheduler: {schedulerEnabled ? "enabled" : "disabled"};
                expiry: {expireEnabled ? "enabled" : "disabled"}.
              </div>
            </div>
          </div>
        )}
        {isElite && (
          <div
            className={`px-3 py-2 rounded border text-[11px] ${
              eliteApplySnapshotReady
                ? "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100"
                : "border-amber-400/25 bg-amber-400/[0.06] text-amber-100"
            }`}
            data-testid="operator-automation-elite-snapshot-status"
          >
            <div className="font-semibold">
              {eliteApplySnapshotReady
                ? "Ready: Elite Apply uses this exact immutable scan"
                : "Apply locked: run a complete Elite scan"}
            </div>
            <div className="break-all">
              Snapshot ID: {eliteScanSnapshot?.id || "missing"}
            </div>
            <div className="break-all">
              SHA-256: {eliteScanSnapshot?.hash || "missing"}
            </div>
            <div className="mt-1 text-white/55">
              Apply consumes this snapshot once. Verify and Publish use the exact
              resulting live set; Expiry remains unavailable and scheduling stays settings-gated.
            </div>
          </div>
        )}
        {isClick && (
          <div
            className={`px-3 py-2 rounded border text-[11px] ${
              clickApplyPreflightReady
                ? "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100"
                : "border-amber-400/25 bg-amber-400/[0.06] text-amber-100"
            }`}
            data-testid="operator-automation-click-snapshot-status"
          >
            <div className="font-semibold">
              {clickApplyPreflightReady
                ? "Ready: Click Apply uses this preflight-approved immutable scan"
                : clickApplySnapshotReady
                ? "Apply locked: run Click Apply Preflight"
                : "Apply locked: run a complete Click scan"}
            </div>
            <div className="break-all">
              Snapshot ID: {clickScanSnapshot?.id || "missing"}
            </div>
            <div className="break-all">
              SHA-256: {clickScanSnapshot?.hash || "missing"}
            </div>
            <div className="mt-1 text-white/55">
              Preflight performs no competition writes. Apply consumes this
              snapshot once and does not rediscover; later phases remain separate.
            </div>
          </div>
        )}
        {isSevenDays && (
          <div
            className={`px-3 py-2 rounded border text-[11px] ${
              sevenDaysApplySnapshotReady
                ? "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100"
                : "border-amber-400/25 bg-amber-400/[0.06] text-amber-100"
            }`}
            data-testid="operator-automation-7days-snapshot-status"
          >
            <div className="font-semibold">
              {sevenDaysApplySnapshotReady
                ? "Ready: 7Days Apply uses this exact immutable scan"
                : "Apply locked: run a complete 7Days scan"}
            </div>
            <div className="break-all">
              Snapshot ID: {sevenDaysScanSnapshot?.id || "missing"}
            </div>
            <div className="break-all">
              SHA-256: {sevenDaysScanSnapshot?.hash || "missing"}
            </div>
            <div className="mt-1 text-white/55">
              Apply consumes this snapshot once. Verify and Publish use the
              exact resulting live set; Expiry and scheduling remain separately gated.
            </div>
          </div>
        )}
        {isBotb && (
          <div
            className={`px-3 py-2 rounded border text-[11px] ${
              botbApplySnapshotReady
                ? "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100"
                : "border-amber-400/25 bg-amber-400/[0.06] text-amber-100"
            }`}
            data-testid="operator-automation-botb-snapshot-status"
          >
            <div className="font-semibold">
              {botbApplySnapshotReady
                ? "Ready: BOTB Apply uses this exact immutable scan"
                : "Apply locked: run a complete BOTB scan"}
            </div>
            <div className="break-all">
              Snapshot ID: {botbScanSnapshot?.id || "missing"}
            </div>
            <div className="break-all">
              SHA-256: {botbScanSnapshot?.hash || "missing"}
            </div>
            <div className="mt-1 text-white/55">
              Apply consumes this snapshot once without rediscovery. Verify and
              Publish use the exact resulting live set; Expiry and scheduling
              remain separately gated and disabled by default.
            </div>
          </div>
        )}
        {isBotb && (
          <span
            className="px-3 py-2 rounded border border-sky-400/20 bg-sky-400/10 text-[11px] text-sky-100"
            data-testid="operator-automation-botb-scan-only-note"
          >
            BOTB now follows the explicit immutable lifecycle: Scan, Apply,
            Verify, then Publish. Each mutation phase is settings-gated and
            bound to the exact prior artifact. Live expiry still requires a
            fresh session-bound dry-run approval for the same exact live set
            and candidate IDs, hides only, and never deletes records. Affiliate
            routing and cart or purchase integration remain separate.
          </span>
        )}
      </div>

      {summary && (
        <div
          className="rounded border border-white/10 bg-black/10 p-3"
          data-testid="operator-automation-result"
        >
          <div className="text-xs font-semibold text-white mb-2">
            Last automation run: {displayedDryRun
              ? displayedPhase === "apply_preflight"
                ? "apply preflight"
                : displayedPhase === "expire"
                ? "expiry dry-run"
                : "scan preview"
              : displayedPhase === "verify"
              ? "verify"
              : displayedPhase === "publish"
              ? "publish"
              : displayedPhase === "publish_public_catalogue"
              ? "public catalogue publish"
              : displayedPhase === "expire"
              ? "expiry"
              : "apply"}
          </div>
          {Array.isArray(summary.health_gate_blocked_reasons)
            && summary.health_gate_blocked_reasons.length > 0 && (
            <div
              className="mb-3 rounded border border-rose-400/25 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-100"
              data-testid="operator-automation-runtime-health-block"
            >
              Runtime health gate blocked: {summary.health_gate_blocked_reasons.join(", ")}
            </div>
          )}
          {Array.isArray(summary.blocked_reasons)
            && summary.blocked_reasons.length > 0 && (
            <div
              className="mb-3 rounded border border-rose-400/25 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-100"
              data-testid="operator-automation-blocked-reasons"
            >
              Blocked: {summary.blocked_reasons.join(", ")}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
            <Stat label="Mode" value={result?.mode || summary.mode} />
            <Stat label="Phase" value={displayedPhase} />
            <Stat label="Dry-run" value={displayedDryRun ? "Yes" : "No"} />
            <Stat
              label="Scheduler"
              value={firstValue(result?.scheduler_enabled, summary.scheduler_enabled, schedulerEnabled) ? "Enabled" : "Disabled"}
            />
            <Stat
              label="Expire"
              value={firstValue(result?.expire_enabled, summary.expire_enabled, expireEnabled) ? "Enabled" : "Disabled"}
            />
            <Stat label="Scanned" value={summary.scanned} />
            {isRevComps && (
              <>
                <Stat label="Unique" value={summary.unique_candidates_found} />
                <Stat label="Fetched" value={summary.details_fetched} />
                <Stat label="Pending" value={summary.cards_pending_detail ?? summary.pending_detail_enrichment} />
                <Stat label="Ceiling" value={summary.detail_fetch_hard_ceiling || MAX_DETAIL_FETCH_LIMIT} />
                <Stat label="Next offset" value={summary.next_detail_fetch_offset} />
              </>
            )}
            <Stat label="Would create" value={summary.would_create} muted={!displayedDryRun} />
            <Stat label="Would update" value={summary.would_update} muted={!displayedDryRun} />
            <Stat label="Created" value={summary.created} />
            <Stat label="Updated" value={summary.updated} />
            <Stat label="Verified" value={summary.auto_verified} />
            <Stat label="Published" value={summary.auto_published} />
            <Stat label="Hidden" value={summary.expired_hidden} />
            <Stat label="Errors" value={summary.errors} />
            {isBotb && (
              <>
                <Stat label="Exact identities" value={summary.botb_snapshot_exact_identity_count} />
                <Stat label="Matched records" value={summary.botb_snapshot_matched_public_record_count} />
                <Stat label="Persisted current" value={summary.botb_snapshot_persisted_current_count} />
                <Stat label="Publishable current" value={summary.botb_snapshot_publishable_current_count} />
                <Stat label="Detail attempts" value={summary.botb_snapshot_detail_fetch_attempted_count} />
                <Stat label="Detail succeeded" value={summary.botb_snapshot_detail_fetch_succeeded_count} />
                <Stat label="Detail failed" value={summary.botb_snapshot_detail_fetch_failed_count} />
                <Stat label="Unresolved detail" value={summary.botb_snapshot_unresolved_detail_count} />
                <Stat label="Duplicate exact IDs" value={summary.botb_snapshot_duplicate_exact_identity_count} />
                <Stat label="Merged representations" value={summary.botb_duplicate_current_representation_count} />
                <Stat label="Identity conflicts" value={summary.botb_identity_conflict_count} />
                <Stat label="Unresolved identity" value={summary.botb_identity_unresolved_count} />
                <Stat label="Exact updates" value={summary.botb_identity_exact_match_count} />
                <Stat label="Legacy slot migrations" value={summary.botb_identity_legacy_slot_migration_count} />
                <Stat label="Apply set" value={summary.botb_apply_preflight_expected_identity_count} />
                <Stat label="Preflight creates" value={summary.botb_apply_preflight_create_count} />
                <Stat label="Preflight existing" value={summary.botb_apply_preflight_existing_count} />
                <Stat label="Create limit" value={summary.botb_apply_preflight_new_record_limit} />
              </>
            )}
          </div>
          {isBotb && summary.botb_apply_failure_reason && (
            <div
              className="mt-3 rounded border border-rose-400/25 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-100"
              data-testid="operator-automation-botb-apply-failure"
            >
              Apply failure: {summary.botb_apply_failure_reason}
              {summary.botb_apply_failure_exception_type
                ? ` (${summary.botb_apply_failure_exception_type})`
                : ""}
            </div>
          )}
          {isDcg && displayedPhase === "apply_preflight" && (
            <div
              className="mt-3 rounded border border-white/10 bg-white/[0.03] p-3"
              data-testid="operator-automation-dcg-apply-preflight-result"
            >
              <div className="mb-2 text-[11px] font-semibold text-white">
                DCG exact write-set preflight
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
                <Stat label="Expected identities" value={summary.dcg_apply_expected_identity_count} />
                <Stat label="Creates" value={summary.dcg_apply_genuine_create_count} />
                <Stat label="Updates" value={summary.dcg_apply_genuine_update_count} />
                <Stat label="Unchanged" value={summary.dcg_apply_genuine_unchanged_count} />
                <Stat label="Unaccounted" value={summary.dcg_apply_unaccounted_identity_count} />
                <Stat
                  label="Exact set"
                  value={summary.dcg_apply_exact_identity_set_match === true ? "Yes" : "No"}
                />
                <Stat label="Create limit" value={result?.refresh_result?.safety?.max_new_records} />
              </div>
            </div>
          )}
          {isRevComps && (
            <div
              className="mt-3 rounded border border-white/10 bg-white/[0.03] p-3"
              data-testid="rev-run-mode-integrity"
            >
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
                <Stat label="Requested phase" value={firstValue(result?.requested_phase, summary.requested_phase, result?.diagnostics?.requested_phase)} />
                <Stat label="Effective phase" value={firstValue(result?.effective_phase, summary.effective_phase, result?.diagnostics?.effective_phase)} />
                <Stat label="Requested dry-run" value={firstValue(result?.requested_dry_run, summary.requested_dry_run, result?.diagnostics?.requested_dry_run) ? "Yes" : "No"} />
                <Stat label="Effective dry-run" value={firstValue(result?.effective_dry_run, summary.effective_dry_run, result?.diagnostics?.effective_dry_run) ? "Yes" : "No"} />
                <Stat label="Mode consistent" value={firstValue(result?.run_mode_consistent, summary.run_mode_consistent, result?.diagnostics?.run_mode_consistent) === false ? "No" : "Yes"} />
                <Stat label="Safety blocked" value={firstValue(result?.run_mode_safety_blocked, summary.run_mode_safety_blocked, result?.diagnostics?.run_mode_safety_blocked) ? "Yes" : "No"} />
                <Stat label="Created" value={summary.created} />
                <Stat label="Updated" value={summary.updated} />
                <Stat label="Verified" value={summary.auto_verified} />
                <Stat label="Published" value={summary.auto_published} />
                <Stat label="Hidden" value={summary.expired_hidden} />
              </div>
              {firstValue(result?.run_mode_blocked_reason, summary.run_mode_blocked_reason, result?.diagnostics?.run_mode_blocked_reason) && (
                <div className="mt-2 text-[11px] text-rose-200" data-testid="rev-run-mode-blocked-reason">
                  {firstValue(result?.run_mode_blocked_reason, summary.run_mode_blocked_reason, result?.diagnostics?.run_mode_blocked_reason)}
                </div>
              )}
            </div>
          )}
          {isRevComps && (
            <div className="mt-3 space-y-3" data-testid="rev-scan-diagnostics">
            <div className="rounded border border-white/10 bg-white/[0.03] p-3" data-testid="rev-detail-batch-state">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
                <Stat label="Limit" value={summary.detail_fetch_limit} />
                <Stat label="Offset" value={summary.detail_fetch_offset} />
                <Stat label="Fetched" value={summary.details_fetched} />
                <Stat label="Pending" value={summary.cards_pending_detail ?? summary.pending_detail_enrichment} />
                <Stat label="Ceiling" value={summary.detail_fetch_hard_ceiling || MAX_DETAIL_FETCH_LIMIT} />
                <Stat label="Next" value={summary.next_detail_fetch_offset} />
                <Stat label="More" value={summary.has_more_detail_batches ? "Yes" : "No"} />
              </div>
              {summary.has_more_detail_batches && (
                <button
                  type="button"
                  onClick={() => runAutomation({
                    dryRun: true,
                    phase: "scan",
                    detailFetchOffset: summary.next_detail_fetch_offset,
                  })}
                  disabled={busy != null || !scanEnabled || !dryRunEnabled}
                  data-testid="rev-run-next-detail-batch-btn"
                  className="mt-3 px-4 py-2 rounded text-xs inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-45"
                >
                  <ArrowsClockwise size={13} weight="bold" />
                  {busy === "next-scan" ? "Running next batch..." : "Run next detail batch"}
                </button>
              )}
            </div>
            <div className="rounded border border-white/10 bg-white/[0.03] p-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                <Stat label="Scanner health" value={(result?.diagnostics?.rev_scan_degraded ?? summary.rev_scan_degraded) ? "Degraded" : "Healthy"} />
                <Stat label="Parser" value={result?.diagnostics?.rev_parser_strategy_used || summary.rev_parser_strategy_used || "unknown"} />
                <Stat label="Listing health" value={(result?.diagnostics?.rev_listing_fetch_healthy ?? summary.rev_listing_fetch_healthy) ? "Healthy" : "Unhealthy"} />
                <Stat label="Detail enrichment" value={(result?.diagnostics?.rev_detail_enrichment_complete ?? summary.rev_detail_enrichment_complete) ? "Complete" : "Partial"} />
                <Stat label="Current cards" value={result?.diagnostics?.rev_current_card_count ?? summary.rev_current_card_count} />
                <Stat label="Full catalogue" value={result?.diagnostics?.rev_catalogue_unique_url_count ?? summary.rev_catalogue_unique_url_count} />
                <Stat label="Catalogue state" value={(result?.diagnostics?.rev_catalogue_partial ?? summary.rev_catalogue_partial) ? "Partial" : (result?.diagnostics?.rev_catalogue_complete ?? summary.rev_catalogue_complete) ? "Complete" : "Unknown"} />
                <Stat label="Catalogue batches" value={result?.diagnostics?.rev_catalogue_batches_processed ?? summary.rev_catalogue_batches_processed} />
                <Stat label="Genuine images" value={result?.diagnostics?.rev_genuine_image_count ?? summary.rev_genuine_image_count} />
                <Stat label="Logo/header rejected" value={result?.diagnostics?.rev_rejected_logo_header_image_count ?? summary.rev_rejected_logo_header_image_count} />
                <Stat label="Identity creates" value={result?.diagnostics?.rev_identity_new_count ?? summary.rev_identity_new_count} />
                <Stat label="Identity updates" value={result?.diagnostics?.rev_would_update_count ?? summary.rev_would_update_count} />
                <Stat label="Normalised URL matches" value={result?.diagnostics?.rev_identity_normalised_url_match_count ?? summary.rev_identity_normalised_url_match_count} />
                <Stat label="Trusted key matches" value={result?.diagnostics?.rev_identity_trusted_key_match_count ?? summary.rev_identity_trusted_key_match_count} />
                <Stat label="Identity ambiguous" value={result?.diagnostics?.rev_identity_ambiguous_count ?? summary.rev_identity_ambiguous_count} />
                <Stat label="Identity duplicates" value={result?.diagnostics?.rev_identity_duplicate_match_count ?? summary.rev_identity_duplicate_match_count} />
                <Stat label="Existing identities" value={result?.diagnostics?.rev_existing_identity_record_count ?? summary.rev_existing_identity_record_count} />
                <Stat label="Vehicle routes" value={result?.diagnostics?.rev_vehicle_route_count ?? summary.rev_vehicle_route_count} />
                <Stat label="Vehicles accepted" value={result?.diagnostics?.rev_vehicle_route_accepted_count ?? summary.rev_vehicle_route_accepted_count} />
                <Stat label="Vehicles rejected" value={result?.diagnostics?.rev_vehicle_route_rejected_count ?? summary.rev_vehicle_route_rejected_count} />
                <Stat label="Route errors" value={result?.diagnostics?.rev_route_construction_error_count ?? summary.rev_route_construction_error_count} />
                <Stat label="Double cars fixed" value={result?.diagnostics?.rev_double_cars_prefix_corrected_count ?? summary.rev_double_cars_prefix_corrected_count} />
                <Stat label="Scheduler" value={result?.settings?.scheduler_enabled ? "Enabled" : "Disabled"} />
                <Stat label="Expiry" value={result?.settings?.expire_enabled ? "Enabled" : "Disabled"} />
                <Stat label="Public records" value={result?.diagnostics?.rev_public_records_count ?? summary.rev_public_records_count} />
                <Stat label="Public images missing" value={result?.diagnostics?.rev_images_missing_public_count ?? summary.rev_images_missing_public_count} />
                <Stat label="Postal routes found" value={result?.diagnostics?.rev_free_postal_entry_found_count ?? summary.rev_free_postal_entry_found_count} />
                <Stat label="Explicit postal text" value={result?.diagnostics?.rev_postal_entry_explicit_text_count ?? summary.rev_postal_entry_explicit_text_count} />
                <Stat label="Trusted postal URLs" value={result?.diagnostics?.rev_postal_entry_trusted_url_count ?? summary.rev_postal_entry_trusted_url_count} />
                <Stat label="Postal text only" value={result?.diagnostics?.rev_postal_entry_text_only_count ?? summary.rev_postal_entry_text_only_count} />
                <Stat label="Postal false positives rejected" value={result?.diagnostics?.rev_postal_false_positive_rejected_count ?? summary.rev_postal_false_positive_rejected_count} />
                <Stat label="Paid + postal" value={result?.diagnostics?.rev_paid_with_postal_route_count ?? summary.rev_paid_with_postal_route_count} />
                <Stat label="Live prices checked" value={result?.diagnostics?.rev_current_price_checked_count ?? summary.rev_current_price_checked_count} />
                <Stat label="Included cash rejected as price" value={result?.diagnostics?.rev_included_cash_rejected_as_ticket_price_count ?? summary.rev_included_cash_rejected_as_ticket_price_count} />
                <Stat label="Cash alternatives rejected as price" value={result?.diagnostics?.rev_cash_alternative_rejected_as_ticket_price_count ?? summary.rev_cash_alternative_rejected_as_ticket_price_count} />
                <Stat label="Price timestamp missing" value={result?.diagnostics?.rev_drafts_trusted_price_missing_checked_at_count ?? summary.rev_drafts_trusted_price_missing_checked_at_count} />
                <Stat label="Bulk tiers found" value={result?.diagnostics?.rev_bulk_tiers_found_count ?? summary.rev_bulk_tiers_found_count} />
                <Stat label="Category overrides" value={result?.diagnostics?.rev_category_safety_override_count ?? summary.rev_category_safety_override_count} />
                <Stat label="Current-live candidates" value={result?.diagnostics?.rev_current_live_candidate_count ?? summary.rev_current_live_candidate_count} />
                <Stat label="Current-live existing drafts" value={result?.diagnostics?.rev_current_live_existing_draft_count ?? summary.rev_current_live_existing_draft_count} />
                <Stat label="Current-live new candidates" value={result?.diagnostics?.rev_current_live_new_candidate_count ?? summary.rev_current_live_new_candidate_count} />
                <Stat label="Current-live verify eligible" value={result?.diagnostics?.rev_current_live_verify_eligible_count ?? summary.rev_current_live_verify_eligible_count} />
                <Stat label="Current-live verify blocked" value={result?.diagnostics?.rev_current_live_verify_blocked_count ?? summary.rev_current_live_verify_blocked_count} />
                <Stat label="Historical / legacy drafts" value={result?.diagnostics?.rev_historical_or_legacy_draft_count ?? summary.rev_historical_or_legacy_draft_count} />
                <Stat label="Historical placeholders" value={result?.diagnostics?.rev_historical_placeholder_count ?? summary.rev_historical_placeholder_count} />
                <Stat label="Historical images missing" value={result?.diagnostics?.rev_historical_missing_image_count ?? summary.rev_historical_missing_image_count} />
                <Stat label="Historical suspicious categories" value={result?.diagnostics?.rev_historical_suspicious_category_count ?? summary.rev_historical_suspicious_category_count} />
                <Stat label="Draft genuine images" value={result?.diagnostics?.rev_draft_genuine_image_count ?? summary.rev_draft_genuine_image_count} />
                <Stat label="Draft placeholders" value={result?.diagnostics?.rev_draft_placeholder_count ?? summary.rev_draft_placeholder_count} />
                <Stat label="Draft non-prize images" value={result?.diagnostics?.rev_draft_non_prize_image_count ?? summary.rev_draft_non_prize_image_count} />
                <Stat label="Draft images missing" value={result?.diagnostics?.rev_draft_missing_image_count ?? summary.rev_draft_missing_image_count} />
                <Stat label="Card image fallback" value={result?.diagnostics?.rev_card_image_fallback_used_count ?? summary.rev_card_image_fallback_used_count} />
                <Stat label="Suspicious Watches" value={result?.diagnostics?.rev_suspicious_watches_event_count ?? summary.rev_suspicious_watches_event_count} />
                <Stat label="Latest live-set ID" value={result?.diagnostics?.rev_verify_selector_live_set_id ?? result?.diagnostics?.rev_publish_selector_live_set_id ?? result?.settings?.latest_successful_rev_live_set_id ?? summary.rev_verify_selector_live_set_id ?? summary.rev_publish_selector_live_set_id} />
                <Stat label="Verify selector current-live" value={result?.diagnostics?.rev_verify_selector_current_live_count ?? summary.rev_verify_selector_current_live_count} />
                <Stat label="Verify historical excluded" value={result?.diagnostics?.rev_verify_selector_historical_excluded_count ?? summary.rev_verify_selector_historical_excluded_count} />
                <Stat label="Out-of-scope verified" value={result?.diagnostics?.rev_out_of_scope_verified_count ?? summary.rev_out_of_scope_verified_count} />
                <Stat label="Out-of-scope public" value={result?.diagnostics?.rev_out_of_scope_verified_public_count ?? summary.rev_out_of_scope_verified_public_count} />
                <Stat label="Out-of-scope drafts" value={result?.diagnostics?.rev_out_of_scope_verified_draft_count ?? summary.rev_out_of_scope_verified_draft_count} />
                <Stat label="Publish selector current-live" value={result?.diagnostics?.rev_publish_selector_current_live_count ?? summary.rev_publish_selector_current_live_count} />
                <Stat label="Publish verified" value={result?.diagnostics?.rev_publish_selector_verified_count ?? summary.rev_publish_selector_verified_count} />
                <Stat label="Publish overlap blocked" value={result?.diagnostics?.rev_publish_selector_overlap_blocked_count ?? summary.rev_publish_selector_overlap_blocked_count} />
                <Stat label="Publish safe" value={result?.diagnostics?.rev_publish_safe_count ?? summary.rev_publish_safe_count} />
                <Stat label="Publish blocked" value={result?.diagnostics?.rev_publish_blocked_count ?? summary.rev_publish_blocked_count} />
                <Stat label="Current-live public" value={result?.diagnostics?.rev_public_image_current_live_public_count ?? summary.rev_public_image_current_live_public_count} />
                <Stat label="Out-of-scope public" value={result?.diagnostics?.rev_public_image_out_of_scope_public_count ?? summary.rev_public_image_out_of_scope_public_count} />
                <Stat label="Server image failures" value={result?.diagnostics?.rev_public_image_server_failure_count ?? summary.rev_public_image_server_failure_count} />
                <Stat label="Browser failures reported" value={result?.diagnostics?.rev_public_image_browser_failure_count ?? summary.rev_public_image_browser_failure_count} />
                <Stat label="Legacy replacements" value={result?.diagnostics?.rev_public_image_legacy_duplicate_replacement_count ?? summary.rev_public_image_legacy_duplicate_replacement_count} />
                <Stat label="Current-live repairs" value={result?.diagnostics?.rev_public_image_current_live_repair_count ?? summary.rev_public_image_current_live_repair_count} />
              </div>
              {Object.keys(result?.diagnostics?.rev_catalogue_count_by_category || summary.rev_catalogue_count_by_category || {}).length > 0 && (
                <div className="mt-3 text-[11px] text-white/65" data-testid="rev-category-breakdown">
                  Categories: {Object.entries(result?.diagnostics?.rev_catalogue_count_by_category || summary.rev_catalogue_count_by_category)
                    .map(([category, count]) => `${category}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_catalogue_count_by_route_family || summary.rev_catalogue_count_by_route_family || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-white/65" data-testid="rev-route-breakdown">
                  Routes: {Object.entries(result?.diagnostics?.rev_catalogue_count_by_route_family || summary.rev_catalogue_count_by_route_family)
                    .map(([route, count]) => `${route}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_existing_identity_field_coverage || summary.rev_existing_identity_field_coverage || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-white/65" data-testid="rev-identity-coverage">
                  Existing identity coverage: {Object.entries(result?.diagnostics?.rev_existing_identity_field_coverage || summary.rev_existing_identity_field_coverage)
                    .map(([field, count]) => `${field}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_rejection_reason_breakdown || summary.rev_rejection_reason_breakdown || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-amber-300/80" data-testid="rev-rejection-breakdown">
                  Rejections: {Object.entries(result?.diagnostics?.rev_rejection_reason_breakdown || summary.rev_rejection_reason_breakdown)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_price_source_breakdown || summary.rev_price_source_breakdown || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-white/65" data-testid="rev-price-source-breakdown">
                  Price sources: {Object.entries(result?.diagnostics?.rev_price_source_breakdown || summary.rev_price_source_breakdown)
                    .map(([source, count]) => `${source}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_postal_false_positive_rejection_reason_breakdown || summary.rev_postal_false_positive_rejection_reason_breakdown || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-amber-300/80" data-testid="rev-postal-rejection-breakdown">
                  Postal URL rejections: {Object.entries(result?.diagnostics?.rev_postal_false_positive_rejection_reason_breakdown || summary.rev_postal_false_positive_rejection_reason_breakdown)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_current_live_verify_blocked_reason_breakdown || summary.rev_current_live_verify_blocked_reason_breakdown || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-amber-300/80" data-testid="rev-current-live-verify-blockers">
                  Current-live verify blockers: {Object.entries(result?.diagnostics?.rev_current_live_verify_blocked_reason_breakdown || summary.rev_current_live_verify_blocked_reason_breakdown)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_verify_blocked_reason_breakdown || summary.rev_verify_blocked_reason_breakdown || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-amber-300/80" data-testid="rev-verify-blocker-breakdown">
                  Verify blockers: {Object.entries(result?.diagnostics?.rev_verify_blocked_reason_breakdown || summary.rev_verify_blocked_reason_breakdown)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_public_overlap_classification_counts || summary.rev_public_overlap_classification_counts || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-white/65" data-testid="rev-public-overlap-breakdown">
                  Public overlap: {Object.entries(result?.diagnostics?.rev_public_overlap_classification_counts || summary.rev_public_overlap_classification_counts)
                    .map(([classification, count]) => `${classification}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_publish_blocked_reason_breakdown || summary.rev_publish_blocked_reason_breakdown || {}).length > 0 && (
                <div className="mt-2 text-[11px] text-amber-300/80" data-testid="rev-publish-blocker-breakdown">
                  Publish blockers: {Object.entries(result?.diagnostics?.rev_publish_blocked_reason_breakdown || summary.rev_publish_blocked_reason_breakdown)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {(result?.diagnostics?.rev_odds_compatible_counter_status || summary.rev_odds_compatible_counter_status) && (
                <div className="mt-2 text-[11px] text-white/55" data-testid="rev-odds-counter-status">
                  Odds counter: {result?.diagnostics?.rev_odds_compatible_counter_status || summary.rev_odds_compatible_counter_status}
                </div>
              )}
              {(result?.diagnostics?.rev_scan_degraded_reasons || summary.rev_scan_degraded_reasons)?.length > 0 && (
                <div className="mt-2 text-[11px] text-amber-300/80" data-testid="rev-degraded-reasons">
                  Degraded reasons: {(result?.diagnostics?.rev_scan_degraded_reasons || summary.rev_scan_degraded_reasons).join(", ")}
                </div>
              )}
              {(result?.diagnostics?.sample_rev_selected_genuine_images || summary.sample_rev_selected_genuine_images)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Selected genuine Rev prize images"
                    rows={result?.diagnostics?.sample_rev_selected_genuine_images || summary.sample_rev_selected_genuine_images}
                    empty="No genuine-image samples"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_identity_decisions || summary.sample_rev_identity_decisions)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Rev identity decisions"
                    rows={result?.diagnostics?.sample_rev_identity_decisions || summary.sample_rev_identity_decisions}
                    empty="No identity samples"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_chosen_vehicle_urls || summary.sample_rev_chosen_vehicle_urls)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Canonical Rev vehicle routes"
                    rows={result?.diagnostics?.sample_rev_chosen_vehicle_urls || summary.sample_rev_chosen_vehicle_urls}
                    empty="No vehicle-route samples"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_recent_existing_identities || summary.sample_rev_recent_existing_identities)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Recent existing Rev identities"
                    rows={result?.diagnostics?.sample_rev_recent_existing_identities || summary.sample_rev_recent_existing_identities}
                    empty="No existing identity samples"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_category_safety_overrides || summary.sample_rev_category_safety_overrides)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Rev category safety overrides"
                    rows={result?.diagnostics?.sample_rev_category_safety_overrides || summary.sample_rev_category_safety_overrides}
                    empty="No category override samples"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_postal_entry_evidence || summary.sample_rev_postal_entry_evidence)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Rev postal-entry evidence"
                    rows={result?.diagnostics?.sample_rev_postal_entry_evidence || summary.sample_rev_postal_entry_evidence}
                    empty="No postal-entry samples"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_current_prices || summary.sample_rev_current_prices)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Rev current price evidence"
                    rows={result?.diagnostics?.sample_rev_current_prices || summary.sample_rev_current_prices}
                    empty="No current-price samples"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_draft_image_issues || summary.sample_rev_draft_image_issues)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Rev draft image blockers"
                    rows={result?.diagnostics?.sample_rev_draft_image_issues || summary.sample_rev_draft_image_issues}
                    empty="No draft image blockers"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_suspicious_watches_events || summary.sample_rev_suspicious_watches_events)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65" data-testid="rev-suspicious-watches-samples">
                  <SampleList
                    title="Suspicious Rev Watches event records"
                    rows={result?.diagnostics?.sample_rev_suspicious_watches_events || summary.sample_rev_suspicious_watches_events}
                    empty="No suspicious Watches event records"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_out_of_scope_verified || summary.sample_rev_out_of_scope_verified)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65" data-testid="rev-out-of-scope-verified-samples">
                  <SampleList
                    title="Rev verified outside latest live set"
                    rows={result?.diagnostics?.sample_rev_out_of_scope_verified || summary.sample_rev_out_of_scope_verified}
                    empty="No out-of-scope verified Rev records"
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_public_overlap_collisions || summary.sample_rev_public_overlap_collisions)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65" data-testid="rev-public-overlap-samples">
                  <SampleList
                    title="Rev public legacy overlap review"
                    rows={result?.diagnostics?.sample_rev_public_overlap_collisions || summary.sample_rev_public_overlap_collisions}
                    empty="No public overlap samples"
                    showReason
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_public_image_broken_records || summary.sample_rev_public_image_broken_records)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65" data-testid="rev-public-image-broken-samples">
                  <SampleList
                    title="Rev broken public images"
                    rows={result?.diagnostics?.sample_rev_public_image_broken_records || summary.sample_rev_public_image_broken_records}
                    empty="No broken public Rev images"
                    showReason
                  />
                </div>
              )}
              {(result?.diagnostics?.sample_rev_public_image_cleanup_decisions || summary.sample_rev_public_image_cleanup_decisions)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65" data-testid="rev-public-image-cleanup-decisions">
                  <SampleList
                    title="Rev public image cleanup decisions"
                    rows={result?.diagnostics?.sample_rev_public_image_cleanup_decisions || summary.sample_rev_public_image_cleanup_decisions}
                    empty="No Rev public image cleanup decisions"
                    showReason
                  />
                </div>
              )}
              {Object.keys(result?.diagnostics?.rev_budget_odds_missing_reason_breakdown || summary.rev_budget_odds_missing_reason_breakdown || {}).length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  Budget Odds blockers: {Object.entries(result?.diagnostics?.rev_budget_odds_missing_reason_breakdown || summary.rev_budget_odds_missing_reason_breakdown)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {(result?.diagnostics?.sample_rev_public_records_missing_images || summary.sample_rev_public_records_missing_images)?.length > 0 && (
                <div className="mt-3 text-[11px] text-white/65">
                  <SampleList
                    title="Public Rev records missing images"
                    rows={result?.diagnostics?.sample_rev_public_records_missing_images || summary.sample_rev_public_records_missing_images}
                    empty="No missing-image samples"
                  />
                </div>
              )}
            </div>
            </div>
          )}
          {isBotb && (
            <ScanOnlyDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {isSevenDays && (
            <SevenDaysDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {isClick && (
            <ClickDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {isJonnyFromDonny && (
            <JonnyFromDonnyDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {isFeelGood && (
            <FeelGoodDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {isAspire && (
            <AspireDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {isMcKinney && (
            <McKinneyDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {isLlf && (
            <LlfDiagnosticsPanel
              summary={summary}
              diagnostics={result?.diagnostics || {}}
            />
          )}
          {verification && (
            <div
              className="mt-3 rounded border border-white/10 bg-white/[0.03] p-3"
              data-testid="operator-automation-verification"
            >
              <div className="text-[11px] font-semibold text-white/80 mb-2">
                Verification {verification.enabled ? "enabled" : "disabled"}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <Stat label="Auto verified" value={verification.auto_verified} />
                <Stat label="Already verified" value={verification.skipped_already_verified} />
                <Stat label="Needs review" value={verification.skipped_needs_review} />
                <Stat label="Gate blocked" value={verification.skipped_public_gate_blocked} />
              </div>
              {(verification.sample_verified?.length > 0
                || verification.sample_skipped?.length > 0) && (
                <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] text-white/65">
                  <SampleList
                    title="Sample verified"
                    rows={verification.sample_verified}
                    empty="No verified samples"
                  />
                  <SampleList
                    title="Sample skipped"
                    rows={verification.sample_skipped}
                    empty="No skipped samples"
                    showReason
                  />
                </div>
              )}
            </div>
          )}
          {publication && (
            <div
              className="mt-3 rounded border border-white/10 bg-white/[0.03] p-3"
              data-testid="operator-automation-publication"
            >
              <div className="text-[11px] font-semibold text-white/80 mb-2">
                Publication {publication.enabled ? "enabled" : "disabled"}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <Stat label="Auto published" value={publication.auto_published} />
                <Stat label="Already live" value={publication.skipped_already_published} />
                <Stat label="Not verified" value={publication.skipped_not_verified} />
                <Stat label="Gate blocked" value={publication.skipped_public_gate_blocked} />
                {publication.sevendays_publish_image_repair_attempted !== undefined && (
                  <>
                    <Stat label="Image repair checked" value={publication.sevendays_publish_image_repair_attempted} />
                    <Stat label="Images updated" value={publication.sevendays_publish_image_repair_updated} />
                    <Stat label="Image repair skipped" value={publication.sevendays_publish_image_repair_skipped} />
                    <Stat label="Image repair failed" value={publication.sevendays_publish_image_repair_failed} />
                  </>
                )}
                {publication.sevendays_publish_cash_alt_repair_attempted !== undefined && (
                  <>
                    <Stat label="Cash alt checked" value={publication.sevendays_publish_cash_alt_repair_attempted} />
                    <Stat label="Cash alt updated" value={publication.sevendays_publish_cash_alt_repair_updated} />
                    <Stat label="Cash alt skipped" value={publication.sevendays_publish_cash_alt_repair_skipped} />
                    <Stat label="Cash alt failed" value={publication.sevendays_publish_cash_alt_repair_failed} />
                  </>
                )}
              </div>
              {(publication.sample_published?.length > 0
                || publication.sample_skipped?.length > 0
                || publication.sample_image_repaired?.length > 0
                || publication.sample_image_repair_failed?.length > 0
                || publication.sample_cash_alt_repaired?.length > 0
                || publication.sample_cash_alt_missing?.length > 0
                || publication.sample_cash_alt_rejected?.length > 0) && (
                <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] text-white/65">
                  <SampleList
                    title="Sample published"
                    rows={publication.sample_published}
                    empty="No published samples"
                  />
                  <SampleList
                    title="Sample skipped"
                    rows={publication.sample_skipped}
                    empty="No skipped samples"
                    showReason
                  />
                  {publication.sample_image_repaired?.length > 0 && (
                    <SampleList
                      title="Sample image repaired"
                      rows={publication.sample_image_repaired}
                      empty="No image repair samples"
                    />
                  )}
                  {publication.sample_image_repair_failed?.length > 0 && (
                    <SampleList
                      title="Sample image repair failed"
                      rows={publication.sample_image_repair_failed}
                      empty="No image repair failures"
                      showReason
                    />
                  )}
                  {publication.sample_cash_alt_repaired?.length > 0 && (
                    <SampleList
                      title="Sample cash alternative repaired"
                      rows={publication.sample_cash_alt_repaired}
                      empty="No cash alternative repair samples"
                    />
                  )}
                  {publication.sample_cash_alt_missing?.length > 0 && (
                    <SampleList
                      title="Sample cash alternative missing"
                      rows={publication.sample_cash_alt_missing}
                      empty="No missing cash alternative samples"
                      showReason
                    />
                  )}
                  {publication.sample_cash_alt_rejected?.length > 0 && (
                    <SampleList
                      title="Sample cash alternative rejected"
                      rows={publication.sample_cash_alt_rejected}
                      empty="No rejected cash alternative samples"
                      showReason
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {expiry && (
            <div
              className="mt-3 rounded border border-white/10 bg-white/[0.03] p-3"
              data-testid="operator-automation-expiry"
            >
              <div className="text-[11px] font-semibold text-white/80 mb-2">
                {expiry.dry_run
                  ? "Expiry dry-run: no records hidden"
                  : `Expiry ${expiry.enabled ? "enabled" : "disabled"}`}
                {expiry.safety_blocked ? " - safety blocked" : ""}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <Stat label="Dry-run" value={expiry.dry_run ? "Yes" : "No"} />
                <Stat label="Safety" value={expiry.safety_blocked ? "Blocked" : "Clear"} />
                <Stat label="Hidden" value={expiry.expired_hidden} />
                <Stat label="Would hide" value={expiry.would_expire} />
                <Stat label="Candidates" value={expiry.candidate_count} />
                <Stat
                  label={isBotb ? "Current exact" : "Current URLs"}
                  value={expiry.current_public_group_count ?? expiry.current_url_count}
                />
                <Stat label="Max expire" value={expiry.max_expire_records} />
                {isBotb && (
                  <>
                    <Stat label="Exact current" value={expiry.current_exact_identity_count} />
                    <Stat label="Public before" value={expiry.public_before} />
                    <Stat label="Expected after" value={expiry.expected_public_after} />
                    <Stat label="Current protected" value={expiry.current_live_protected_count} />
                    <Stat label="Snapshot reconciled" value={expiry.snapshot_reconciled ? "Yes" : "No"} />
                    <Stat label="Deletes" value={expiry.deleted_count} />
                  </>
                )}
                {isRevComps && (
                  <>
                    <Stat label="Published status" value={expiry.rev_published_status_count} />
                    <Stat label="Effectively public" value={expiry.rev_effective_public_count} />
                    <Stat label="Published but hidden" value={expiry.rev_hidden_published_count} />
                    <Stat label="Current-live public" value={expiry.rev_current_live_effective_public_count} />
                    <Stat label="Managed stale public" value={expiry.rev_prior_live_set_effective_public_candidate_count} />
                    <Stat label="Unmanaged public" value={expiry.rev_unmanaged_effective_public_count} />
                    <Stat label="Expected public after" value={expiry.rev_expected_effective_public_count_after_expiry} />
                  </>
                )}
              </div>
              {expiry.block_reason && (
                <div className="mt-2 text-[11px] text-amber-100">
                  Blocked: {expiry.block_reason}
                </div>
              )}
              {expiry.skipped_reason && (
                <div className="mt-2 text-[11px] text-amber-100">
                  Skipped: {expiry.skipped_reason}
                </div>
              )}
              {expiryBlockedReasons.length > 0 && (
                <div className="mt-2 text-[11px] text-amber-100">
                  Blocked reasons: {expiryBlockedReasons.slice(0, 5).join(", ")}
                </div>
              )}
              {Object.keys(expiryReasonBreakdown).length > 0 && (
                <div className="mt-2 text-[11px] text-white/60">
                  Reasons: {Object.entries(expiryReasonBreakdown)
                    .slice(0, 5)
                    .map(([reason, count]) => `${reason}: ${count}`)
                    .join(", ")}
                </div>
              )}
              {(expiryWouldRows.length > 0
                || expirySkippedRows.length > 0) && (
                <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] text-white/65">
                  <SampleList
                    title={expiry.dry_run ? "Sample would hide" : "Sample hidden"}
                    rows={expiryWouldRows}
                    empty="No hidden samples"
                    showReason
                  />
                  <SampleList
                    title="Sample blocked/skipped"
                    rows={expirySkippedRows}
                    empty="No skipped samples"
                    showReason
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Flag({ label, enabled, danger }) {
  return (
    <span
      className={`px-2 py-0.5 rounded border ${
        enabled
          ? danger
            ? "bg-red-400/10 text-red-100 border-red-400/30"
            : "bg-emerald-400/10 text-emerald-100 border-emerald-400/30"
          : "bg-white/[0.04] text-white/45 border-white/10"
      }`}
    >
      {label}: {enabled ? "enabled" : "disabled"}
    </span>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div className={`rounded px-2 py-1.5 bg-white/[0.04] ${muted ? "text-white/35" : "text-white/80"}`}>
      <div className="text-[9px] uppercase tracking-wide opacity-70">
        {label}
      </div>
      <div className="text-sm font-semibold font-mono-num">{value ?? 0}</div>
    </div>
  );
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function SevenDaysDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const acceptedRows = diagnostics.sample_sevendays_accepted_standard_draw || [];
  const quarantinedRows = diagnostics.sample_sevendays_quarantined_mechanic || [];
  const closedRows = diagnostics.sample_sevendays_closed_or_sold || [];
  const rejectedRows = diagnostics.sample_sevendays_rejected_missing_field || [];
  const rejectionReasons = diagnostics.sevendays_rejection_reasons || {};

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="sevendays-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        7days dry-run diagnostics
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Candidates" value={firstValue(diagnostics.sevendays_api_listing_candidates_seen, summary.cards_found)} />
        <Stat label="Details fetched" value={firstValue(diagnostics.sevendays_api_detail_fetch_success, summary.details_fetched)} />
        <Stat label="Accepted" value={summary.cards_accepted} />
        <Stat label="Rejected" value={summary.cards_rejected} />
        <Stat label="GBP prices" value={diagnostics.sevendays_gbp_safe_price_count} />
        <Stat label="Non-GBP rejected" value={diagnostics.sevendays_non_gbp_price_rejected_count} />
        <Stat label="Missing price" value={diagnostics.sevendays_missing_price_count} />
        <Stat label="Max entries" value={diagnostics.sevendays_max_entries_found_count} />
        <Stat label="Per-user diagnostic" value={diagnostics.sevendays_per_user_limit_diagnostic_count} />
        <Stat label="Quarantined" value={diagnostics.sevendays_quarantined_mechanic_count} />
        <Stat label="Closed/sold" value={diagnostics.sevendays_closed_or_sold_count} />
        <Stat label="Budget-eligible dry-run" value={diagnostics.sevendays_budget_odds_eligible_dry_run_count} />
      </div>
      {Object.keys(rejectionReasons).length > 0 && (
        <div className="mt-2 text-[11px] text-white/60">
          Reasons: {Object.entries(rejectionReasons)
            .slice(0, 5)
            .map(([reason, count]) => `${reason}: ${count}`)
            .join(", ")}
        </div>
      )}
      <div className="mt-3 grid md:grid-cols-4 gap-3 text-[11px] text-white/65">
        <SampleList title="Accepted examples" rows={acceptedRows} empty="No accepted examples" showReason />
        <SampleList title="Quarantined examples" rows={quarantinedRows} empty="No quarantined examples" showReason />
        <SampleList title="Closed/sold examples" rows={closedRows} empty="No closed or sold examples" showReason />
        <SampleList title="Rejected examples" rows={rejectedRows} empty="No rejected examples" showReason />
      </div>
    </div>
  );
}

function ClickDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const displayRows = diagnostics.sample_click_display_safe_standard || [];
  const cashRows = diagnostics.sample_click_cash_competitions || [];
  const vehicleRows = diagnostics.sample_click_car_bike_competitions || [];
  const quarantinedRows = diagnostics.sample_click_quarantined_mechanic || [];
  const rejectedRows = diagnostics.sample_click_rejected_missing_field || [];
  const winnerRows = diagnostics.sample_click_winner_results || [];
  const rejectionReasons = diagnostics.click_rejection_reasons || {};

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="click-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        Click dry-run diagnostics
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Candidates" value={firstValue(diagnostics.click_listing_candidates_seen, summary.cards_found)} />
        <Stat label="Details fetched" value={firstValue(diagnostics.click_detail_fetch_success, summary.details_fetched)} />
        <Stat label="Display-safe" value={firstValue(diagnostics.click_display_safe_candidate_count, summary.cards_accepted)} />
        <Stat label="Public write set" value={firstValue(diagnostics.click_public_write_candidate_count, summary.click_public_write_candidate_count)} />
        <Stat label="Odds-ready set" value={firstValue(diagnostics.click_odds_ready_public_candidate_count, summary.click_odds_ready_public_candidate_count)} />
        <Stat label="Awareness-only set" value={firstValue(diagnostics.click_awareness_public_candidate_count, summary.click_awareness_public_candidate_count)} />
        <Stat label="Quarantined" value={diagnostics.click_quarantined_mechanic_count} />
        <Stat label="GBP prices" value={diagnostics.click_gbp_price_found_count} />
        <Stat label="Missing price" value={diagnostics.click_missing_price_count} />
        <Stat label="Cash alternatives" value={diagnostics.click_cash_alternative_found_count} />
        <Stat label="Per-user diagnostic" value={diagnostics.click_per_user_limit_diagnostic_count} />
        <Stat label="Trusted caps" value={diagnostics.click_total_cap_trusted_count} />
        <Stat label="Budget-eligible dry-run" value={diagnostics.click_budget_odds_eligible_dry_run_count} />
        <Stat label="Winner rows" value={diagnostics.click_winner_result_rows_found} />
        <Stat label="Rejected" value={summary.cards_rejected} />
      </div>
      {Object.keys(rejectionReasons).length > 0 && (
        <div className="mt-2 text-[11px] text-white/60">
          Reasons: {Object.entries(rejectionReasons)
            .slice(0, 5)
            .map(([reason, count]) => `${reason}: ${count}`)
            .join(", ")}
        </div>
      )}
      <div className="mt-3 grid md:grid-cols-3 gap-3 text-[11px] text-white/65">
        <SampleList title="Display-safe examples" rows={displayRows} empty="No display-safe examples" showReason />
        <SampleList title="Cash examples" rows={cashRows} empty="No cash examples" showReason />
        <SampleList title="Vehicle examples" rows={vehicleRows} empty="No vehicle examples" showReason />
        <SampleList title="Quarantined examples" rows={quarantinedRows} empty="No quarantined examples" showReason />
        <SampleList title="Rejected examples" rows={rejectedRows} empty="No rejected examples" showReason />
        <SampleList title="Winner/result examples" rows={winnerRows} empty="No winner examples" showReason />
      </div>
    </div>
  );
}

function JonnyFromDonnyDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const productRows = diagnostics.sample_jfd_product_urls || [];
  const displayRows = diagnostics.sample_jfd_display_safe || [];
  const totalTicketRows = diagnostics.sample_jfd_trusted_total_tickets || [];
  const cashRows = diagnostics.sample_jfd_cash_values || [];
  const reviewRows = diagnostics.sample_jfd_review_needed || [];
  const missingRows = diagnostics.sample_jfd_missing_fields || [];
  const rejectionReasons = diagnostics.jfd_rejection_reasons || {};

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="jonnyfromdonny-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        Jonny From Donny feasibility diagnostics
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Homepage" value={diagnostics.jfd_homepage_fetch_status || "unknown"} />
        <Stat label="Links seen" value={diagnostics.jfd_links_seen_count} />
        <Stat label="Product URLs" value={firstValue(diagnostics.jfd_product_url_accepted_count, summary.cards_found)} />
        <Stat label="Details fetched" value={firstValue(diagnostics.jfd_detail_fetch_success, summary.details_fetched)} />
        <Stat label="Display-safe" value={firstValue(diagnostics.jfd_display_safe_count, summary.cards_accepted)} />
        <Stat label="Needs review" value={firstValue(diagnostics.jfd_review_needed_count, summary.review_needed)} />
        <Stat label="GBP prices" value={diagnostics.jfd_price_found_count} />
        <Stat label="Missing price" value={diagnostics.jfd_missing_ticket_price_count} />
        <Stat label="Total tickets" value={diagnostics.jfd_total_tickets_found_count} />
        <Stat label="Missing total" value={diagnostics.jfd_missing_max_tickets_count} />
        <Stat label="Per-user diagnostic" value={diagnostics.jfd_per_user_limit_diagnostic_count} />
        <Stat label="Cash alternatives" value={diagnostics.jfd_cash_alternative_found_count} />
        <Stat label="Included cash" value={diagnostics.jfd_included_cash_found_count} />
        <Stat label="Draw dates" value={diagnostics.jfd_draw_date_found_count} />
        <Stat label="Live/open" value={diagnostics.jfd_status_live_count} />
        <Stat label="Budget-eligible dry-run" value={diagnostics.jfd_budget_odds_eligible_dry_run_count} />
      </div>
      {Object.keys(rejectionReasons).length > 0 && (
        <div className="mt-2 text-[11px] text-white/60">
          Reasons: {Object.entries(rejectionReasons)
            .slice(0, 5)
            .map(([reason, count]) => `${reason}: ${count}`)
            .join(", ")}
        </div>
      )}
      <div className="mt-3 grid md:grid-cols-3 gap-3 text-[11px] text-white/65">
        <SampleList title="Product URL examples" rows={productRows} empty="No product URLs" showReason />
        <SampleList title="Display-safe examples" rows={displayRows} empty="No display-safe examples" showReason />
        <SampleList title="Trusted total examples" rows={totalTicketRows} empty="No total-ticket examples" showReason />
        <SampleList title="Cash examples" rows={cashRows} empty="No cash examples" showReason />
        <SampleList title="Review examples" rows={reviewRows} empty="No review examples" showReason />
        <SampleList title="Missing-field examples" rows={missingRows} empty="No missing-field examples" showReason />
      </div>
    </div>
  );
}

function FeelGoodDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const displayRows = diagnostics.sample_feelgood_display_safe || [];
  const missingRows = diagnostics.sample_feelgood_missing_fields || [];
  const rejectionReasons = diagnostics.feelgood_rejection_reasons || {};

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="feelgood-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        Feel Good feasibility diagnostics
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Cards found" value={firstValue(diagnostics.feelgood_cards_found_count, summary.cards_found)} />
        <Stat label="Candidate URLs" value={diagnostics.feelgood_candidate_urls_found_count} />
        <Stat label="Details fetched" value={firstValue(diagnostics.feelgood_detail_fetch_success_count, summary.details_fetched)} />
        <Stat label="Display-safe" value={firstValue(diagnostics.feelgood_display_safe_candidate_count, summary.cards_accepted)} />
        <Stat label="GBP prices" value={diagnostics.feelgood_price_found_count} />
        <Stat label="Max tickets" value={diagnostics.feelgood_max_tickets_found_count} />
        <Stat label="Cash alternatives" value={diagnostics.feelgood_cash_alternative_found_count} />
        <Stat label="Budget-eligible dry-run" value={diagnostics.feelgood_budget_odds_eligible_dry_run_count} />
        <Stat label="Rejected" value={summary.cards_rejected} />
        <Stat label="Errors" value={summary.errors} />
      </div>
      {Object.keys(rejectionReasons).length > 0 && (
        <div className="mt-2 text-[11px] text-white/60">
          Reasons: {Object.entries(rejectionReasons)
            .slice(0, 5)
            .map(([reason, count]) => `${reason}: ${count}`)
            .join(", ")}
        </div>
      )}
      <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] text-white/65">
        <SampleList title="Display-safe examples" rows={displayRows} empty="No display-safe examples" showReason />
        <SampleList title="Missing-field examples" rows={missingRows} empty="No missing-field examples" showReason />
      </div>
    </div>
  );
}

function LlfDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const formatBreakdown = (value) => Object.entries(value || {})
    .map(([reason, count]) => `${reason}: ${count}`)
    .join(", ") || "none";
  const formatFlag = (value) => (
    value === true ? "yes" : value === false ? "no" : "unknown"
  );
  const completenessBlockers = firstValue(
    diagnostics.llf_scan_completeness_blockers,
    diagnostics.blocked_reasons,
    summary.blocked_reasons,
    [],
  );
  const candidateRows = diagnostics.sample_llf_display_safe || [];
  const reviewRows = firstValue(
    diagnostics.sample_llf_review_only,
    diagnostics.sample_llf_apply_review_exclusions,
    diagnostics.sample_llf_verify_review_exclusions,
    diagnostics.sample_llf_publish_review_exclusions,
    [],
  );
  const errorRows = diagnostics.sample_llf_fetch_errors || [];

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="llf-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        LLF source, exact-set lifecycle and public reconciliation
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Homepage" value={diagnostics.llf_homepage_fetch_status || "unknown"} />
        <Stat label="Active listing" value={diagnostics.llf_active_listing_fetch_status || "unknown"} />
        <Stat label="Source declared" value={diagnostics.llf_source_declared_count} />
        <Stat label="Source provenance" value={diagnostics.llf_source_count_provenance || "unknown"} />
        <Stat label="Routes selected" value={diagnostics.llf_routes_selected_for_detail} />
        <Stat label="Routes deferred" value={diagnostics.llf_routes_deferred_by_limit} />
        <Stat label="Details fetched" value={firstValue(diagnostics.llf_detail_fetch_success_count, summary.details_fetched)} />
        <Stat label="Exact identities" value={diagnostics.llf_exact_identity_count} />
        <Stat label="Complete source set" value={diagnostics.llf_complete_source_identity_count} />
        <Stat label="Safe candidates" value={diagnostics.llf_safe_candidate_count} />
        <Stat label="Review-only" value={diagnostics.llf_review_only_candidate_count} />
        <Stat label="Ineligible" value={diagnostics.llf_ineligible_candidate_count} />
        <Stat label="Partition accounted" value={diagnostics.llf_partition_accounted_identity_count} />
        <Stat label="Partition exact" value={formatFlag(diagnostics.llf_partition_exact_set_accounted)} />
        <Stat label="Duplicates" value={diagnostics.llf_duplicate_exact_identity_count} />
        <Stat label="Unresolved" value={diagnostics.llf_unresolved_identity_count} />
        <Stat label="Unaccounted" value={diagnostics.llf_unaccounted_count} />
        <Stat label="GBP prices" value={diagnostics.llf_gbp_trusted_price_count} />
        <Stat label="Trusted caps" value={diagnostics.llf_trusted_total_cap_count} />
        <Stat label="Arithmetic reconciled" value={diagnostics.llf_entry_arithmetic_reconciled_count} />
        <Stat label="Arithmetic mismatch" value={diagnostics.llf_entry_arithmetic_mismatch_count} />
        <Stat label="Per-user limits" value={diagnostics.llf_per_user_limit_found_count} />
        <Stat label="Images ready" value={diagnostics.llf_image_ready_count} />
        <Stat label="Display safe" value={diagnostics.llf_display_safe_count} />
        <Stat label="Budget Odds eligible" value={diagnostics.llf_budget_odds_eligible_count} />
        <Stat label="Awareness/no odds" value={diagnostics.llf_awareness_no_odds_count} />
        <Stat label="Catalogue complete" value={formatFlag(diagnostics.llf_catalogue_complete)} />
        <Stat label="Scan complete" value={formatFlag(diagnostics.llf_scan_complete)} />
        <Stat label="Completeness blockers" value={completenessBlockers.length ? completenessBlockers.join(", ") : "none"} />
        <Stat label="Mechanics" value={formatBreakdown(diagnostics.llf_mechanic_counts)} />
        <Stat label="Categories" value={formatBreakdown(diagnostics.llf_category_counts)} />
        <Stat label="Cap rejections" value={formatBreakdown(diagnostics.llf_total_cap_rejection_breakdown)} />
        <Stat label="Image rejections" value={formatBreakdown(diagnostics.llf_image_rejection_breakdown)} />
        <Stat label="Snapshot ID" value={diagnostics.llf_apply_scan_snapshot_id || "not created"} />
        <Stat label="Snapshot state" value={diagnostics.llf_scan_snapshot_state || "unknown"} />
        <Stat label="Snapshot version" value={diagnostics.llf_scan_snapshot_version} />
        <Stat label="Snapshot complete set" value={diagnostics.llf_scan_snapshot_complete_source_identity_count} />
        <Stat label="Snapshot safe set" value={diagnostics.llf_scan_snapshot_safe_candidate_count} />
        <Stat label="Snapshot review set" value={diagnostics.llf_scan_snapshot_review_only_candidate_count} />
        <Stat label="Snapshot ineligible set" value={diagnostics.llf_scan_snapshot_ineligible_candidate_count} />
        <Stat label="Apply expected" value={diagnostics.llf_apply_expected_identity_count} />
        <Stat label="Preflight creates" value={diagnostics.llf_apply_genuine_create_count} />
        <Stat label="Preflight updates" value={diagnostics.llf_apply_genuine_update_count} />
        <Stat label="Preflight unchanged" value={diagnostics.llf_apply_unchanged_count} />
        <Stat label="Apply conflicts" value={diagnostics.llf_apply_conflict_count} />
        <Stat label="Apply accounted" value={diagnostics.llf_apply_accounted_identity_count} />
        <Stat label="Complete set persisted" value={formatFlag(diagnostics.llf_apply_complete_set_persisted)} />
        <Stat label="Live-set pointer advanced" value={formatFlag(diagnostics.llf_apply_live_set_pointer_advanced)} />
        <Stat label="Verify candidates" value={diagnostics.llf_verify_candidate_count} />
        <Stat label="Verified" value={diagnostics.llf_verify_verified_count} />
        <Stat label="Verify rejected" value={diagnostics.llf_verify_rejected_count} />
        <Stat label="Verify accounted" value={diagnostics.llf_verify_accounted_count} />
        <Stat label="Verify exact set" value={formatFlag(diagnostics.llf_verify_exact_set_accounted)} />
        <Stat label="Publish set" value={diagnostics.llf_publish_complete_apply_set_count} />
        <Stat label="Newly published" value={diagnostics.llf_publish_newly_published_count} />
        <Stat label="Already published" value={diagnostics.llf_publish_already_published_count} />
        <Stat label="Publish gate failures" value={diagnostics.llf_publish_failed_gate_count} />
        <Stat label="Publish accounted" value={diagnostics.llf_publish_accounted_set_count} />
        <Stat label="Publish exact set" value={formatFlag(diagnostics.llf_publish_exact_set_accounted)} />
        <Stat label="Public expected" value={diagnostics.llf_public_projection_expected_count} />
        <Stat label="Public actual" value={diagnostics.llf_public_projection_actual_count} />
        <Stat label="Public missing" value={diagnostics.llf_public_projection_missing_count} />
        <Stat label="Public unexpected" value={diagnostics.llf_public_projection_unexpected_count} />
        <Stat label="Historical leaks" value={diagnostics.llf_public_projection_historical_leak_count} />
        <Stat label="Review leaks" value={diagnostics.llf_public_projection_review_excluded_leak_count} />
        <Stat label="Operator filter" value={diagnostics.llf_public_projection_operator_filter_exposed === true ? "exposed" : "not exposed"} />
        <Stat label="Expiry candidates" value={diagnostics.llf_expiry_candidate_count} />
        <Stat label="Expiry protected" value={diagnostics.llf_expiry_protected_count} />
        <Stat label="Expiry exact set" value={formatFlag(diagnostics.llf_expiry_exact_set_accounted)} />
        <Stat label="Expiry" value={diagnostics.llf_expiry_supported === true ? "supported" : "unavailable"} />
        <Stat label="Scheduler" value={diagnostics.llf_scheduler_supported === true ? "supported" : "unavailable"} />
      </div>
      <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] text-white/65">
        <SampleList title="Display-safe candidates" rows={candidateRows} empty="No display-safe samples" showReason />
        <SampleList title="Review-only exclusions" rows={reviewRows} empty="No review exclusions" showReason />
        <SampleList title="Fetch or extraction errors" rows={errorRows} empty="No errors" showReason />
        <SampleList title="Candidate URLs" rows={diagnostics.sample_llf_candidate_urls || []} empty="No candidate URLs" />
      </div>
    </div>
  );
}

function McKinneyDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const candidateRows = (diagnostics.sample_mckinney_candidates || []).map((row) => ({
    ...row,
    reasons: row?.mckinney_display_blocking_reasons || row?.reasons,
  }));
  const reviewRows = firstValue(
    diagnostics.sample_mckinney_review_only,
    diagnostics.sample_mckinney_apply_review_exclusions,
    diagnostics.sample_mckinney_verify_review_exclusions,
    diagnostics.sample_mckinney_publish_review_exclusions,
    [],
  ).map((row) => ({
    ...row,
    reasons: row?.review_reasons || row?.reasons,
    evidence: row?.evidence || [
      row?.card_price_evidence ? `card: ${row.card_price_evidence}` : null,
      row?.detail_price_evidence ? `detail: ${row.detail_price_evidence}` : null,
      row?.detected_currency ? `currency: ${row.detected_currency}` : null,
    ].filter(Boolean).join("; "),
  }));
  const errorRows = diagnostics.sample_mckinney_errors || [];
  const completenessBlockers = firstValue(
    diagnostics.mckinney_scan_completeness_blockers,
    diagnostics.blocked_reasons,
    summary.blocked_reasons,
    [],
  );
  const formatBreakdown = (value) => Object.entries(value || {})
    .map(([reason, count]) => `${reason}: ${count}`)
    .join(", ") || "none";
  const formatFlag = (value) => (
    value === true ? "yes" : value === false ? "no" : "unknown"
  );
  const fieldCoverage = Object.entries(
    diagnostics.mckinney_required_field_coverage || {},
  ).map(([field, value]) => (
    `${field}: ${value?.coverage_percent ?? 0}%`
  )).join(", ") || "none";
  const fieldProvenanceRows = (
    diagnostics.sample_mckinney_field_provenance || []
  ).map((row) => ({
    ...row,
    evidence: Object.entries(row?.fields || {})
      .map(([field, value]) => (
        `${field}: ${value?.confidence || "unknown"}`
        + (value?.source ? ` (${value.source})` : "")
      ))
      .join("; "),
  }));

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="mckinney-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        McKinney source and lifecycle diagnostics
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Homepage" value={diagnostics.mckinney_homepage_fetch_status || "unknown"} />
        <Stat label="Source declared" value={diagnostics.mckinney_source_declared_count} />
        <Stat label="Source provenance" value={diagnostics.mckinney_source_count_provenance || "unknown"} />
        <Stat label="Initial page" value={diagnostics.mckinney_initial_page_count} />
        <Stat label="Later pages" value={diagnostics.mckinney_later_page_count} />
        <Stat label="Pages processed" value={diagnostics.mckinney_pages_processed_count} />
        <Stat label="Pagination complete" value={diagnostics.mckinney_pagination_contract_complete ? "yes" : "no"} />
        <Stat label="Pagination stop" value={diagnostics.mckinney_pagination_termination_reason || "unknown"} />
        <Stat label="Routes selected" value={diagnostics.mckinney_routes_selected_for_detail} />
        <Stat label="Routes deferred" value={diagnostics.mckinney_routes_deferred_by_limit} />
        <Stat label="Details attempted" value={diagnostics.mckinney_detail_fetch_attempted_count} />
        <Stat label="Details fetched" value={firstValue(diagnostics.mckinney_detail_fetch_success_count, summary.details_fetched)} />
        <Stat label="Exact identities" value={diagnostics.mckinney_exact_identity_count} />
        <Stat label="Complete source set" value={diagnostics.mckinney_complete_source_identity_count} />
        <Stat label="Safe candidates" value={diagnostics.mckinney_safe_candidate_count} />
        <Stat label="Review-only candidates" value={firstValue(diagnostics.mckinney_review_only_candidate_count, diagnostics.mckinney_review_only_count)} />
        <Stat label="Ineligible candidates" value={diagnostics.mckinney_ineligible_candidate_count} />
        <Stat label="Partition accounted" value={diagnostics.mckinney_partition_accounted_identity_count} />
        <Stat label="Partition exact" value={formatFlag(diagnostics.mckinney_partition_exact_set_accounted)} />
        <Stat label="Duplicate identities" value={diagnostics.mckinney_duplicate_exact_identity_count} />
        <Stat label="Unresolved identities" value={diagnostics.mckinney_unresolved_identity_count} />
        <Stat label="Unaccounted" value={diagnostics.mckinney_unaccounted_count} />
        <Stat label="GBP prices" value={diagnostics.mckinney_gbp_trusted_price_count} />
        <Stat label="EUR rejected" value={diagnostics.mckinney_eur_rejected_count} />
        <Stat label="Trusted caps" value={diagnostics.mckinney_trusted_total_cap_count} />
        <Stat label="Per-user limits" value={diagnostics.mckinney_per_user_limit_found_count} />
        <Stat label="Postal entry evidence" value={diagnostics.mckinney_free_entry_evidence_count} />
        <Stat label="Maximum-date caveats" value={diagnostics.mckinney_maximum_date_caveat_count} />
        <Stat label="Images ready" value={diagnostics.mckinney_image_ready_count} />
        <Stat label="Display safe" value={diagnostics.mckinney_display_safe_count} />
        <Stat label="Budget Odds eligible" value={diagnostics.mckinney_budget_odds_eligible_count} />
        <Stat label="Awareness/no odds" value={diagnostics.mckinney_awareness_no_odds_count} />
        <Stat label="Review only" value={diagnostics.mckinney_review_only_count} />
        <Stat label="Requests" value={diagnostics.mckinney_request_count} />
        <Stat label="Retries" value={diagnostics.mckinney_retry_count} />
        <Stat label="Duration ms" value={diagnostics.mckinney_duration_ms} />
        <Stat label="Catalogue complete" value={diagnostics.mckinney_catalogue_complete ? "yes" : "no"} />
        <Stat label="All routes accounted" value={diagnostics.mckinney_all_routes_accounted ? "yes" : "no"} />
        <Stat label="Identity reconciliation" value={diagnostics.mckinney_identity_reconciliation_complete ? "complete" : "incomplete"} />
        <Stat label="Scan complete" value={diagnostics.mckinney_scan_complete ? "yes" : "no"} />
        <Stat label="Completeness blockers" value={completenessBlockers.length ? completenessBlockers.join(", ") : "none"} />
        <Stat label="Field coverage" value={fieldCoverage} />
        <Stat label="Mechanics" value={formatBreakdown(diagnostics.mckinney_mechanic_counts)} />
        <Stat label="Categories" value={formatBreakdown(diagnostics.mckinney_category_counts)} />
        <Stat label="Cap rejections" value={formatBreakdown(diagnostics.mckinney_total_cap_rejection_breakdown)} />
        <Stat label="Image rejections" value={formatBreakdown(diagnostics.mckinney_image_rejection_breakdown)} />
        <Stat label="Snapshot ID" value={diagnostics.mckinney_apply_scan_snapshot_id || "not created"} />
        <Stat label="Snapshot state" value={diagnostics.mckinney_scan_snapshot_state || "unknown"} />
        <Stat label="Snapshot version" value={diagnostics.mckinney_scan_snapshot_version} />
        <Stat label="Snapshot complete set" value={diagnostics.mckinney_scan_snapshot_complete_source_identity_count} />
        <Stat label="Snapshot safe set" value={diagnostics.mckinney_scan_snapshot_safe_candidate_count} />
        <Stat label="Snapshot review set" value={diagnostics.mckinney_scan_snapshot_review_only_candidate_count} />
        <Stat label="Snapshot settings pointer" value={formatFlag(diagnostics.mckinney_scan_snapshot_settings_pointer_written)} />
        <Stat label="Apply expected" value={diagnostics.mckinney_apply_expected_identity_count} />
        <Stat label="Apply safe" value={diagnostics.mckinney_apply_safe_candidate_count} />
        <Stat label="Apply review excluded" value={diagnostics.mckinney_apply_intentional_review_exclusion_count} />
        <Stat label="Preflight creates" value={diagnostics.mckinney_apply_genuine_create_count} />
        <Stat label="Preflight updates" value={diagnostics.mckinney_apply_genuine_update_count} />
        <Stat label="Required max new" value={diagnostics.mckinney_apply_required_max_new_records} />
        <Stat label="Create limit" value={diagnostics.mckinney_apply_new_record_limit} />
        <Stat label="Create limit exceeded" value={formatFlag(diagnostics.mckinney_apply_new_record_limit_exceeded)} />
        <Stat label="Apply attempted" value={diagnostics.mckinney_apply_attempted_identity_count} />
        <Stat label="Apply created" value={diagnostics.mckinney_apply_created_count} />
        <Stat label="Apply updated" value={diagnostics.mckinney_apply_updated_count} />
        <Stat label="Apply unchanged" value={diagnostics.mckinney_apply_unchanged_count} />
        <Stat label="Apply unaccounted" value={diagnostics.mckinney_apply_unaccounted_count} />
        <Stat label="Complete set persisted" value={diagnostics.mckinney_apply_complete_set_persisted ? "yes" : "no"} />
        <Stat label="Live-set pointer advanced" value={diagnostics.mckinney_apply_live_set_pointer_advanced ? "yes" : "no"} />
        <Stat label="Verify candidates" value={diagnostics.mckinney_verify_candidate_count} />
        <Stat label="Verify complete set" value={diagnostics.mckinney_verify_complete_source_count} />
        <Stat label="Verify review excluded" value={diagnostics.mckinney_verify_review_excluded_count} />
        <Stat label="Verify historical excluded" value={diagnostics.mckinney_verify_historical_excluded_count} />
        <Stat label="Verify missing" value={diagnostics.mckinney_verify_missing_expected_identity_count} />
        <Stat label="Verify unexpected" value={diagnostics.mckinney_verify_unexpected_identity_count} />
        <Stat label="Verified" value={diagnostics.mckinney_verify_verified_count} />
        <Stat label="Verify rejected" value={diagnostics.mckinney_verify_rejected_count} />
        <Stat label="Verify accounted" value={diagnostics.mckinney_verify_accounted_count} />
        <Stat label="Verify exact set" value={formatFlag(diagnostics.mckinney_verify_exact_set_accounted)} />
        <Stat label="Publish set" value={diagnostics.mckinney_publish_complete_apply_set_count} />
        <Stat label="Publish safe selector" value={diagnostics.mckinney_publish_safe_selector_count} />
        <Stat label="Publish review excluded" value={diagnostics.mckinney_publish_review_excluded_count} />
        <Stat label="Publish historical excluded" value={diagnostics.mckinney_publish_historical_excluded_count} />
        <Stat label="Newly published" value={diagnostics.mckinney_publish_newly_published_count} />
        <Stat label="Already published" value={diagnostics.mckinney_publish_already_published_count} />
        <Stat label="Publish gate failures" value={diagnostics.mckinney_publish_failed_gate_count} />
        <Stat label="Publish accounted" value={diagnostics.mckinney_publish_accounted_set_count} />
        <Stat label="Publish exact set" value={formatFlag(diagnostics.mckinney_publish_exact_set_accounted)} />
        <Stat label="Budget Odds published" value={diagnostics.mckinney_publish_budget_odds_eligible_count} />
        <Stat label="Awareness-only published" value={diagnostics.mckinney_publish_awareness_no_odds_count} />
        <Stat label="Lifecycle blocked" value={completenessBlockers.length ? completenessBlockers.join(", ") : "none"} />
        <Stat label="Expiry candidates" value={diagnostics.mckinney_expiry_candidate_count} />
        <Stat label="Expiry exact set" value={diagnostics.mckinney_expiry_exact_candidate_set ? "yes" : "no"} />
      </div>
      <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] text-white/65">
        <SampleList
          title="Candidate evidence"
          rows={candidateRows}
          empty="No candidate samples"
          showReason
        />
        <SampleList
          title="Fetch or extraction errors"
          rows={errorRows}
          empty="No errors"
          showReason
        />
        <SampleList
          title="Field provenance"
          rows={fieldProvenanceRows}
          empty="No field provenance"
          showReason
        />
        <SampleList
          title="Review-only exclusions"
          rows={reviewRows}
          empty="No review-only exclusions"
          showReason
        />
      </div>
    </div>
  );
}

function AspireDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const candidateRows = diagnostics.sample_aspire_candidate_urls || [];
  const displayRows = diagnostics.sample_aspire_display_safe || [];
  const missingRows = diagnostics.sample_aspire_missing_fields || [];
  const rejectedRows = diagnostics.sample_aspire_rejected_urls || [];
  const fetchErrors = diagnostics.sample_aspire_fetch_errors || [];
  const endpointRows = diagnostics.sample_aspire_preload_data_endpoints || [];
  const productRecordRows = diagnostics.sample_aspire_data_product_records || [];
  const productRouteRows = diagnostics.sample_aspire_product_routes || [];
  const metaFallbackRows = diagnostics.sample_aspire_static_meta_detail_fallback || [];
  const jsBundleRows = diagnostics.sample_aspire_js_bundle_urls || [];
  const jsMarkerRows = diagnostics.sample_aspire_js_route_markers || [];
  const possibleEndpointRows = diagnostics.sample_aspire_possible_api_endpoints || [];
  const dataCollectionRows = diagnostics.sample_aspire_data_collection_markers || [];
  const transportExcerptRows = diagnostics.sample_aspire_data_transport_excerpt || [];
  const transportProductRows = diagnostics.sample_aspire_data_transport_product_markers || [];
  const transportRouteRows = diagnostics.sample_aspire_data_transport_route_markers || [];
  const transportBoundaryRows = diagnostics.sample_aspire_data_transport_record_boundaries || [];
  const transportRecordRows = diagnostics.sample_aspire_data_transport_product_records || [];
  const transportKeyRows = diagnostics.sample_aspire_data_transport_product_keys || [];
  const transportDecodeErrorRows = diagnostics.sample_aspire_data_transport_decode_errors || [];
  const rejectedRouteRows = diagnostics.sample_aspire_rejected_route_fragments || [];
  const probeSuccessRows = diagnostics.sample_aspire_discovered_endpoint_probe_success || [];
  const probeRejectedRows = diagnostics.sample_aspire_discovered_endpoint_probe_rejected || [];
  const additionalDataRows = diagnostics.sample_aspire_data_x_additional_data || [];
  const dataHandlerRows = diagnostics.sample_aspire_data_handlers || [];
  const rawProductRouteRows = diagnostics.sample_aspire_raw_product_routes || [];
  const decodedProductRouteRows = diagnostics.sample_aspire_decoded_product_routes || [];
  const linkresolverUrlRows = diagnostics.sample_aspire_linkresolver_urls || [];
  const linkresolverExcerptRows = diagnostics.sample_aspire_linkresolver_response_excerpt || [];
  const linkresolverKeyRows = diagnostics.sample_aspire_linkresolver_response_keys || [];
  const collectionConfigRows = diagnostics.sample_aspire_collection_configs || [];
  const manualExtractedRows = diagnostics.sample_aspire_manual_routes_extracted || [];
  const manualAcceptedRows = diagnostics.sample_aspire_manual_routes_accepted || [];
  const manualRejectedRows = diagnostics.sample_aspire_manual_routes_rejected || [];
  const manualHintRows = diagnostics.sample_aspire_manual_hints || [];
  const manualHintLoadedRows = diagnostics.sample_aspire_manual_hints_loaded || [];
  const manualHintUsedRows = diagnostics.sample_aspire_manual_hints_used || [];
  const decodedProductDocRows = diagnostics.sample_aspire_decoded_product_docs || [];
  const decodedVariantRows = diagnostics.sample_aspire_decoded_variants || [];
  const decodedStockRows = diagnostics.sample_aspire_decoded_stocklocks || [];
  const currentEntryRows = diagnostics.sample_aspire_current_entries_derivations || [];
  const autoNetworkLinkresolverRows = diagnostics.sample_aspire_auto_network_linkresolver_urls || [];
  const autoNetworkAcceptedRows = diagnostics.sample_aspire_auto_network_routes_accepted || [];
  const autoNetworkErrorRows = diagnostics.sample_aspire_auto_network_errors || [];
  const paginationPageRows = diagnostics.sample_aspire_pagination_pages || [];
  const aspireVerifiedRows = diagnostics.sample_aspire_verified || [];
  const aspireVerifySkippedRows = diagnostics.sample_aspire_verify_skipped || [];
  const aspirePublishedRows = diagnostics.sample_aspire_published || [];
  const aspirePublishedRepairRows = diagnostics.sample_aspire_already_published_repaired || [];
  const aspirePublishSkippedRows = diagnostics.sample_aspire_publish_skipped || [];
  const aspireOddsMismatchRows = diagnostics.sample_aspire_card_detail_odds_mismatches || [];
  const identityDuplicateRows = diagnostics.sample_aspire_identity_duplicates || [];
  const identityConflictRows = diagnostics.sample_aspire_identity_conflicts || [];
  const unresolvedIdentityRows = diagnostics.sample_aspire_unresolved_identities || [];
  const reviewOnlyRows = diagnostics.sample_aspire_review_only_ids || [];
  const applySafeRows = diagnostics.sample_aspire_apply_safe_ids || [];
  const autoDrawConfirmedRows = diagnostics.sample_aspire_autodraw_cap_confirmed || [];
  const autoDrawRejectedRows = diagnostics.sample_aspire_autodraw_cap_rejected || [];
  const holidayCategoryRows = diagnostics.sample_aspire_explicit_holiday_categories || [];
  const applyCreateRows = diagnostics.sample_aspire_apply_creates || [];
  const applyUpdateRows = diagnostics.sample_aspire_apply_updates || [];
  const applyMigrationRows = diagnostics.sample_aspire_apply_legacy_migrations || [];
  const applyConflictRows = diagnostics.sample_aspire_apply_conflicts || [];
  const voucherPrizeRows = diagnostics.sample_aspire_voucher_prizes_detected || [];
  const pageShapeRows = (diagnostics.sample_aspire_page_shape_fingerprints || []).map(
    (row) => ({
      ...row,
      evidence: [
        row?.extraction_strategy,
        row?.fingerprint?.hash
          ? `${String(row.fingerprint.hash).slice(0, 16)}...`
          : null,
        row?.fingerprint?.node_count != null
          ? `${row.fingerprint.node_count} nodes`
          : null,
      ].filter(Boolean).join("; "),
    }),
  );
  const fieldProvenanceRows = (diagnostics.sample_aspire_field_provenance || []).map(
    (row) => ({
      ...row,
      evidence: Object.entries(row?.fields || {})
        .map(([field, value]) => (
          `${field}: ${value?.confidence || "unknown"}`
          + (value?.source ? ` (${value.source})` : "")
        ))
        .join("; "),
    }),
  );
  const moneyEvidenceRows = (diagnostics.sample_aspire_money_evidence || []).map((row) => ({
    ...row,
    evidence: Array.isArray(row?.evidence)
      ? row.evidence
          .map((item) => {
            if (item == null) return "";
            if (typeof item !== "object") return String(item);
            const amount = item.raw || item.amount || "";
            return item.clause ? `${amount}: ${item.clause}` : String(amount);
          })
          .filter(Boolean)
          .join("; ")
      : row?.evidence,
  }));
  const formatRepairRows = (rows) => (rows || []).map((row) => ({
    ...row,
    evidence: Object.entries(row?.changes || {})
      .map(([field, change]) => {
        const oldValue = change?.old == null ? "null" : JSON.stringify(change.old);
        const newValue = change?.new == null ? "null" : JSON.stringify(change.new);
        return `${field}: ${oldValue} -> ${newValue}`;
      })
      .join("; "),
  }));
  const moneyRepairRows = formatRepairRows(
    diagnostics.sample_aspire_money_fields_repaired,
  );
  const categoryRepairRows = formatRepairRows(
    diagnostics.sample_aspire_category_fields_repaired,
  );
  const metadataBackfillRows = formatRepairRows(
    diagnostics.sample_aspire_metadata_fields_backfilled,
  );
  const rejectionReasons = diagnostics.aspire_candidate_url_rejection_reasons || {};
  const reviewReasons = diagnostics.aspire_review_only_reason_counts || {};
  const completenessBlockers = diagnostics.aspire_scan_completeness_blockers || [];
  const degradationReasons = diagnostics.aspire_degradation_reasons || [];
  const extractionStrategySummary = Object.entries(
    diagnostics.aspire_extraction_strategy_counts || {},
  ).map(([strategy, count]) => `${strategy}: ${count}`).join(", ") || "none";
  const requiredFieldCoverageSummary = Object.entries(
    diagnostics.aspire_required_field_coverage || {},
  ).map(([field, value]) => `${field}: ${value?.coverage_percent ?? 0}%`).join(", ") || "none";

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="aspire-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        Aspire feasibility diagnostics
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Homepage" value={diagnostics.aspire_homepage_fetch_status || "unknown"} />
        <Stat label="Products" value={diagnostics.aspire_products_fetch_status || "unknown"} />
        <Stat label="Entry list" value={diagnostics.aspire_entry_list_fetch_status || "unknown"} />
        <Stat label="403s" value={diagnostics.aspire_fetch_403_count} />
        <Stat label="Fetch profile" value={diagnostics.aspire_fetch_profile_used || "unknown"} />
        <Stat label="Feasibility" value={diagnostics.aspire_operator_feasibility_status || "unknown"} />
        <Stat label="Canonical domain" value={diagnostics.aspire_canonical_domain || "aspirecomps.co.uk"} />
        <Stat label="Source class" value={diagnostics.aspire_source_classification || "unknown"} />
        <Stat label="Detected schema" value={diagnostics.aspire_detected_schema || "unknown"} />
        <Stat label="Extraction strategies" value={extractionStrategySummary} />
        <Stat label="Required-field coverage" value={requiredFieldCoverageSummary} />
        <Stat label="Page-shape fingerprints" value={diagnostics.aspire_page_shape_fingerprint_count} />
        <Stat label="Quarantined candidates" value={diagnostics.aspire_quarantined_candidate_count} />
        <Stat label="Exact identities" value={diagnostics.aspire_exact_identity_count} />
        <Stat label="Duplicate identities" value={diagnostics.aspire_duplicate_exact_identity_count} />
        <Stat label="Ambiguous identities" value={diagnostics.aspire_ambiguous_identity_count} />
        <Stat label="Unresolved identities" value={diagnostics.aspire_unresolved_identity_count} />
        <Stat label="Requested max details" value={firstValue(diagnostics.requested_max_details, "default")} />
        <Stat label="Validated max details" value={firstValue(diagnostics.validated_max_details, "default")} />
        <Stat label="Runner max details" value={firstValue(diagnostics.runner_max_details, "default")} />
        <Stat label="Effective max details" value={diagnostics.aspire_effective_max_details} />
        <Stat label="Requested max new records" value={firstValue(diagnostics.requested_max_new_records, "not supplied")} />
        <Stat label="Validated max new records" value={firstValue(diagnostics.validated_max_new_records, "not supplied")} />
        <Stat label="Settings max new records" value={diagnostics.settings_max_new_records} />
        <Stat label="Runner max new records" value={firstValue(diagnostics.runner_max_new_records, "not supplied")} />
        <Stat label="Effective max new records" value={diagnostics.aspire_effective_max_new_records} />
        <Stat label="Max new records source" value={diagnostics.aspire_max_new_records_source || "unknown"} />
        <Stat label="Ordinary create limit" value={diagnostics.aspire_apply_ordinary_new_record_limit} />
        <Stat label="One-time authorisation" value={diagnostics.aspire_initial_replacement_authorisation_present ? "present" : "not present"} />
        <Stat label="Authorisation ID" value={diagnostics.aspire_initial_replacement_authorisation_id || "none"} />
        <Stat label="Authorised snapshot" value={diagnostics.aspire_initial_replacement_authorised_snapshot_id || "not bound"} />
        <Stat label="Authorised identities" value={diagnostics.aspire_initial_replacement_authorised_identity_count} />
        <Stat label="Authorised creates" value={diagnostics.aspire_initial_replacement_authorised_create_count} />
        <Stat label="Requested creates" value={diagnostics.aspire_initial_replacement_requested_create_count} />
        <Stat label="Authorisation validation" value={diagnostics.aspire_initial_replacement_authorisation_validation_result || "not requested"} />
        <Stat label="Authorisation state" value={diagnostics.aspire_initial_replacement_authorisation_consumption_state || "none"} />
        <Stat label="Authorisation rejection" value={diagnostics.aspire_initial_replacement_authorisation_rejection_reason || "none"} />
        <Stat label="Routes discovered" value={diagnostics.routes_discovered} />
        <Stat label="Routes selected" value={diagnostics.routes_selected_for_detail} />
        <Stat label="Routes deferred" value={diagnostics.routes_deferred_by_limit} />
        <Stat label="Declared source count" value={diagnostics.aspire_source_declared_count} />
        <Stat label="Source count provenance" value={diagnostics.aspire_source_count_provenance || "unknown"} />
        <Stat label="Initial page routes" value={diagnostics.aspire_initial_page_count} />
        <Stat label="Later-page routes" value={diagnostics.aspire_later_page_count} />
        <Stat label="Pagination complete" value={diagnostics.aspire_pagination_contract_complete ? "yes" : "no"} />
        <Stat label="Pagination stop" value={diagnostics.aspire_pagination_termination_reason || "unknown"} />
        <Stat label="Bounded browser requests" value={diagnostics.aspire_auto_network_bounded_request_count} />
        <Stat label="Browser duration ms" value={diagnostics.aspire_auto_network_duration_ms} />
        <Stat label="Duplicate routes" value={diagnostics.aspire_auto_network_duplicate_route_count} />
        <Stat label="Linkresolver discovered" value={diagnostics.aspire_linkresolver_routes_discovered} />
        <Stat label="Linkresolver selected" value={diagnostics.aspire_linkresolver_routes_selected} />
        <Stat label="Linkresolver deferred" value={diagnostics.aspire_linkresolver_routes_deferred_by_limit} />
        <Stat label="Routes not fetched" value={diagnostics.aspire_candidate_routes_not_fetched_count} />
        <Stat label="Catalogue complete" value={diagnostics.aspire_catalogue_complete ? "yes" : "no"} />
        <Stat label="All routes accounted" value={diagnostics.aspire_all_routes_accounted ? "yes" : "no"} />
        <Stat label="Identity reconciliation" value={diagnostics.aspire_identity_reconciliation_complete ? "complete" : "incomplete"} />
        <Stat label="Routes unaccounted" value={diagnostics.aspire_routes_unaccounted_count} />
        <Stat label="Scan complete" value={diagnostics.aspire_scan_complete ? "yes" : "no"} />
        <Stat label="Completeness blockers" value={completenessBlockers.length ? completenessBlockers.join(", ") : "none"} />
        <Stat label="Degradation reasons" value={degradationReasons.length ? degradationReasons.join(", ") : "none"} />
        <Stat
          label="Last-known-good public data"
          value={diagnostics.aspire_last_known_good_public_preserved === true ? "preserved" : "not reported"}
        />
        <Stat label="Safe candidates" value={diagnostics.aspire_safe_candidate_count} />
        <Stat label="Review-only" value={diagnostics.aspire_review_only_candidate_count} />
        <Stat label="Ineligible" value={diagnostics.aspire_ineligible_candidate_count} />
        <Stat label="Apply excluded IDs" value={diagnostics.aspire_apply_excluded_ids_count} />
        <Stat label="Apply eligible" value={diagnostics.aspire_apply_eligible_dry_run_count} />
        <Stat label="Verify eligible" value={diagnostics.aspire_verify_eligible_dry_run_count} />
        <Stat label="Publish eligible" value={diagnostics.aspire_publish_eligible_dry_run_count} />
        <Stat label="Current public" value={firstValue(diagnostics.aspire_current_public_count, "unknown")} />
        <Stat label="Public count status" value={diagnostics.aspire_current_public_count_status || "unknown"} />
        <Stat label="Data endpoints" value={diagnostics.aspire_preload_data_endpoint_found_count} />
        <Stat label="Endpoint fetches" value={diagnostics.aspire_preload_data_endpoint_fetch_attempted_count} />
        <Stat label="Endpoint OK" value={diagnostics.aspire_preload_data_endpoint_fetch_success_count} />
        <Stat label="Endpoint failed" value={diagnostics.aspire_preload_data_endpoint_fetch_failed_count} />
        <Stat label="Data records" value={diagnostics.aspire_data_product_records_found_count} />
        <Stat label="Data routes" value={diagnostics.aspire_data_product_routes_found_count} />
        <Stat label="x-additional-data" value={diagnostics.aspire_data_x_additional_data_found_count} />
        <Stat label="Data handlers" value={diagnostics.aspire_data_handler_detected_count} />
        <Stat label="JS bundles" value={diagnostics.aspire_js_bundle_found_count} />
        <Stat label="JS fetched" value={diagnostics.aspire_js_bundle_fetch_success_count} />
        <Stat label="JS markers" value={diagnostics.aspire_js_route_marker_count} />
        <Stat label="Possible APIs" value={diagnostics.aspire_js_possible_api_endpoint_count} />
        <Stat label="Transport shape" value={diagnostics.aspire_data_transport_shape || "unknown"} />
        <Stat label="Transport bytes" value={diagnostics.aspire_data_transport_payload_size} />
        <Stat label="Transport tokens" value={diagnostics.aspire_data_transport_token_count} />
        <Stat label="Decoder" value={diagnostics.aspire_data_transport_decoder_success ? "decoded" : diagnostics.aspire_data_transport_decoder_attempted ? "attempted" : "not run"} />
        <Stat label="Decoder records" value={diagnostics.aspire_data_transport_record_count} />
        <Stat label="Exact product records" value={diagnostics.aspire_data_transport_product_record_candidate_count} />
        <Stat label="Related transport records" value={diagnostics.aspire_data_transport_related_record_count} />
        <Stat label="Collection contracts" value={diagnostics.aspire_data_transport_contract_record_count} />
        <Stat label="Decoded products" value={diagnostics.aspire_data_transport_product_record_extracted_count} />
        <Stat label="Total caps confirmed" value={diagnostics.aspire_decoded_variant_total_cap_confirmed_count} />
        <Stat label="Total caps rejected" value={diagnostics.aspire_decoded_variant_total_cap_rejected_count} />
        <Stat label="Auto Draw caps confirmed" value={diagnostics.aspire_autodraw_total_cap_confirmed_count} />
        <Stat label="Auto Draw caps rejected" value={diagnostics.aspire_autodraw_total_cap_rejected_count} />
        <Stat label="Auto Draw entries derived" value={diagnostics.aspire_autodraw_current_entries_derived_count} />
        <Stat label="Auto Draw contract" value={diagnostics.aspire_autodraw_contract_evidence?.contract || "not reported"} />
        <Stat label="Parse strategy" value={diagnostics.aspire_data_transport_parse_strategy || "unknown"} />
        <Stat label="Raw route scan" value={diagnostics.aspire_raw_product_route_scan_attempted ? "yes" : "no"} />
        <Stat label="Raw routes" value={diagnostics.aspire_raw_product_route_found_count} />
        <Stat label="Decoded routes" value={diagnostics.aspire_decoded_product_route_found_count} />
        <Stat label="Route dedupe" value={diagnostics.aspire_product_route_dedupe_count} />
        <Stat label="Linkresolver routes" value={diagnostics.aspire_linkresolver_route_count} />
        <Stat label="Linkresolver fetches" value={diagnostics.aspire_linkresolver_fetch_attempted_count} />
        <Stat label="Linkresolver OK" value={diagnostics.aspire_linkresolver_fetch_success_count} />
        <Stat label="Linkresolver failed" value={diagnostics.aspire_linkresolver_fetch_failed_count} />
        <Stat label="Linkresolver type" value={diagnostics.aspire_linkresolver_response_content_type || "unknown"} />
        <Stat label="Collection configs" value={diagnostics.aspire_collection_config_detected_count} />
        <Stat label="Route source" value={diagnostics.aspire_runtime_route_source_status || "unknown"} />
        <Stat label="Manual input" value={diagnostics.aspire_manual_import_input_type || "none"} />
        <Stat label="HAR entries" value={diagnostics.aspire_manual_har_entries_scanned_count} />
        <Stat label="HAR linkresolvers" value={diagnostics.aspire_manual_linkresolver_requests_found_count} />
        <Stat label="Manual routes" value={diagnostics.aspire_manual_routes_extracted_count} />
        <Stat label="Manual accepted" value={diagnostics.aspire_manual_routes_accepted_count} />
        <Stat label="Manual rejected" value={diagnostics.aspire_manual_routes_rejected_count} />
        <Stat label="Manual dedupe" value={diagnostics.aspire_manual_route_dedupe_count} />
        <Stat label="Hints would create" value={diagnostics.aspire_manual_hints_would_create_count} />
        <Stat label="Hints would update" value={diagnostics.aspire_manual_hints_would_update_count} />
        <Stat label="Hints created" value={diagnostics.aspire_manual_hints_created_count} />
        <Stat label="Hints updated" value={diagnostics.aspire_manual_hints_updated_count} />
        <Stat label="Hints loaded" value={diagnostics.aspire_manual_hints_loaded_count} />
        <Stat label="Hints used" value={diagnostics.aspire_manual_hints_used_count} />
        <Stat label="Auto network" value={diagnostics.aspire_auto_network_discovery_requested ? diagnostics.aspire_auto_network_discovery_status || "requested" : "not run"} />
        <Stat label="Network available" value={diagnostics.aspire_auto_network_discovery_available === false ? "no" : diagnostics.aspire_auto_network_discovery_available === true ? "yes" : "unknown"} />
        <Stat label="Network requests" value={diagnostics.aspire_auto_network_requests_observed_count} />
        <Stat label="Network linkresolvers" value={diagnostics.aspire_auto_network_linkresolver_requests_found_count} />
        <Stat label="Network routes" value={diagnostics.aspire_auto_network_routes_extracted_count} />
        <Stat label="Network accepted" value={diagnostics.aspire_auto_network_routes_accepted_count} />
        <Stat label="Network rejected" value={diagnostics.aspire_auto_network_routes_rejected_count} />
        <Stat label="Network hints would create" value={diagnostics.aspire_auto_network_hints_would_create_count} />
        <Stat label="Network hints created" value={diagnostics.aspire_auto_network_hints_created_count} />
        <Stat label="Decoded product docs" value={diagnostics.aspire_decoded_product_doc_found_count} />
        <Stat label="Decoded names" value={diagnostics.aspire_decoded_product_name_found_count} />
        <Stat label="Decoded draw dates" value={diagnostics.aspire_decoded_product_draw_date_found_count} />
        <Stat label="Decoded finished" value={diagnostics.aspire_decoded_product_finished_found_count} />
        <Stat label="Decoded variants" value={diagnostics.aspire_decoded_variant_found_count} />
        <Stat label="Variant prices" value={diagnostics.aspire_decoded_variant_price_found_count} />
        <Stat label="Variant maxStock" value={diagnostics.aspire_decoded_variant_max_stock_found_count} />
        <Stat label="Variant maxPerPerson" value={diagnostics.aspire_decoded_variant_max_per_person_found_count} />
        <Stat label="Price unit" value={diagnostics.aspire_decoded_variant_price_unit_assumption || "unknown"} />
        <Stat label="Stock locks" value={diagnostics.aspire_decoded_stocklock_found_count} />
        <Stat label="Remaining stock" value={diagnostics.aspire_decoded_remaining_stock_found_count} />
        <Stat label="Derived current entries" value={diagnostics.aspire_current_entries_derived_from_stock_count} />
        <Stat label="Probe attempts" value={diagnostics.aspire_discovered_endpoint_probe_attempted_count} />
        <Stat label="Probe OK" value={diagnostics.aspire_discovered_endpoint_probe_success_count} />
        <Stat label="Meta fallbacks" value={diagnostics.aspire_static_meta_detail_fallback_count} />
        <Stat label="Cards found" value={firstValue(diagnostics.aspire_cards_found_count, summary.cards_found)} />
        <Stat label="Candidate URLs" value={diagnostics.aspire_candidate_urls_found_count} />
        <Stat label="Accepted URLs" value={diagnostics.aspire_candidate_urls_accepted_count} />
        <Stat label="Rejected URLs" value={diagnostics.aspire_candidate_urls_rejected_count} />
        <Stat label="Details fetched" value={firstValue(diagnostics.aspire_detail_fetch_success_count, summary.details_fetched)} />
        <Stat label="Display-safe" value={firstValue(diagnostics.aspire_display_safe_candidate_count, summary.cards_accepted)} />
        <Stat label="GBP prices" value={diagnostics.aspire_price_found_count} />
        <Stat label="Max tickets" value={diagnostics.aspire_max_tickets_found_count} />
        <Stat label="Current entries" value={diagnostics.aspire_current_entries_found_count} />
        <Stat label="Sold percent" value={diagnostics.aspire_sold_percent_found_count} />
        <Stat label="Cash alternatives" value={diagnostics.aspire_cash_alternative_found_count} />
        <Stat label="Voucher prizes" value={diagnostics.aspire_voucher_prize_detected_count} />
        <Stat label="Voucher included-cash rejections" value={diagnostics.aspire_voucher_amount_rejected_as_included_cash_count} />
        <Stat label="Voucher cash-alt rejections" value={diagnostics.aspire_voucher_denomination_rejected_as_cash_alternative_count} />
        <Stat label="No-cash-alt evidence" value={diagnostics.aspire_explicit_no_cash_alternative_detected_count} />
        <Stat label="Money fields repaired" value={diagnostics.aspire_money_fields_repaired_count} />
        <Stat label="Category fields repaired" value={diagnostics.aspire_category_fields_repaired_count} />
        <Stat label="Metadata fields backfilled" value={diagnostics.aspire_metadata_fields_backfilled_count} />
        <Stat label="Draw dates" value={diagnostics.aspire_draw_date_found_count} />
        <Stat label="Images" value={diagnostics.aspire_image_found_count} />
        <Stat label="Budget-eligible dry-run" value={diagnostics.aspire_budget_odds_eligible_dry_run_count} />
        <Stat label="Holiday mapped to Cars" value={diagnostics.aspire_explicit_holiday_to_cars_count} />
        <Stat label="Apply expected IDs" value={diagnostics.aspire_apply_expected_identity_count} />
        <Stat label="Apply attempted IDs" value={diagnostics.aspire_apply_attempted_identity_count} />
        <Stat label="Existing matches" value={diagnostics.aspire_apply_existing_match_count} />
        <Stat label="Genuine creates" value={diagnostics.aspire_apply_genuine_create_count} />
        <Stat label="Genuine updates" value={diagnostics.aspire_apply_genuine_update_count} />
        <Stat label="Unchanged" value={diagnostics.aspire_apply_unchanged_count} />
        <Stat label="Identity conflicts" value={diagnostics.aspire_apply_conflict_count} />
        <Stat label="Unaccounted IDs" value={diagnostics.aspire_apply_unaccounted_count} />
        <Stat label="Duplicate existing" value={diagnostics.aspire_apply_duplicate_existing_count} />
        <Stat label="Legacy migrations" value={diagnostics.aspire_apply_legacy_identity_migration_count} />
        <Stat label="New-record limit" value={diagnostics.aspire_apply_new_record_limit} />
        <Stat label="Create limit exceeded" value={diagnostics.aspire_apply_new_record_limit_exceeded ? "yes" : "no"} />
        <Stat label="Exact set match" value={diagnostics.aspire_apply_exact_identity_set_match ? "yes" : "no"} />
        <Stat label="Complete set persisted" value={diagnostics.aspire_apply_complete_set_persisted ? "yes" : "no"} />
        <Stat label="Live-set pointer advanced" value={diagnostics.aspire_apply_live_set_pointer_advanced ? "yes" : "no"} />
        <Stat label="Verify candidates" value={diagnostics.aspire_verify_candidates_count} />
        <Stat label="Verify accepted" value={diagnostics.aspire_verify_verified_count} />
        <Stat label="Verify skipped" value={diagnostics.aspire_verify_skipped_count} />
        <Stat label="Complete apply set" value={diagnostics.aspire_publish_complete_apply_set_count} />
        <Stat label="Publish candidates" value={diagnostics.aspire_publish_candidate_count ?? diagnostics.aspire_publish_candidates_count} />
        <Stat label="Newly published" value={diagnostics.aspire_publish_newly_published_count ?? diagnostics.aspire_publish_published_count} />
        <Stat label="Already published" value={diagnostics.aspire_publish_already_published_count} />
        <Stat label="Intentionally retained hidden" value={diagnostics.aspire_publish_intentionally_retained_hidden_count} />
        <Stat label="Remaining drafts" value={diagnostics.aspire_publish_remaining_draft_count} />
        <Stat label="Failed public gates" value={diagnostics.aspire_publish_failed_gate_count ?? diagnostics.aspire_publish_skipped_count} />
        <Stat label="Accounted set" value={diagnostics.aspire_publish_accounted_set_count} />
        <Stat label="Exact set accounted" value={diagnostics.aspire_publish_exact_set_accounted ? "yes" : "no"} />
        <Stat label="Budget Odds eligible" value={diagnostics.aspire_publish_budget_odds_eligible_count} />
        <Stat label="Budget Odds excluded" value={diagnostics.aspire_publish_budget_odds_excluded_count} />
        <Stat label="Odds ready before" value={diagnostics.aspire_publish_odds_ready_before_count} />
        <Stat label="Odds ready after" value={diagnostics.aspire_publish_odds_ready_after_count} />
        <Stat label="Card/detail odds consistent" value={diagnostics.aspire_publish_card_detail_odds_consistent_count} />
        <Stat label="Card/detail odds mismatches" value={diagnostics.aspire_publish_card_detail_odds_mismatch_count} />
        <Stat label="Rejected" value={summary.cards_rejected} />
        <Stat label="Errors" value={summary.errors} />
      </div>
      {Object.keys(rejectionReasons).length > 0 && (
        <div className="mt-2 text-[11px] text-white/60">
          Reasons: {Object.entries(rejectionReasons)
            .slice(0, 5)
            .map(([reason, count]) => `${reason}: ${count}`)
            .join(", ")}
        </div>
      )}
      {Object.keys(reviewReasons).length > 0 && (
        <div className="mt-2 text-[11px] text-white/60">
          Review-only reasons: {Object.entries(reviewReasons)
            .slice(0, 8)
            .map(([reason, count]) => `${reason}: ${count}`)
            .join(", ")}
        </div>
      )}
      <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] text-white/65">
        <SampleList title="Candidate URL examples" rows={candidateRows} empty="No candidate URLs" showReason />
        <SampleList title="Display-safe examples" rows={displayRows} empty="No display-safe examples" showReason />
        <SampleList title="Missing-field examples" rows={missingRows} empty="No missing-field examples" showReason />
        <SampleList title="Rejected URL examples" rows={rejectedRows} empty="No rejected URLs" showReason />
        <SampleList title="Fetch errors" rows={fetchErrors} empty="No fetch errors" showReason />
        <SampleList title="Data endpoints" rows={endpointRows} empty="No data endpoints" showReason />
        <SampleList title="x-additional-data" rows={additionalDataRows} empty="No x-additional-data headers" showReason />
        <SampleList title="Data handlers" rows={dataHandlerRows} empty="No data handlers" showReason />
        <SampleList title="Data product records" rows={productRecordRows} empty="No data product records" showReason />
        <SampleList title="Data product routes" rows={productRouteRows} empty="No data product routes" showReason />
        <SampleList title="Static meta fallbacks" rows={metaFallbackRows} empty="No static meta fallbacks" showReason />
        <SampleList title="JS bundles" rows={jsBundleRows} empty="No JS bundles" showReason />
        <SampleList title="JS route markers" rows={jsMarkerRows} empty="No JS route markers" showReason />
        <SampleList title="Possible API endpoints" rows={possibleEndpointRows} empty="No possible API endpoints" showReason />
        <SampleList title="Data collection markers" rows={dataCollectionRows} empty="No data collection markers" showReason />
        <SampleList title="Transport excerpts" rows={transportExcerptRows} empty="No transport excerpts" showReason />
        <SampleList title="Transport product markers" rows={transportProductRows} empty="No transport product markers" showReason />
        <SampleList title="Transport route markers" rows={transportRouteRows} empty="No transport route markers" showReason />
        <SampleList title="Transport record boundaries" rows={transportBoundaryRows} empty="No decoded record boundaries" showReason />
        <SampleList title="Decoded product records" rows={transportRecordRows} empty="No decoded product records" showReason />
        <SampleList title="Decoded product keys" rows={transportKeyRows} empty="No decoded product keys" showReason />
        <SampleList title="Transport decode errors" rows={transportDecodeErrorRows} empty="No transport decode errors" showReason />
        <SampleList title="Rejected route fragments" rows={rejectedRouteRows} empty="No rejected route fragments" showReason />
        <SampleList title="Raw product routes" rows={rawProductRouteRows} empty="No raw product routes" showReason />
        <SampleList title="Decoded product routes" rows={decodedProductRouteRows} empty="No decoded product routes" showReason />
        <SampleList title="Linkresolver URLs" rows={linkresolverUrlRows} empty="No linkresolver requests" showReason />
        <SampleList title="Linkresolver excerpts" rows={linkresolverExcerptRows} empty="No linkresolver response excerpts" showReason />
        <SampleList title="Linkresolver keys" rows={linkresolverKeyRows} empty="No linkresolver response keys" showReason />
        <SampleList title="Collection configs" rows={collectionConfigRows} empty="No collection configs" showReason />
        <SampleList title="Manual routes extracted" rows={manualExtractedRows} empty="No manual routes extracted" showReason />
        <SampleList title="Manual routes accepted" rows={manualAcceptedRows} empty="No manual routes accepted" showReason />
        <SampleList title="Manual routes rejected" rows={manualRejectedRows} empty="No manual routes rejected" showReason />
        <SampleList title="Manual hint writes" rows={manualHintRows} empty="No manual hint writes" showReason />
        <SampleList title="Manual hints loaded" rows={manualHintLoadedRows} empty="No manual hints loaded" showReason />
        <SampleList title="Manual hints used" rows={manualHintUsedRows} empty="No manual hints used" showReason />
        <SampleList title="Auto network linkresolver URLs" rows={autoNetworkLinkresolverRows} empty="No automated linkresolver URLs" showReason />
        <SampleList title="Auto network accepted routes" rows={autoNetworkAcceptedRows} empty="No automated routes accepted" showReason />
        <SampleList title="Auto network errors" rows={autoNetworkErrorRows} empty="No automated network errors" showReason />
        <SampleList title="Pagination pages" rows={paginationPageRows} empty="No pagination evidence" showReason />
        <SampleList title="Decoded product docs" rows={decodedProductDocRows} empty="No decoded product docs" showReason />
        <SampleList title="Decoded variants" rows={decodedVariantRows} empty="No decoded variants" showReason />
        <SampleList title="Decoded stock locks" rows={decodedStockRows} empty="No decoded stock locks" showReason />
        <SampleList title="Current entry derivations" rows={currentEntryRows} empty="No current entry derivations" showReason />
        <SampleList title="Endpoint probe success" rows={probeSuccessRows} empty="No endpoint probe success" showReason />
        <SampleList title="Endpoint probe rejected" rows={probeRejectedRows} empty="No endpoint probe rejected" showReason />
        <SampleList title="Aspire verified drafts" rows={aspireVerifiedRows} empty="No verified Aspire drafts" showReason />
        <SampleList title="Aspire verify skipped" rows={aspireVerifySkippedRows} empty="No skipped Aspire drafts" showReason />
        <SampleList title="Aspire published" rows={aspirePublishedRows} empty="No published Aspire drafts" showReason />
        <SampleList title="Aspire public odds repaired" rows={aspirePublishedRepairRows} empty="No existing Aspire public odds repairs" showReason />
        <SampleList title="Aspire publish skipped" rows={aspirePublishSkippedRows} empty="No skipped Aspire publications" showReason />
        <SampleList title="Aspire card/detail odds mismatches" rows={aspireOddsMismatchRows} empty="No Aspire card/detail odds mismatches" showReason />
        <SampleList title="Aspire duplicate identities" rows={identityDuplicateRows} empty="No duplicate exact identities" showReason />
        <SampleList title="Aspire identity conflicts" rows={identityConflictRows} empty="No ambiguous exact identities" showReason />
        <SampleList title="Aspire unresolved identities" rows={unresolvedIdentityRows} empty="No unresolved identities" showReason />
        <SampleList title="Aspire safe apply identities" rows={applySafeRows} empty="No safe apply identities" showReason />
        <SampleList title="Aspire review-only identities" rows={reviewOnlyRows} empty="No review-only identities" showReason />
        <SampleList title="Auto Draw caps confirmed" rows={autoDrawConfirmedRows} empty="No confirmed Auto Draw caps" showReason />
        <SampleList title="Auto Draw caps rejected" rows={autoDrawRejectedRows} empty="No rejected Auto Draw caps" showReason />
        <SampleList title="Explicit Holiday categories" rows={holidayCategoryRows} empty="No explicit Holiday category samples" showReason />
        <SampleList title="Aspire apply creates" rows={applyCreateRows} empty="No planned Aspire creates" showReason />
        <SampleList title="Aspire apply updates" rows={applyUpdateRows} empty="No planned Aspire updates" showReason />
        <SampleList title="Aspire legacy identity migrations" rows={applyMigrationRows} empty="No legacy identity migrations" showReason />
        <SampleList title="Aspire apply identity conflicts" rows={applyConflictRows} empty="No apply identity conflicts" showReason />
        <SampleList title="Aspire voucher prizes" rows={voucherPrizeRows} empty="No voucher prizes detected" showReason />
        <SampleList title="Page-shape fingerprints" rows={pageShapeRows} empty="No page-shape fingerprints" showReason />
        <SampleList title="Field provenance" rows={fieldProvenanceRows} empty="No field provenance" showReason />
        <SampleList title="Aspire money evidence" rows={moneyEvidenceRows} empty="No scoped money evidence" showReason />
        <SampleList title="Aspire money repairs" rows={moneyRepairRows} empty="No planned money repairs" showReason />
        <SampleList title="Aspire category repairs" rows={categoryRepairRows} empty="No planned category repairs" showReason />
        <SampleList title="Aspire metadata backfills" rows={metadataBackfillRows} empty="No metadata backfills" showReason />
      </div>
    </div>
  );
}

function ScanOnlyDiagnosticsPanel({ summary = {}, diagnostics = {} }) {
  const acceptedRows = diagnostics.sample_accepted || summary.sample_accepted || [];
  const rejectedRows = diagnostics.sample_rejected || summary.sample_rejected || [];
  const skillRows = diagnostics.sample_skill_based || summary.sample_skill_based || [];
  const classification = diagnostics.candidate_classification_breakdown || summary.candidate_classification_breakdown || {};
  const missing = diagnostics.missing_field_summary || summary.missing_field_summary || {};

  return (
    <div
      className="mt-3 rounded border border-sky-400/20 bg-sky-400/10 p-3"
      data-testid="botb-scan-diagnostics"
    >
      <div className="text-[11px] font-semibold text-sky-100 mb-2">
        BOTB scan-only diagnostics
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
        <Stat label="Found" value={firstValue(diagnostics.raw_candidate_count, summary.cards_found, summary.candidates_found)} />
        <Stat label="Accepted" value={firstValue(diagnostics.accepted_live_detail_count, summary.cards_accepted, summary.candidates_accepted)} />
        <Stat label="Rejected" value={firstValue(diagnostics.rejected_count, summary.cards_rejected, summary.candidates_rejected)} />
        <Stat label="Skill-based" value={firstValue(diagnostics.skill_based_count, summary.skill_based_count)} />
        <Stat label="Instant Win" value={firstValue(diagnostics.instant_win_count, summary.instant_win_count, classification.instant_win)} />
        <Stat label="Odds compatible" value={firstValue(diagnostics.odds_compatible_count, summary.odds_compatible_count)} />
        <Stat label="Missing max" value={firstValue(diagnostics.missing_max_tickets_count, summary.missing_max_tickets_count, missing.max_tickets)} />
        <Stat label="Missing price" value={firstValue(diagnostics.missing_ticket_price_count, summary.missing_ticket_price_count, missing.ticket_price)} />
        <Stat label="Missing date" value={firstValue(diagnostics.missing_draw_date_count, summary.missing_draw_date_count, missing.draw_date)} />
        <Stat label="Missing image" value={firstValue(diagnostics.missing_image_count, summary.missing_image_count, missing.image)} />
      </div>
      <div className="mt-3 grid md:grid-cols-3 gap-3 text-[11px] text-white/65">
        <SampleList title="Accepted examples" rows={acceptedRows} empty="No accepted examples" showReason />
        <SampleList title="Rejected examples" rows={rejectedRows} empty="No rejected examples" showReason />
        <SampleList title="Skill-based examples" rows={skillRows} empty="No skill-based examples" showReason />
      </div>
      {diagnostics.recommended_next_step && (
        <div className="mt-3 text-[11px] text-sky-100/80">
          {diagnostics.recommended_next_step}
        </div>
      )}
    </div>
  );
}

function SampleList({ title, rows = [], empty, showReason }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-white/45 mb-1">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-white/35">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {rows.slice(0, 5).map((row, index) => (
            <li key={`${row.id || row.title || row.reason || "row"}-${index}`}>
              <span className="text-white/80">
                {row.title || row.source_title || row.detail_url || row.url || row.canonical_url || row.route || row.marker || row.key || row.transport_key || row.exact_identity || row.product_id || row.variant_id || row.id || "Record"}
              </span>
              {showReason && (row.reason || row.reasons) && (
                <span className="text-white/45">
                  {" - "}
                  {Array.isArray(row.reasons)
                    ? row.reasons.join(", ")
                    : row.reason || row.reasons}
                </span>
              )}
              {showReason && !row.reason && !row.reasons && row.odds_model_status && (
                <span className="text-white/45"> - {row.odds_model_status}</span>
              )}
              {showReason && !row.reason && !row.reasons && !row.odds_model_status && row.classification && (
                <span className="text-white/45"> - {row.classification}</span>
              )}
              {showReason && !row.reason && !row.reasons && !row.odds_model_status && !row.classification && row.decision_reason && (
                <span className="text-white/45"> - {row.decision_reason}</span>
              )}
              {showReason && !row.reason && !row.reasons && !row.odds_model_status && !row.classification && !row.decision_reason && row.evidence && (
                <span className="text-white/45"> - {row.evidence}</span>
              )}
              {showReason && !row.reason && !row.reasons && !row.odds_model_status && !row.classification && !row.decision_reason && !row.evidence && row.ticket_price != null && (
                <span className="text-white/45"> - ticket {row.ticket_price}</span>
              )}
              {showReason && !row.reason && !row.reasons && !row.odds_model_status && !row.classification && !row.decision_reason && !row.evidence && row.ticket_price == null && row.raw_price != null && (
                <span className="text-white/45"> - price {row.raw_price}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
